/*
 * Product Set Builder core — v2.42 landscape multi-row preview floor
 * Contains the main builder runtime and the drag guidance/trash controller.
 * v2.42: keeps v2.41 intact and prevents 2+ row layouts in landscape desktop-like
 * mode from being compressed below a configurable share of the visible builder area.
 * Natural stages that are already shorter than that share are never enlarged.
 * Layout geometry remains in psb-layout-presets.js.
 */
const psbBuilderLoaderScript = document.currentScript;
const psbBuilderRootId = psbBuilderLoaderScript?.dataset.psbRootId || '';

(() => {
  const STORAGE_KEY = 'psb_drag_guidance_seen_v1';
  const FORCE_PARAM = 'psb_hint';
  const SHOW_DELAY_MS = 900;
  const AUTO_HIDE_MS = 10000;

  function safeStorageGet(key) {
    try { return window.localStorage.getItem(key); } catch (error) { return null; }
  }

  function safeStorageSet(key, value) {
    try { window.localStorage.setItem(key, value); } catch (error) {}
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function getViewportSize() {
    const viewport = window.visualViewport;
    return {
      width: viewport && viewport.width ? viewport.width : window.innerWidth,
      height: viewport && viewport.height ? viewport.height : window.innerHeight
    };
  }

  function rectIsVisible(rect) {
    const viewport = getViewportSize();
    const visibleWidth = Math.min(rect.right, viewport.width) - Math.max(rect.left, 0);
    const visibleHeight = Math.min(rect.bottom, viewport.height) - Math.max(rect.top, 0);
    return visibleWidth > 40 && visibleHeight > Math.min(80, rect.height * 0.28);
  }

  function createElement(className, html) {
    const element = document.createElement('div');
    element.className = className;
    if (html) element.innerHTML = html;
    return element;
  }

  class DragOverlay {
    constructor({ root, stage }) {
      this.root = root;
      this.stage = stage;
      this.forceHint = false;
      try {
        this.forceHint = new URL(window.location.href).searchParams.get(FORCE_PARAM) === '1';
      } catch (error) {}
      this.hintSeen = !this.forceHint && safeStorageGet(STORAGE_KEY) === '1';
      this.hintVisible = false;
      this.draggingSlot = false;
      this.trashActive = false;
      this.hintTimer = null;
      this.autoHideTimer = null;
      this.hideTimer = null;
      this.lastSlots = [];
      this.build();
      this.bind();
      this.scheduleHint();
    }

    build() {
      this.coach = createElement('psb-drag-coach');
      this.coach.hidden = true;
      this.coach.innerHTML = `
        <div class="psb-drag-coach__ring psb-drag-coach__ring--a" aria-hidden="true"></div>
        <div class="psb-drag-coach__ring psb-drag-coach__ring--b" aria-hidden="true"></div>
        <div class="psb-drag-coach__ghost" aria-hidden="true"><img alt=""></div>
        <div class="psb-drag-coach__trash" aria-hidden="true">
          <span class="psb-drag-coach__trash-icon">${this.trashSvg()}</span>
          <span>Drop below to remove</span>
        </div>
        <div class="psb-drag-coach__bubble" role="status" aria-live="polite">
          <button type="button" class="psb-drag-coach__close" aria-label="Dismiss drag tip">×</button>
          <strong>Drag a print to rearrange</strong>
          <span>Move it onto another print to swap.</span>
        </div>`;

      this.trash = createElement('psb-drag-trash');
      this.trash.hidden = true;
      this.trash.innerHTML = `
        <span class="psb-drag-trash__icon" aria-hidden="true">${this.trashSvg()}</span>
        <span class="psb-drag-trash__label psb-drag-trash__label--idle">Drop to remove</span>
        <span class="psb-drag-trash__label psb-drag-trash__label--active">Release to remove</span>`;

      document.body.appendChild(this.coach);
      document.body.appendChild(this.trash);

      this.ringA = this.coach.querySelector('.psb-drag-coach__ring--a');
      this.ringB = this.coach.querySelector('.psb-drag-coach__ring--b');
      this.ghost = this.coach.querySelector('.psb-drag-coach__ghost');
      this.ghostImage = this.ghost.querySelector('img');
      this.coachTrash = this.coach.querySelector('.psb-drag-coach__trash');
      this.bubble = this.coach.querySelector('.psb-drag-coach__bubble');
      this.closeButton = this.coach.querySelector('.psb-drag-coach__close');
    }

    trashSvg() {
      return '<svg viewBox="0 0 24 24" focusable="false" aria-hidden="true"><path d="M9 3.75h6l.75 1.5H19a.75.75 0 0 1 0 1.5h-.8l-.76 12.05A2.25 2.25 0 0 1 15.2 21H8.8a2.25 2.25 0 0 1-2.24-2.2L5.8 6.75H5a.75.75 0 0 1 0-1.5h3.25L9 3.75Zm-1.7 3 .75 11.96c.02.44.35.79.75.79h6.4c.4 0 .73-.35.75-.79l.75-11.96H7.3Zm2.45 2.25c.41 0 .75.34.75.75v6.5a.75.75 0 0 1-1.5 0v-6.5c0-.41.34-.75.75-.75Zm4.5 0c.41 0 .75.34.75.75v6.5a.75.75 0 0 1-1.5 0v-6.5c0-.41.34-.75.75-.75Z" fill="currentColor"/></svg>';
    }

    bind() {
      this.closeButton.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        this.hideHint(true);
      });

      this.handleHintKeydown = event => {
        if (!this.hintVisible || event.key !== 'Tab') return;
        event.preventDefault();
        this.closeButton.focus({ preventScroll: true });
      };
      document.addEventListener('keydown', this.handleHintKeydown, true);

      this.mutationObserver = new MutationObserver(() => {
        if (this.hintVisible) this.refreshHint();
        else this.scheduleHint();
      });
      this.mutationObserver.observe(this.stage, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'hidden', 'src', 'style']
      });

      this.handleViewportChange = () => {
        if (this.hintVisible) this.refreshHint();
        if (this.draggingSlot) this.positionTrash();
      };

      window.addEventListener('resize', this.handleViewportChange, { passive: true });
      window.addEventListener('scroll', this.handleViewportChange, { passive: true, capture: true });
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.handleViewportChange, { passive: true });
        window.visualViewport.addEventListener('scroll', this.handleViewportChange, { passive: true });
      }
    }

    getFilledSlots() {
      return Array.from(this.stage.querySelectorAll('.psb-slot.has-print:not([hidden])'))
        .filter(slot => {
          const rect = slot.getBoundingClientRect();
          return rect.width > 12 && rect.height > 12;
        });
    }

    scheduleHint() {
      if (this.hintSeen || this.draggingSlot || this.hintVisible) return;
      window.clearTimeout(this.hintTimer);
      if (this.getFilledSlots().length < 2) return;
      this.hintTimer = window.setTimeout(() => this.showHint(), SHOW_DELAY_MS);
    }

    clearHintSlotClasses() {
      if (!Array.isArray(this.lastSlots)) return;
      this.lastSlots.forEach(slot => {
        if (!slot || !slot.classList) return;
        slot.classList.remove('psb-drag-coach-source-slot', 'psb-drag-coach-target-slot');
      });
    }

    applyHintSlotClasses() {
      this.clearHintSlotClasses();
      if (this.lastSlots[0]) this.lastSlots[0].classList.add('psb-drag-coach-source-slot');
      if (this.lastSlots[1]) this.lastSlots[1].classList.add('psb-drag-coach-target-slot');
    }

    showHint() {
      if (this.hintSeen || this.draggingSlot || this.hintVisible) return;
      const slots = this.getFilledSlots();
      const stageRect = this.stage.getBoundingClientRect();
      if (slots.length < 2 || !rectIsVisible(stageRect)) return;

      this.lastSlots = slots.slice(0, 2);
      this.coach.hidden = false;
      this.hintVisible = true;
      this.rootWasInert = Boolean(this.root && this.root.inert);
      if (this.root) {
        this.root.inert = true;
        this.root.classList.add('psb-drag-hint-active');
      }
      if (document.body) document.body.classList.add('psb-drag-focus-active');
      this.applyHintSlotClasses();
      this.refreshHint();
      window.requestAnimationFrame(() => {
        this.coach.classList.add('is-visible');
        if (this.closeButton && typeof this.closeButton.focus === 'function') {
          this.closeButton.focus({ preventScroll: true });
        }
      });
      window.clearTimeout(this.autoHideTimer);
      this.autoHideTimer = window.setTimeout(() => this.hideHint(true), AUTO_HIDE_MS);
    }

    refreshHint() {
      if (!this.hintVisible) return;
      const slots = this.getFilledSlots();
      if (slots.length < 2) {
        this.hideHint(false);
        return;
      }

      const nextSlots = slots.slice(0, 2);
      const slotPairChanged = nextSlots[0] !== this.lastSlots[0] || nextSlots[1] !== this.lastSlots[1];
      this.lastSlots = nextSlots;
      if (slotPairChanged) this.applyHintSlotClasses();
      const firstRect = this.lastSlots[0].getBoundingClientRect();
      const secondRect = this.lastSlots[1].getBoundingClientRect();
      const stageRect = this.stage.getBoundingClientRect();
      if (!rectIsVisible(stageRect)) {
        this.hideHint(false);
        return;
      }

      this.positionRing(this.ringA, firstRect);
      this.positionRing(this.ringB, secondRect);
      this.positionCoachTrash(stageRect);
      this.positionBubble(stageRect);
      this.positionGhost(firstRect, secondRect);
    }

    positionRing(element, rect) {
      const pad = 5;
      element.style.left = `${rect.left - pad}px`;
      element.style.top = `${rect.top - pad}px`;
      element.style.width = `${rect.width + pad * 2}px`;
      element.style.height = `${rect.height + pad * 2}px`;
    }

    positionBubble(stageRect) {
      const viewport = getViewportSize();
      const width = Math.min(286, Math.max(220, viewport.width - 28));
      const left = clamp(stageRect.left + stageRect.width / 2 - width / 2, 12, viewport.width - width - 12);
      const top = clamp(stageRect.top + 12, 10, Math.max(10, viewport.height - 92));
      this.bubble.style.width = `${width}px`;
      this.bubble.style.left = `${left}px`;
      this.bubble.style.top = `${top}px`;
    }

    positionCoachTrash(stageRect) {
      const viewport = getViewportSize();
      const width = Math.min(200, viewport.width - 28);
      const height = 46;
      const left = clamp(stageRect.left + stageRect.width / 2 - width / 2, 12, viewport.width - width - 12);
      const top = clamp(stageRect.bottom + 8, 10, viewport.height - height - 10);
      this.coachTrash.style.width = `${width}px`;
      this.coachTrash.style.left = `${left}px`;
      this.coachTrash.style.top = `${top}px`;
    }

    positionGhost(firstRect, secondRect) {
      const firstImage = this.lastSlots[0].querySelector('img');
      const source = firstImage ? (firstImage.currentSrc || firstImage.src || '') : '';
      if (source && this.ghostImage.src !== source) this.ghostImage.src = source;

      const width = clamp(firstRect.width * 0.42, 34, 74);
      const height = width * (firstRect.height / firstRect.width);
      const trashRect = this.coachTrash.getBoundingClientRect();
      const x1 = firstRect.left + firstRect.width / 2 - width / 2;
      const y1 = firstRect.top + firstRect.height / 2 - height / 2;
      const x2 = secondRect.left + secondRect.width / 2 - width / 2;
      const y2 = secondRect.top + secondRect.height / 2 - height / 2;
      const x3 = trashRect.left + trashRect.width / 2 - width / 2;
      const y3 = trashRect.top + trashRect.height / 2 - height / 2;

      this.ghost.style.width = `${width}px`;
      this.ghost.style.height = `${height}px`;
      this.ghost.style.setProperty('--psb-coach-x1', `${x1}px`);
      this.ghost.style.setProperty('--psb-coach-y1', `${y1}px`);
      this.ghost.style.setProperty('--psb-coach-x2', `${x2}px`);
      this.ghost.style.setProperty('--psb-coach-y2', `${y2}px`);
      this.ghost.style.setProperty('--psb-coach-x3', `${x3}px`);
      this.ghost.style.setProperty('--psb-coach-y3', `${y3}px`);
    }

    hideHint(markSeen = true) {
      window.clearTimeout(this.hintTimer);
      window.clearTimeout(this.autoHideTimer);
      if (markSeen) {
        this.hintSeen = true;
        if (!this.forceHint) safeStorageSet(STORAGE_KEY, '1');
      }
      if (!this.hintVisible) return;
      this.hintVisible = false;
      if (this.root) {
        this.root.inert = Boolean(this.rootWasInert);
        this.root.classList.remove('psb-drag-hint-active');
      }
      if (document.body && !(this.root && this.root.classList.contains('psb-slot-drag-focus-active'))) {
        document.body.classList.remove('psb-drag-focus-active');
      }
      this.clearHintSlotClasses();
      this.coach.classList.remove('is-visible');
      window.clearTimeout(this.hideTimer);
      this.hideTimer = window.setTimeout(() => {
        if (!this.hintVisible) {
          this.coach.hidden = true;
        }
      }, 180);
    }

    startTrash() {
      this.draggingSlot = true;
      this.trashActive = false;
      this.hideHint(true);
      this.trash.classList.remove('is-target', 'is-complete');
      this.trash.hidden = false;
      this.positionTrash();
      window.requestAnimationFrame(() => {
        this.trash.classList.add('is-visible');
      });
    }

    getVisibleStageRect() {
      const viewport = getViewportSize();
      const stageRect = this.stage.getBoundingClientRect();
      let bottomLimit = viewport.height;

      const controls = this.root && this.root.querySelector
        ? this.root.querySelector('[data-psb-controls]')
        : null;
      if (this.root && this.root.classList.contains('psb-sheet-expanded') && controls) {
        const controlsRect = controls.getBoundingClientRect();
        if (controlsRect.width > 0 && controlsRect.height > 0) {
          bottomLimit = Math.min(bottomLimit, controlsRect.top);
        }
      }

      const bottom = clamp(Math.min(stageRect.bottom, bottomLimit), stageRect.top, viewport.height);
      return {
        top: stageRect.top,
        bottom,
        height: Math.max(0, bottom - stageRect.top),
        left: stageRect.left,
        right: stageRect.right,
        width: stageRect.width
      };
    }

    positionTrash() {
      if (!this.draggingSlot || this.trash.hidden) return;
      const viewport = getViewportSize();
      const stageRect = this.getVisibleStageRect();
      const width = Math.min(270, viewport.width - 24);
      const height = viewport.width <= 749 ? 62 : 68;
      const left = clamp(stageRect.left + stageRect.width / 2 - width / 2, 10, viewport.width - width - 10);
      const top = clamp(stageRect.bottom + 10, 8, viewport.height - height - 8);
      this.trash.style.width = `${width}px`;
      this.trash.style.left = `${left}px`;
      this.trash.style.top = `${top}px`;

    }

    updateTrash(clientX, clientY) {
      if (!this.draggingSlot || this.trash.hidden) return false;
      this.positionTrash();
      const rect = this.trash.getBoundingClientRect();
      const hitPad = 8;
      const active = clientX >= rect.left - hitPad && clientX <= rect.right + hitPad && clientY >= rect.top - hitPad && clientY <= rect.bottom + hitPad;
      if (active !== this.trashActive) {
        this.trashActive = active;
        this.trash.classList.toggle('is-target', active);
      }
      return active;
    }

    completeTrashDrop() {
      this.trashActive = false;
      this.trash.classList.remove('is-target');
      this.trash.classList.add('is-complete');
      window.setTimeout(() => this.endTrash(), 230);
    }

    endTrash() {
      this.draggingSlot = false;
      this.trashActive = false;
      this.trash.classList.remove('is-visible', 'is-target');
      window.setTimeout(() => {
        if (!this.draggingSlot) {
          this.trash.hidden = true;
          this.trash.classList.remove('is-complete');
        }
      }, 170);
    }

    destroy() {
      window.clearTimeout(this.hintTimer);
      window.clearTimeout(this.autoHideTimer);
      window.clearTimeout(this.hideTimer);
      if (this.mutationObserver) this.mutationObserver.disconnect();
      document.removeEventListener('keydown', this.handleHintKeydown, true);
      if (this.root) {
        this.root.inert = Boolean(this.rootWasInert);
        this.root.classList.remove('psb-drag-hint-active', 'psb-slot-drag-focus-active');
      }
      if (document.body) document.body.classList.remove('psb-drag-focus-active');
      this.clearHintSlotClasses();
      window.removeEventListener('resize', this.handleViewportChange);
      window.removeEventListener('scroll', this.handleViewportChange, true);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.handleViewportChange);
        window.visualViewport.removeEventListener('scroll', this.handleViewportChange);
      }
      this.coach.remove();
      this.trash.remove();
    }
  }

  window.PSBDragOverlayModule = {
    create(options) {
      if (!options || !options.root || !options.stage) return null;
      return new DragOverlay(options);
    }
  };

  window.dispatchEvent(new CustomEvent('psb:drag-overlay-ready'));
})();

(() => {
  const startBuilder = () => {
    (() => {
  const root = document.getElementById(psbBuilderRootId);
  if (!root || root.dataset.psbReady === 'true') return;
  root.dataset.psbReady = 'true';

  const productScript = root.querySelector('[data-psb-product-json]');
  if (!productScript) return;

  const BUILDER_CONFIG = {

    ui: {
      mode: 'compact', // 'compact' or 'extended'
      showStageLayoutPicker: true,
      showProductLayoutSelector: true,
      showQuickCartButtons: true,
      showStagePrice: true
    },
    
    catalog: {
      mode: root.dataset.catalogMode || 'json',
      jsonUrl: root.dataset.catalogUrl || '',
      apiBaseUrl: (root.dataset.catalogApi || '').replace(/\/$/, ''),
      apiLimit: 80,
      pickerHiddenHandles: (root.dataset.collectionPickerHiddenHandles || '')
        .split(',')
        .map(handle => handle.trim())
        .filter(Boolean)
    },

    productDisplay: {
      defaultSetSize: 3,
      frameProfileIn: 0.75,
      baseDisplayWidthIn: 24,
      baseSlotWidthPct: 23.5,
      wallGapIn: 4.7,
      maxOuterGroupWidthPct: 88,
      maxOuterGroupHeightPct: 88,
      defaultPrintSize: { width: 5, height: 7 },
      defaultMattedFrameSize: { width: 8, height: 10 },
      defaultMattedPrintSize: { width: 5, height: 7 },
      
      mattedPrintByFrame: {
        '8x10': { width: 5, height: 7 },
        '11x14': { width: 8, height: 10 },
        '12x16': { width: 9, height: 12 },
        '16x20': { width: 11, height: 14 },
        '18x24': { width: 14, height: 20 },
        '20x24': { width: 16, height: 20 },
        '20x28': { width: 16, height: 24 },
        '24x36': { width: 20, height: 30 }
      }
    },

    system: {
      pageSize: 40,
      allHandle: '__all',
      imageWidths: {
        thumb: 220,
        slot: 720,
        dragGhost: 220,
        cartProperty: 480
      },
      desktopPicker: {
        visibleRows: 4.5,
        minReadableRows: 2,
        minSlotScale: 0.44,
        scaleStep: 0.04,
        maxCompactColumns: 8,
        compactRowHeightMinPx: 54,
        slotSafeGapPx: 15,
        sheetTopOffsetPx: 2,
        sheetBottomReservePx: 4,
        controlsExtraPx: 10,
        minThumbColumns: 6,
        minThumbColumnWidthPx: 88,
        // Landscape desktop-like layouts with 2+ stage rows keep at least this
        // share of the visible stage+catalog area for the preview. If the natural
        // stage is already shorter, it remains at its natural size instead.
        multiRowLandscapeMinPreviewShare: 0.50
      },
      mobilePicker: {
        slotSafeGapPx: 15,
        resultRows: 3.15,
        minReadableRows: 2,
        searchMinReadableRows: 2,
        preferredColumns: 5,
        narrowColumns: 4,
        emergencyColumns: 6,
        minThumbColumnWidthPx: 50,
        preferredThumbMinHeightPx: 74,
        compactColumns: 6,
        compactThumbMinHeightPx: 58,
        compactThumbMaxHeightPx: 104,
        minSlotScale: 0.44,
        scaleStep: 0.04,
        viewportBottomGapPx: 4,
        viewportLimitRatio: 0.44,
        maxThumbsHeightPx: 430,
        controlsExtraPx: 8,
        // Portrait-tablet experiment: for stage presets with 3 rows only, try to
        // reveal this many additional catalog rows without changing phone/desktop
        // behavior or exceeding the existing minimum slot scale.
        portraitTabletThreeRowExtraRows: 0.5
      },
      virtualResults: {
        enabledAfter: 180,
        maxRenderedThumbs: 180,
        overscanRows: 10,
        imageRetryCount: 2,
        scrollRenderThrottleMs: 120
      }
    },

    integration: {
      purchaseSelector: [
        'button[name="add"]',
        'input[name="add"]',
        '.product-form__submit',
        'form[action*="/cart/add"] button[type="submit"]',
        'form[action*="/cart/add"] input[type="submit"]',
        '[form][type="submit"]',
        '.shopify-payment-button button',
        '.shopify-payment-button__button',
        '[data-shopify="payment-button"]',
        'shop-pay-wallet-button',
        'shopify-accelerated-checkout'
      ].join(',')
    }
  };

  const UI_CONFIG = BUILDER_CONFIG.ui || {};
  const UI_MODE = UI_CONFIG.mode === 'extended' ? 'extended' : 'compact';
  const IS_COMPACT_UI = UI_MODE === 'compact';
  const SHOW_QUICK_CART_BUTTONS = IS_COMPACT_UI && UI_CONFIG.showQuickCartButtons !== false;
  const SHOW_STAGE_PRICE = IS_COMPACT_UI && UI_CONFIG.showStagePrice !== false;
  root.classList.toggle('psb-ui-compact', IS_COMPACT_UI);
  root.classList.toggle('psb-ui-extended', !IS_COMPACT_UI);
  root.classList.toggle('psb-quick-cart-enabled', SHOW_QUICK_CART_BUTTONS);
  root.classList.toggle('psb-stage-summary-enabled', SHOW_STAGE_PRICE);
  root.dataset.psbUiMode = UI_MODE;

  const LAYOUT_MODULE = window.PSBLayoutModule;
  const PRODUCT_MAP_MODULE = window.PSBProductMapModule || null;
  const MULTI_PRODUCT_SESSION_KEY = 'psb:multi-product-transfer:v2';
  const MULTI_PRODUCT_SESSION_MAX_AGE = 30 * 60 * 1000;
  const DRAG_OVERLAY_MODULE = window.PSBDragOverlayModule;
  if (!LAYOUT_MODULE) {
    console.error('Product Set Builder: psb-layout-presets.js is missing or failed to load.');
    return;
  }

  const DEFAULT_SET_SIZE = BUILDER_CONFIG.productDisplay.defaultSetSize;
  const SUPPORTED_SET_SIZES = LAYOUT_MODULE.supportedCounts;
  const PAGE_SIZE = BUILDER_CONFIG.system.pageSize;
  const ALL_HANDLE = BUILDER_CONFIG.system.allHandle;
  const THUMB_IMAGE_WIDTH = BUILDER_CONFIG.system.imageWidths.thumb;
  const SLOT_IMAGE_WIDTH = BUILDER_CONFIG.system.imageWidths.slot;
  const DRAG_GHOST_IMAGE_WIDTH = BUILDER_CONFIG.system.imageWidths.dragGhost;
  const PROPERTY_IMAGE_WIDTH = BUILDER_CONFIG.system.imageWidths.cartProperty;
  const VIRTUAL_RESULTS = BUILDER_CONFIG.system.virtualResults;
  const CATALOG_CONFIG = BUILDER_CONFIG.catalog;
  const PURCHASE_SELECTOR = BUILDER_CONFIG.integration.purchaseSelector;

  const FRAME_PROFILE_IN = BUILDER_CONFIG.productDisplay.frameProfileIn;
  const BASE_DISPLAY_WIDTH_IN = BUILDER_CONFIG.productDisplay.baseDisplayWidthIn;
  const BASE_SLOT_WIDTH_PCT = BUILDER_CONFIG.productDisplay.baseSlotWidthPct;
  const WALL_GAP_IN = BUILDER_CONFIG.productDisplay.wallGapIn;
  const MAX_OUTER_GROUP_WIDTH_PCT = BUILDER_CONFIG.productDisplay.maxOuterGroupWidthPct;
  const MAX_OUTER_GROUP_HEIGHT_PCT = BUILDER_CONFIG.productDisplay.maxOuterGroupHeightPct;
  const DEFAULT_PRINT_SIZE = BUILDER_CONFIG.productDisplay.defaultPrintSize;
  const DEFAULT_MATTED_FRAME_SIZE = BUILDER_CONFIG.productDisplay.defaultMattedFrameSize;
  const DEFAULT_MATTED_PRINT_SIZE = BUILDER_CONFIG.productDisplay.defaultMattedPrintSize;
  const MATTED_PRINT_BY_FRAME = BUILDER_CONFIG.productDisplay.mattedPrintByFrame;

  const ATTRIBUTION_PARAM_NAMES = new Set([
    '_ga', '_gl', '_kx',
    'dclid', 'fbclid', 'gbraid', 'gclid', 'gclsrc', 'gclsrcaw', 'gclsrcds', 'wbraid',
    'gad_source', 'gad_campaignid',
    'msclkid', 'ttclid', 'twclid', 'li_fat_id', 'irclickid', 'srsltid',
    'epik', 'mc_cid', 'mc_eid'
  ]);

  const ATTRIBUTION_PARAM_PREFIXES = ['utm_'];
  const MAX_PRINT_ID_LENGTH = 80;
  const PRINT_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]*$/;

  function isAttributionParamName(name) {
    const normalized = String(name || '').trim().toLowerCase();
    if (!normalized) return false;
    return ATTRIBUTION_PARAM_NAMES.has(normalized)
      || ATTRIBUTION_PARAM_PREFIXES.some(prefix => normalized.startsWith(prefix));
  }

  function copyAttributionParams(searchParams) {
    const params = new URLSearchParams();
    if (!searchParams || typeof searchParams.forEach !== 'function') return params;

    searchParams.forEach((value, key) => {
      if (isAttributionParamName(key)) params.append(key, value);
    });

    return params;
  }

  function deleteAttributionParams(searchParams) {
    if (!searchParams || typeof searchParams.keys !== 'function') return;
    Array.from(searchParams.keys()).forEach(key => {
      if (isAttributionParamName(key)) searchParams.delete(key);
    });
  }

  function getPreservedNavigationParams() {
    try {
      return copyAttributionParams(new URL(window.location.href).searchParams);
    } catch (error) {
      return new URLSearchParams();
    }
  }

  const state = {
    product: JSON.parse(productScript.textContent),
    catalogProvider: null,
    catalog: null,
    apiResultIds: [],
    apiCursor: null,
    apiTotal: 0,
    apiHasMore: false,
    apiLoading: false,
    apiQuerySignature: '',
    apiRequestSeq: 0,
    apiAbortController: null,
    collectionEntries: [],
    printToSource: {},
    searchIndex: {},
    activeSlot: null,
    setSize: DEFAULT_SET_SIZE,
    hasCompletedFirstSet: false,
    selectedPrints: Array(DEFAULT_SET_SIZE).fill(null),
    selectedCollectionHandle: ALL_HANDLE,
    selectedVariantId: null,
    shuffleIndexByCollection: {},
    searchQuery: '',
    currentResultIds: [],
    filteredResultIds: [],
    filteredResultSignature: '',
    renderedResultSignature: '',
    renderedWindowStart: 0,
    renderedWindowEnd: 0,
    virtualColumns: 5,
    virtualRowHeight: 0,
    virtualResultCount: 0,
    virtualScrollTimer: null,
    virtualRenderRaf: null,
    lastResultsMetaText: '',
    selectedSizeInches: null,
    previewOuterSize: DEFAULT_PRINT_SIZE,
    previewPrintSize: DEFAULT_PRINT_SIZE,
    hasMat: false,
    isFramed: false,
    historyPast: [],
    historyFuture: [],
    isRestoringHistory: false,
    maxHistory: 50,
    drag: null,
    suppressNextClick: false,
    sheetMode: 'closed',
    specSlideIndex: 0,
    specScrollLockY: 0,
    specBodyLockStyles: null,
    zoomScrollLockY: 0,
    zoomBodyLockStyles: null,
    zoomScale: 1,
    zoomMinScale: 1,
    zoomMaxScale: 4,
    zoomTranslateX: 0,
    zoomTranslateY: 0,
    zoomSceneWidth: 0,
    zoomSceneHeight: 0,
    sheetLayoutLocked: false,
    sheetLayoutMetrics: null,
    variantIdObserver: null,
    variantSyncRaf: null,
    isSurprising: false,
    surpriseTimers: [],
    lastSurpriseSourceId: null,
    lastSurpriseResultKey: null,
    lastSurpriseContextKey: '',
    apiSurpriseSkipSourceIds: [],
    apiPrintMetaById: {},
    apiEmptyConfirmTimer: null,
    emptySearchResetQuery: '',
    gridScrollTop: 0,
    mobileViewportLocked: false,
    mobileScrollLockY: 0,
    mobileBodyLockStyles: null,
    mobileTouchStartY: null,
    pendingTouchDrag: null,
    shouldRestoreGridScroll: false,
    dragScrollLocked: false,
    dragLockedThumbScrollTop: null,
    urlSyncReady: false,
    lastSyncedBuilderUrl: '',
    slotScale: 1,
    mobileSearchActive: false,
    mobileSearchRestoreTimer: null,
    touchSearchViewportLocked: false,
    touchSearchScrollY: 0,
    touchSearchScrollCaptured: false,
    restoreSheetExpanded: false,
    cachedTransferState: null,
    transferredOptions: null,
    transferredVariantId: '',
    transferredVariantLockUntil: 0,
    hasExplicitCatalogState: false,
    hasExplicitCollectionState: false,
    hasExplicitSearchState: false,
    lastKnownPrintUrlValue: '',
    hasExplicitlyClearedPrints: false,
    catalogEngagementPending: false,
    catalogPurchaseCueArmed: false,
    catalogPurchaseSlotsChanged: false,
    catalogPurchaseLastSignature: ''
  };

  const els = {
    stage: root.querySelector('.psb-stage'),
    bg: root.querySelector('[data-psb-bg]'),
    variantLabel: root.querySelector('[data-psb-variant-label]'),
    previewTitle: root.querySelector('[data-psb-preview-title]'),
    collectionSelect: root.querySelector('[data-psb-collection-select]'),
    search: root.querySelector('[data-psb-search]'),
    searchWrap: root.querySelector('.psb-search-wrap'),
    searchClear: root.querySelector('[data-psb-search-clear]'),
    resultsMeta: root.querySelector('[data-psb-results-meta]'),
    thumbs: root.querySelector('[data-psb-thumbs]'),
    mobileVariantBar: root.querySelector('[data-psb-mobile-variant-bar]'),
    status: root.querySelector('[data-psb-status]'),
    shuffle: root.querySelector('[data-psb-shuffle]'),
    clear: root.querySelector('[data-psb-clear]'),
    undo: root.querySelector('[data-psb-undo]'),
    redo: root.querySelector('[data-psb-redo]'),
    toast: root.querySelector('[data-psb-toast]'),
    guidance: root.querySelector('[data-psb-guidance]'),
    guidancePanel: root.querySelector('.psb-guidance__panel'),
    guidanceTitle: root.querySelector('[data-psb-guidance-title]'),
    guidanceMessage: root.querySelector('[data-psb-guidance-message]'),
    guidanceCollectionWrap: root.querySelector('[data-psb-guidance-collection-wrap]'),
    guidanceCollection: root.querySelector('[data-psb-guidance-collection]'),
    guidanceCloseButtons: Array.from(root.querySelectorAll('[data-psb-guidance-close]')),
    controls: root.querySelector('[data-psb-controls]'),
    sheetAddCart: root.querySelector('[data-psb-sheet-add-cart]'),
    sheetCollapse: root.querySelector('[data-psb-sheet-collapse]'),
    footerAddCart: root.querySelector('[data-psb-footer-add-cart]'),
    stageSummary: root.querySelector('[data-psb-stage-summary]'),
    stageSummaryConfig: root.querySelector('[data-psb-stage-summary-config]'),
    stageSummaryValue: root.querySelector('[data-psb-stage-summary-value]'),
    stageSummaryDiscount: root.querySelector('[data-psb-stage-summary-discount]'),
    stageSummaryAvailability: root.querySelector('[data-psb-stage-summary-availability]'),
    stageZoom: root.querySelector('[data-psb-stage-zoom]'),
    zoomLightbox: root.querySelector('[data-psb-zoom-lightbox]'),
    zoomViewport: root.querySelector('[data-psb-zoom-viewport]'),
    zoomScene: root.querySelector('[data-psb-zoom-scene]'),
    zoomClose: root.querySelector('[data-psb-zoom-close]'),
    catalogOpen: root.querySelector('[data-psb-open-catalog]'),
    shuffleFooterHost: root.querySelector('[data-psb-footer-magic-host]'),
    specsOpen: root.querySelector('[data-psb-open-specs]'),
    share: root.querySelector('[data-psb-share]'),
    specsLightbox: root.querySelector('[data-psb-spec-lightbox]'),
    specsTrack: root.querySelector('[data-psb-spec-track]'),
    specsSlides: Array.from(root.querySelectorAll('[data-psb-spec-slide]')),
    specsCloseButtons: Array.from(root.querySelectorAll('[data-psb-spec-close]')),
    specsPrev: root.querySelector('[data-psb-spec-prev]'),
    specsNext: root.querySelector('[data-psb-spec-next]'),
    specsCounter: root.querySelector('[data-psb-spec-counter]'),
    setSizeSelector: root.querySelector('[data-psb-set-size-selector]'),
    setSizeOptions: root.querySelector('[data-psb-set-size-options]'),
    productLayoutHost: document.querySelector(`[data-psb-product-layout-host="${root.id}"]`),
    productLayoutSelect: null,
    stageFooterActions: root.querySelector('.psb-stage-footer-actions'),
    slotButtons: Array.from(root.querySelectorAll('[data-psb-slot]')),
    slotTabs: Array.from(root.querySelectorAll('[data-psb-slot-tab]'))
  };

  const dragOverlay = DRAG_OVERLAY_MODULE ? DRAG_OVERLAY_MODULE.create({ root, stage: els.stage }) : null;
  const shuffleStageHome = els.shuffle ? { parent: els.shuffle.parentNode, next: els.shuffle.nextSibling } : null;
  const mobileVariantBarHome = els.mobileVariantBar ? { parent: els.mobileVariantBar.parentNode, next: els.mobileVariantBar.nextSibling } : null;

  function syncShufflePlacement() {
    if (!els.shuffle || !els.shuffleFooterHost || !shuffleStageHome || !shuffleStageHome.parent) return;
    const useFooter = !IS_COMPACT_UI && state.sheetMode !== 'expanded';

    if (useFooter) {
      if (els.shuffle.parentNode !== els.shuffleFooterHost) els.shuffleFooterHost.appendChild(els.shuffle);
    } else if (els.shuffle.parentNode !== shuffleStageHome.parent) {
      const anchor = shuffleStageHome.next && shuffleStageHome.next.parentNode === shuffleStageHome.parent
        ? shuffleStageHome.next
        : null;
      shuffleStageHome.parent.insertBefore(els.shuffle, anchor);
    }

    root.classList.toggle('psb-magic-in-footer', useFooter);
  }

  function syncMobileVariantPlacement() {
    if (!els.mobileVariantBar || !mobileVariantBarHome || !mobileVariantBarHome.parent) return;

    if (IS_COMPACT_UI && els.searchWrap) {
      if (els.mobileVariantBar.previousElementSibling !== els.searchWrap) {
        els.searchWrap.insertAdjacentElement('afterend', els.mobileVariantBar);
      }
      return;
    }

    if (els.mobileVariantBar.parentNode !== mobileVariantBarHome.parent
      || (mobileVariantBarHome.next && els.mobileVariantBar.nextSibling !== mobileVariantBarHome.next)) {
      const anchor = mobileVariantBarHome.next && mobileVariantBarHome.next.parentNode === mobileVariantBarHome.parent
        ? mobileVariantBarHome.next
        : null;
      mobileVariantBarHome.parent.insertBefore(els.mobileVariantBar, anchor);
    }
  }

  function setStatus(message, isError = false) {
    els.status.textContent = message || '';
    els.status.classList.toggle('is-error', Boolean(isError));
  }

  let toastTimer = null;
  let guidanceLastFocus = null;
  let layoutPickerMenu = null;
  let layoutPickerTrigger = null;
  const overlayScrollLocks = new Set();
  const OVERLAY_SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);
  let overlayTouchY = null;

  function ensureToastTopLayer() {
    if (!els.toast || els.toast.parentElement === document.body) return;
    document.body.appendChild(els.toast);
  }

  function getOverlayScrollContainer(target) {
    if (!(target instanceof Element)) return null;

    const layoutMenu = getLayoutPickerMenu();
    if (layoutMenu && !layoutMenu.hidden && layoutMenu.contains(target)) {
      return target.closest('.psb-layout-picker__menu-options');
    }

    if (els.guidance && !els.guidance.hidden && els.guidance.contains(target)) {
      return target.closest('.psb-guidance__panel');
    }

    return null;
  }

  function canOverlayContainerScroll(container, deltaY) {
    if (!container || !Number.isFinite(deltaY) || deltaY === 0) return false;
    const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
    if (maxScrollTop <= 1) return false;
    if (deltaY < 0) return container.scrollTop > 1;
    return container.scrollTop < maxScrollTop - 1;
  }

  function suppressBackgroundWheel(event) {
    if (!overlayScrollLocks.size) return;
    const container = getOverlayScrollContainer(event.target);
    if (container && canOverlayContainerScroll(container, Number(event.deltaY) || 0)) return;
    event.preventDefault();
  }

  function trackOverlayTouch(event) {
    if (!overlayScrollLocks.size || !event.touches || !event.touches.length) {
      overlayTouchY = null;
      return;
    }
    overlayTouchY = event.touches[0].clientY;
  }

  function suppressBackgroundTouchMove(event) {
    if (!overlayScrollLocks.size || !event.touches || !event.touches.length) return;
    const currentY = event.touches[0].clientY;
    const previousY = overlayTouchY;
    overlayTouchY = currentY;
    if (!Number.isFinite(previousY)) {
      event.preventDefault();
      return;
    }

    const deltaY = previousY - currentY;
    const container = getOverlayScrollContainer(event.target);
    if (container && canOverlayContainerScroll(container, deltaY)) return;
    event.preventDefault();
  }

  function suppressBackgroundScrollKey(event) {
    if (!overlayScrollLocks.size || event.defaultPrevented) return;
    if (!OVERLAY_SCROLL_KEYS.has(event.key)) return;

    const target = event.target;
    const tagName = target && target.tagName ? target.tagName.toLowerCase() : '';
    const editable = tagName === 'input' || tagName === 'textarea' || tagName === 'select' || Boolean(target && target.isContentEditable);
    const activatesButton = event.key === ' ' && tagName === 'button';
    if (editable || activatesButton) return;
    event.preventDefault();
  }

  function setOverlayScrollLock(reason, active) {
    if (!reason) return;
    if (active) overlayScrollLocks.add(reason);
    else overlayScrollLocks.delete(reason);
    if (!overlayScrollLocks.size) overlayTouchY = null;
  }

  function hideToast() {
    if (!els.toast) return;
    window.clearTimeout(toastTimer);
    toastTimer = null;
    els.toast.classList.remove('is-visible');
    window.setTimeout(() => {
      if (els.toast && !els.toast.classList.contains('is-visible')) els.toast.innerHTML = '';
    }, 170);
  }

  function showToast(message, options = {}) {
    if (!els.toast || !message) return;
    ensureToastTopLayer();
    hideToast();
    els.toast.innerHTML = `<span class="psb-toast-message">${escapeHtml(message)}</span>`;
    window.requestAnimationFrame(() => els.toast.classList.add('is-visible'));
    const duration = Math.max(900, Number(options.duration) || 2200);
    toastTimer = window.setTimeout(hideToast, duration);
  }

  function syncSearchClearButton() {
    if (!els.searchClear || !els.search) return;
    els.searchClear.hidden = !String(els.search.value || '').length;
  }

  function clearSearchQuery(options = {}) {
    if (!els.search) return false;
    const hadQuery = Boolean(String(state.searchQuery || els.search.value || '').length);
    els.search.value = '';
    state.searchQuery = '';
    state.emptySearchResetQuery = '';
    window.clearTimeout(state.apiEmptyConfirmTimer);
    state.apiEmptyConfirmTimer = null;
    syncSearchClearButton();

    if (hadQuery && options.render !== false) resetResultsAndRender();
    updateHiddenProperties();

    if (options.focus && typeof els.search.focus === 'function') {
      window.requestAnimationFrame(() => els.search.focus({ preventScroll: true }));
    }
    return hadQuery;
  }

  function ensureGuidanceTopLayer() {
    if (!els.guidance || els.guidance.parentElement === document.body) return;
    document.body.appendChild(els.guidance);
  }

  function syncGuidanceCollectionOptions() {
    if (!els.guidanceCollection || !els.collectionSelect) return;
    els.guidanceCollection.innerHTML = els.collectionSelect.innerHTML;
    els.guidanceCollection.value = els.collectionSelect.value || state.selectedCollectionHandle || ALL_HANDLE;
  }

  function hideGuidance() {
    if (!els.guidance || els.guidance.hidden) return;
    els.guidance.hidden = true;
    els.guidance.setAttribute('aria-hidden', 'true');
    setOverlayScrollLock('guidance', false);
    const focusTarget = guidanceLastFocus;
    guidanceLastFocus = null;
    if (focusTarget && document.contains(focusTarget) && typeof focusTarget.focus === 'function') {
      window.requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }));
    }
  }

  function showGuidance(message, options = {}) {
    if (!els.guidance || !message) {
      showToast(message);
      return;
    }

    hideToast();
    ensureGuidanceTopLayer();
    guidanceLastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (els.guidanceTitle) els.guidanceTitle.textContent = options.title || 'Builder tip';
    if (els.guidanceMessage) els.guidanceMessage.textContent = message;

    const showCollections = Boolean(options.showCollections && els.guidanceCollectionWrap && els.guidanceCollection);
    if (els.guidanceCollectionWrap) els.guidanceCollectionWrap.hidden = !showCollections;
    if (showCollections) syncGuidanceCollectionOptions();

    els.guidance.hidden = false;
    els.guidance.setAttribute('aria-hidden', 'false');
    setOverlayScrollLock('guidance', true);
    window.requestAnimationFrame(() => {
      if (els.guidancePanel && typeof els.guidancePanel.focus === 'function') els.guidancePanel.focus({ preventScroll: true });
    });
  }

  function showMatchingFailureGuidance() {
    showGuidance('Try adding more prints or choose another category.', {
      title: 'No close match found',
      showCollections: true
    });
  }

  function getSlotColor(slotIndex) {
    return LAYOUT_MODULE.getSlotColor(slotIndex);
  }

  function getSetSize() {
    return state.setSize;
  }

  function normalizeSetSize(value, fallback = DEFAULT_SET_SIZE) {
    const count = Number(value);
    return LAYOUT_MODULE.isSupportedCount(count) ? count : fallback;
  }

  function getSetSizeCopy(count = getSetSize()) {
    return `${count} ${count === 1 ? 'print' : 'prints'}`;
  }

  function isMultiProductMode() {
    return root.dataset.psbProductMode === 'multi' && Boolean(PRODUCT_MAP_MODULE);
  }

  function getCurrentProductHandle() {
    return String(root.dataset.psbCurrentProductHandle || '').trim();
  }

  function getMappedCurrentSetSize() {
    if (!isMultiProductMode()) return 0;
    const explicit = normalizeSetSize(root.dataset.psbCurrentSetSize, 0);
    if (explicit) return explicit;
    return normalizeSetSize(PRODUCT_MAP_MODULE.getCountForHandle(getCurrentProductHandle()), 0);
  }

  function getTransferOptionState() {
    const names = getProductOptionNames();
    const values = getCurrentVariantOptions();
    const result = {};
    names.forEach((name, index) => {
      if (!name || getSetSizeOptionIndex() === index) return;
      const value = values[index];
      if (value) result[name] = value;
    });
    return result;
  }

  function getCurrentCatalogControlState() {
    return {
      collection: String((els.collectionSelect && els.collectionSelect.value) || state.selectedCollectionHandle || ALL_HANDLE).trim() || ALL_HANDLE,
      search: els.search ? String(els.search.value || '') : String(state.searchQuery || ''),
      scrollTop: els.thumbs ? Math.max(0, Number(els.thumbs.scrollTop) || 0) : Math.max(0, Number(state.gridScrollTop) || 0)
    };
  }

  function getTransferPrintRecord(printId) {
    if (!printId) return null;
    const print = getPrint(printId);
    if (!print || !print.src) return null;
    const meta = state.apiPrintMetaById && state.apiPrintMetaById[printId] ? state.apiPrintMetaById[printId] : {};
    return {
      id: printId,
      src: print.src,
      thumb: print.thumb || print.src,
      preview: print.preview || print.src,
      sourceIds: Array.isArray(meta.sourceIds) ? meta.sourceIds : (Array.isArray(print.sourceIds) ? print.sourceIds : []),
      collectionHandles: Array.isArray(meta.collectionHandles) ? meta.collectionHandles : (Array.isArray(print.collectionHandles) ? print.collectionHandles : [])
    };
  }

  function writeMultiProductSessionState(nextCount) {
    if (!isMultiProductMode() || !window.sessionStorage) return;
    try {
      rememberGridPosition();
      const controls = getCurrentCatalogControlState();
      const resultIds = isApiCatalogMode() ? (state.apiResultIds || []).slice() : [];
      const selectedIds = getPersistentPrintIdsForCount(Number(nextCount) || getSetSize());
      const recordIds = uniqueIds([...resultIds, ...selectedIds.filter(Boolean)]);
      const records = recordIds.map(getTransferPrintRecord).filter(Boolean);
      const payload = {
        version: 2,
        savedAt: Date.now(),
        targetCount: Number(nextCount) || getSetSize(),
        sourceHandle: getCurrentProductHandle(),
        collection: controls.collection,
        search: controls.search,
        scrollTop: controls.scrollTop,
        sheetExpanded: state.sheetMode === 'expanded',
        activeSlot: state.activeSlot,
        selectedIds,
        api: isApiCatalogMode() ? {
          querySignature: `${controls.collection || ALL_HANDLE}|${controls.search || ''}`,
          resultIds,
          cursor: state.apiCursor || null,
          total: Number(state.apiTotal) || resultIds.length,
          hasMore: Boolean(state.apiHasMore),
          records
        } : null
      };
      window.sessionStorage.setItem(MULTI_PRODUCT_SESSION_KEY, JSON.stringify(payload));
    } catch (error) {
      // URL transfer remains the fallback when sessionStorage is unavailable or full.
    }
  }

  function readMultiProductSessionState() {
    if (!isMultiProductMode() || !window.sessionStorage) return null;
    try {
      const raw = window.sessionStorage.getItem(MULTI_PRODUCT_SESSION_KEY);
      if (!raw) return null;
      const payload = JSON.parse(raw);
      if (!payload || payload.version !== 2) return null;
      if (!Number.isFinite(Number(payload.savedAt)) || Date.now() - Number(payload.savedAt) > MULTI_PRODUCT_SESSION_MAX_AGE) {
        window.sessionStorage.removeItem(MULTI_PRODUCT_SESSION_KEY);
        return null;
      }
      const params = new URLSearchParams(window.location.search || '');
      const urlCount = normalizeSetSize(params.get('psb_count'), getMappedCurrentSetSize() || getSetSize());
      if (normalizeSetSize(payload.targetCount, 0) !== urlCount) return null;
      const urlIds = parseDeeplinkPrintIds(params.get('psb_prints') || '', urlCount);
      if (urlIds.length) {
        const cachedIds = Array.isArray(payload.selectedIds) ? payload.selectedIds.map(value => String(value || '')) : [];
        const comparableUrl = urlIds.map(value => String(value || '')).join('|');
        const comparableCached = cachedIds.slice(0, urlIds.length).join('|');
        if (comparableUrl !== comparableCached) return null;
      }
      return payload;
    } catch (error) {
      return null;
    }
  }

  function hydrateTransferPrintRecords(records) {
    if (!Array.isArray(records) || !records.length) return;
    if (!state.catalog) state.catalog = { collections: {}, sources: {}, prints: {} };
    if (!state.catalog.prints) state.catalog.prints = {};
    records.forEach(record => {
      if (!record || !record.id || !record.src) return;
      const id = String(record.id);
      state.catalog.prints[id] = {
        ...(state.catalog.prints[id] || {}),
        src: record.src,
        thumb: record.thumb || record.src,
        preview: record.preview || record.src,
        sourceIds: Array.isArray(record.sourceIds) ? record.sourceIds : [],
        collectionHandles: Array.isArray(record.collectionHandles) ? record.collectionHandles : []
      };
      state.apiPrintMetaById[id] = {
        sourceIds: Array.isArray(record.sourceIds) ? record.sourceIds : [],
        collectionHandles: Array.isArray(record.collectionHandles) ? record.collectionHandles : []
      };
    });
  }

  function restoreTransferredApiWindow() {
    const cached = state.cachedTransferState;
    const api = cached && cached.api;
    if (!isApiCatalogMode() || !api || !Array.isArray(api.resultIds) || !api.resultIds.length) return false;
    const signature = getApiQuerySignature();
    if (String(api.querySignature || '') !== signature) return false;

    hydrateTransferPrintRecords(api.records);
    state.apiResultIds = uniqueIds(api.resultIds.filter(id => getPrint(id)));
    if (!state.apiResultIds.length) return false;
    state.apiCursor = api.cursor || null;
    state.apiTotal = Math.max(Number(api.total) || 0, state.apiResultIds.length);
    state.apiHasMore = Boolean(api.hasMore && state.apiCursor);
    state.apiQuerySignature = signature;
    state.gridScrollTop = Math.max(0, Number(cached.scrollTop) || 0);
    state.shouldRestoreGridScroll = state.gridScrollTop > 0;
    invalidateFilteredResults();
    return true;
  }

  function getTransferStateParams(nextCount) {
    rememberGridPosition();

    // Read the controls directly. The search field may still be inside its debounce
    // window when the user changes set size, so state.searchQuery can be stale.
    const selectedCollection = String(
      (els.collectionSelect && els.collectionSelect.value) || state.selectedCollectionHandle || ALL_HANDLE
    ).trim() || ALL_HANDLE;
    const searchQuery = els.search ? String(els.search.value || '') : String(state.searchQuery || '');
    state.selectedCollectionHandle = selectedCollection;
    state.searchQuery = searchQuery;

    const params = getPreservedNavigationParams();
    params.set('psb_count', String(nextCount));

    const prints = getPersistentPrintUrlValue(nextCount);
    if (prints) {
      params.set('psb_prints', prints);
    } else {
      const source = getCurrentSourceUrlValue();
      if (source) params.set('psb_source', source);
    }

    // Always pass both real control values, including All Prints and an empty search.
    // Their presence tells the destination builder not to auto-select a collection
    // from the transferred print IDs.
    params.set('psb_collection', selectedCollection);
    params.set('psb_search', searchQuery);
    if (state.gridScrollTop > 0) params.set('psb_scroll', String(Math.round(state.gridScrollTop)));
    if (state.activeSlot !== null && state.activeSlot !== undefined) {
      params.set('psb_slot', String(state.activeSlot));
    }
    if (state.sheetMode === 'expanded') params.set('psb_sheet', '1');

    const options = getTransferOptionState();
    if (Object.keys(options).length) params.set('psb_options', JSON.stringify(options));

    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.get('psb_hint') === '1') params.set('psb_hint', '1');
    params.set('psb_transition', '1');
    return params;
  }

  function routeToSetProduct(nextCount) {
    if (!isMultiProductMode()) return false;
    const handle = PRODUCT_MAP_MODULE.resolveHandle(nextCount, getCurrentProductHandle());
    if (!handle) {
      showGuidance(`The product for ${getSetSizeCopy(nextCount)} is not configured yet.`, { title: 'Layout unavailable' });
      return false;
    }
    if (handle === getCurrentProductHandle()) return false;

    const params = getTransferStateParams(nextCount);
    writeMultiProductSessionState(nextCount);
    window.location.assign(`/products/${encodeURIComponent(handle)}?${params.toString()}`);
    return true;
  }

  function restoreInterfaceStateFromLocation() {
    const params = new URLSearchParams(window.location.search || '');
    const hasCollection = params.has('psb_collection');
    const hasSearch = params.has('psb_search');
    const collection = params.get('psb_collection');
    const search = params.get('psb_search');
    const scroll = Number(params.get('psb_scroll'));
    const activeSlot = Number(params.get('psb_slot'));

    state.hasExplicitCollectionState = hasCollection;
    state.hasExplicitSearchState = hasSearch;
    state.hasExplicitCatalogState = hasCollection || hasSearch;

    if (hasCollection) state.selectedCollectionHandle = collection || ALL_HANDLE;
    if (hasSearch) state.searchQuery = search || '';
    if (Number.isFinite(scroll) && scroll > 0) {
      state.gridScrollTop = scroll;
      state.shouldRestoreGridScroll = true;
    }
    if (Number.isFinite(activeSlot) && activeSlot >= 0) state.activeSlot = activeSlot;
    state.restoreSheetExpanded = params.get('psb_sheet') === '1';
    state.cachedTransferState = readMultiProductSessionState();
    if (state.cachedTransferState) {
      if (!hasCollection && state.cachedTransferState.collection) state.selectedCollectionHandle = state.cachedTransferState.collection;
      if (!hasSearch && typeof state.cachedTransferState.search === 'string') state.searchQuery = state.cachedTransferState.search;
      if (!(Number.isFinite(scroll) && scroll > 0) && Number(state.cachedTransferState.scrollTop) > 0) {
        state.gridScrollTop = Number(state.cachedTransferState.scrollTop);
        state.shouldRestoreGridScroll = true;
      }
      if (!state.restoreSheetExpanded && state.cachedTransferState.sheetExpanded) state.restoreSheetExpanded = true;
    }

    if (els.search) els.search.value = state.searchQuery;
    syncSearchClearButton();
  }

  function normalizeTransferredOptionValue(value) {
    return String(value || '')
      .trim()
      .replace(/\s+/g, ' ')
      .toLowerCase();
  }

  function getTransferredOptionsFromLocation() {
    const raw = new URLSearchParams(window.location.search || '').get('psb_options');
    if (!raw) return null;

    try {
      const requested = JSON.parse(raw);
      return requested && typeof requested === 'object' ? requested : null;
    } catch (error) {
      return null;
    }
  }

  function findExactVariantForTransferredOptions(requested) {
    if (!requested || typeof requested !== 'object') return null;
    const names = getProductOptionNames();
    if (!names.length) return null;

    const requestedByIndex = names.map(name => (
      Object.prototype.hasOwnProperty.call(requested, name)
        ? normalizeTransferredOptionValue(requested[name])
        : ''
    ));

    if (!requestedByIndex.some(Boolean)) return null;

    const matches = variant => requestedByIndex.every((requestedValue, index) => (
      !requestedValue || normalizeTransferredOptionValue(getVariantOptionValue(variant, index)) === requestedValue
    ));

    return (state.product.variants || []).find(variant => variant.available !== false && matches(variant))
      || (state.product.variants || []).find(matches)
      || null;
  }

  function enforceTransferredVariant() {
    const variantId = String(state.transferredVariantId || '');
    if (!variantId) return false;
    const variant = (state.product.variants || []).find(item => String(item.id) === variantId);
    if (!variant) return false;

    setNativeOptionControlsForVariant(variant);
    setVariantIdInProductForm(variant);
    state.selectedVariantId = variantId;
    updatePreviewFromVariant();
    return true;
  }

  function applyVariantOptionsFromLocation() {
    const requested = getTransferredOptionsFromLocation();
    if (!requested) return false;

    const variant = findExactVariantForTransferredOptions(requested);
    if (!variant) return false;

    state.transferredOptions = { ...requested };
    state.transferredVariantId = String(variant.id);
    state.transferredVariantLockUntil = Date.now() + 2200;
    enforceTransferredVariant();

    [80, 220, 500, 950, 1500].forEach(delay => {
      window.setTimeout(() => {
        if (Date.now() <= state.transferredVariantLockUntil) enforceTransferredVariant();
      }, delay);
    });

    return true;
  }

  function getSetSizeDiscountLabel(count) {
    if (count === 2) return '-5%';
    if (count === 3 || count === 4) return '-10%';
    if (count === 6 || count === 8) return '-15%';
    if (count === 9) return '-20%';
    if (count === 12) return '-25%';
    return '';
  }

  function getSetSizeLabel(count) {
    return `Set of ${count} ${count === 1 ? 'print' : 'prints'}`;
  }

  function getLayoutMiniHtml(count) {
    const preset = LAYOUT_MODULE.getPreset(count);
    const columns = Math.max(1, Number(preset && preset.columns) || 1);
    const rows = Math.max(1, Number(preset && preset.rows) || 1);
    const baseCellWidth = 5;
    const baseCellHeight = 7;
    const baseGap = 1.4;
    const naturalWidth = columns * baseCellWidth + Math.max(0, columns - 1) * baseGap;
    const naturalHeight = rows * baseCellHeight + Math.max(0, rows - 1) * baseGap;
    const scale = Math.min(30 / naturalWidth, 24 / naturalHeight);
    const cellWidth = (baseCellWidth * scale).toFixed(2);
    const cellHeight = (baseCellHeight * scale).toFixed(3);
    const gap = (baseGap * scale).toFixed(2);
    const cells = Array.from({ length: count }, () => '<span class="psb-layout-mini__cell"></span>').join('');
    return `<span class="psb-layout-mini" style="--psb-layout-columns:${columns};--psb-layout-rows:${rows};--psb-layout-cell-width:${cellWidth}px;--psb-layout-cell-height:${cellHeight}px;--psb-layout-gap:${gap}px" aria-hidden="true">${cells}</span>`;
  }

  function getLayoutDiscountHtml(count) {
    const discount = getSetSizeDiscountLabel(count);
    return `<span class="psb-layout-discount${discount ? '' : ' psb-layout-discount--empty'}"${discount ? '' : ' aria-hidden="true"'}>${discount || '0%'}</span>`;
  }

  function getLayoutPickerMenu() {
    if (layoutPickerMenu && document.contains(layoutPickerMenu)) return layoutPickerMenu;
    layoutPickerMenu = document.querySelector(`[data-psb-layout-menu][data-psb-owner="${root.id}"]`);
    return layoutPickerMenu;
  }

  function getLayoutPickerTrigger() {
    if (layoutPickerTrigger && document.contains(layoutPickerTrigger)) return layoutPickerTrigger;
    layoutPickerTrigger = els.setSizeOptions
      ? els.setSizeOptions.querySelector('[data-psb-layout-trigger]')
      : null;
    return layoutPickerTrigger;
  }

  function ensureLayoutPickerTopLayer() {
    const menu = getLayoutPickerMenu();
    if (!menu || menu.parentElement === document.body) return menu;
    document.body.appendChild(menu);
    return menu;
  }

  function isLayoutPickerOpen() {
    const menu = getLayoutPickerMenu();
    const trigger = getLayoutPickerTrigger();
    return Boolean(menu && trigger && !menu.hidden && trigger.getAttribute('aria-expanded') === 'true');
  }

  function positionLayoutPickerHorizontally() {
    if (!isLayoutPickerOpen()) return;
    const menu = getLayoutPickerMenu();
    const trigger = getLayoutPickerTrigger();
    if (!menu || !trigger) return;

    const rect = trigger.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      closeLayoutPicker();
      return;
    }

    const viewportWidth = Math.max(1, window.innerWidth || document.documentElement.clientWidth || 1);
    const edge = 10;
    const width = Math.min(rect.width, 560, viewportWidth - edge * 2);
    const centeredLeft = rect.left + rect.width / 2 - width / 2;
    const left = Math.min(Math.max(centeredLeft, edge), viewportWidth - width - edge);

    menu.style.width = `${Math.round(width)}px`;
    menu.style.left = `${Math.round(left)}px`;
    menu.style.right = 'auto';
  }

  function isSetSizeAvailable(count, currentCount = getSetSize()) {
    const normalizedCount = Number(count);
    if (isMultiProductMode()) {
      const mappedCurrentCount = getMappedCurrentSetSize();
      if (normalizedCount === mappedCurrentCount) return true;
      return Boolean(PRODUCT_MAP_MODULE && PRODUCT_MAP_MODULE.resolveHandle(normalizedCount, getCurrentProductHandle()));
    }
    if (normalizedCount === Number(currentCount)) return true;
    const optionIndex = getSetSizeOptionIndex();
    return optionIndex < 0 || Boolean(findVariantForSetSize(normalizedCount));
  }

  function openLayoutPicker() {
    const menu = ensureLayoutPickerTopLayer();
    const trigger = getLayoutPickerTrigger();
    if (!menu || !trigger || isLayoutPickerOpen()) return;
    menu.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    setOverlayScrollLock('layout-picker', true);
    const picker = trigger.closest('[data-psb-layout-picker]');
    if (picker) picker.classList.add('is-open');
    positionLayoutPickerHorizontally();
  }

  function closeLayoutPicker(options = {}) {
    const menu = getLayoutPickerMenu();
    const trigger = getLayoutPickerTrigger();
    if (menu) {
      menu.hidden = true;
      menu.style.left = '';
      menu.style.width = '';
    }
    setOverlayScrollLock('layout-picker', false);
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      const picker = trigger.closest('[data-psb-layout-picker]');
      if (picker) picker.classList.remove('is-open');
      if (options.restoreFocus && typeof trigger.focus === 'function') {
        window.requestAnimationFrame(() => trigger.focus({ preventScroll: true }));
      }
    }
  }

  function toggleLayoutPicker() {
    if (isLayoutPickerOpen()) closeLayoutPicker();
    else openLayoutPicker();
  }

  function renderSetSizeSelector() {
    if (!els.setSizeOptions) return;

    const showStagePicker = UI_CONFIG.showStageLayoutPicker !== false;
    if (els.setSizeSelector) els.setSizeSelector.hidden = !showStagePicker;
    if (!showStagePicker) {
      els.setSizeOptions.innerHTML = '';
      return;
    }

    const options = SUPPORTED_SET_SIZES
      .filter(count => isSetSizeAvailable(count))
      .map(count => {
      const discount = getSetSizeDiscountLabel(count);
      const discountCopy = discount ? `, ${discount} discount` : '';
      return `<button type="button" class="psb-layout-option" data-psb-set-size="${count}" aria-pressed="false" aria-label="${getSetSizeLabel(count)}${discountCopy}"><span class="psb-layout-option__main">${getLayoutMiniHtml(count)}<span class="psb-layout-option__label">${getSetSizeLabel(count)}</span></span>${getLayoutDiscountHtml(count)}</button>`;
    }).join('');

    const menuId = `${root.id}-LayoutMenu`;
    els.setSizeOptions.innerHTML = `
      <div class="psb-layout-picker" data-psb-layout-picker>
        <button type="button" class="psb-layout-picker__trigger" data-psb-layout-trigger aria-label="Choose layout" aria-expanded="false" aria-controls="${menuId}">
          <span class="psb-layout-picker__summary-copy">
            <span class="psb-layout-picker__eyebrow">Choose layout:</span>
            <span class="psb-layout-picker__current" data-psb-layout-current></span>
          </span>
          <span class="psb-layout-picker__caret" aria-hidden="true"></span>
        </button>
      </div>
      <div id="${menuId}" class="psb-layout-picker__menu" data-psb-layout-menu data-psb-owner="${root.id}" aria-label="Available layouts" hidden>
        <div class="psb-layout-picker__menu-head">
          <span>Choose layout</span>
          <button type="button" class="psb-layout-picker__close" data-psb-layout-close aria-label="Close layout menu">×</button>
        </div>
        <div class="psb-layout-picker__menu-options">${options}</div>
      </div>`;

    layoutPickerTrigger = els.setSizeOptions.querySelector('[data-psb-layout-trigger]');
    layoutPickerMenu = els.setSizeOptions.querySelector('[data-psb-layout-menu]');
    ensureLayoutPickerTopLayer();
    updateSetSizeSelector();
  }

  function getProductLayoutOptionLabel(count) {
    const discount = getSetSizeDiscountLabel(count);
    const discountText = discount ? ` — ${discount.replace('-', '')} off` : '';
    return `${getSetSizeLabel(count)}${discountText}`;
  }

  function renderProductLayoutSelector() {
    if (!els.productLayoutHost) return;
    const showProductPicker = UI_CONFIG.showProductLayoutSelector !== false;
    els.productLayoutHost.hidden = !showProductPicker;
    if (!showProductPicker) {
      els.productLayoutHost.innerHTML = '';
      els.productLayoutSelect = null;
      return;
    }

    const availableCounts = SUPPORTED_SET_SIZES.filter(count => isSetSizeAvailable(count));
    if (!availableCounts.length) {
      els.productLayoutHost.hidden = true;
      els.productLayoutHost.innerHTML = '';
      els.productLayoutSelect = null;
      return;
    }

    const selectId = `${root.id}-ProductLayout`;
    const options = availableCounts.map(count => (
      `<option value="${count}">${escapeHtml(getProductLayoutOptionLabel(count))}</option>`
    )).join('');

    els.productLayoutHost.innerHTML = `
      <div class="psb-product-layout-selector">
        <label class="form__label psb-product-layout-label" for="${selectId}">Layout</label>
        <div class="psb-product-layout-select-wrap">
          <select id="${selectId}" class="psb-product-layout-select" data-psb-product-layout-select aria-label="Layout">${options}</select>
        </div>
      </div>`;
    els.productLayoutSelect = els.productLayoutHost.querySelector('[data-psb-product-layout-select]');
    updateProductLayoutSelector();
  }

  function updateProductLayoutSelector() {
    if (!els.productLayoutSelect) return;
    const currentCount = String(getSetSize());
    const hasCurrent = Array.from(els.productLayoutSelect.options).some(option => option.value === currentCount);
    if (hasCurrent) els.productLayoutSelect.value = currentCount;
  }

  function updateSetSizeSelector() {
    updateProductLayoutSelector();
    if (!els.setSizeOptions) return;
    const currentCount = getSetSize();
    const currentHost = els.setSizeOptions.querySelector('[data-psb-layout-current]');
    if (currentHost) {
      currentHost.innerHTML = `${getLayoutMiniHtml(currentCount)}<span class="psb-layout-picker__current-label">${getSetSizeLabel(currentCount)}</span>${getLayoutDiscountHtml(currentCount)}`;
    }

    const menu = getLayoutPickerMenu();
    if (!menu) return;
    menu.querySelectorAll('[data-psb-set-size]').forEach(button => {
      const count = Number(button.dataset.psbSetSize);
      const isCurrent = count === currentCount;
      const isAvailable = isSetSizeAvailable(count, currentCount);
      button.hidden = !isAvailable;
      button.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
    });
  }

  function getInitialSetSizeFromLocation() {
    const params = new URLSearchParams(window.location.search || '');
    const mappedCount = getMappedCurrentSetSize();
    if (mappedCount) return mappedCount;

    const explicitCount = normalizeSetSize(params.get('psb_count'), 0);
    if (explicitCount) return explicitCount;

    const printsParam = params.get('psb_prints') || '';
    if (printsParam) {
      const inferredCount = normalizeSetSize(String(printsParam).split(/[|,]/).length, 0);
      if (inferredCount) return inferredCount;
    }

    const variantCount = getVariantSetSize(getCurrentVariant());
    return variantCount || DEFAULT_SET_SIZE;
  }

  function applySetSize(value, options = {}) {
    const nextCount = normalizeSetSize(value);
    const previousCount = getSetSize();
    const shouldRecordHistory = options.recordHistory !== false;
    const shouldSyncVariant = options.syncVariant !== false;
    const shouldRender = options.render !== false;
    const allowProductNavigation = options.allowProductNavigation !== false;

    if (allowProductNavigation && nextCount !== previousCount && isMultiProductMode()) {
      if (!isSetSizeAvailable(nextCount, previousCount)) {
        updateSetSizeSelector();
        return false;
      }
      if (routeToSetProduct(nextCount)) return true;
      return false;
    }

    if (nextCount === previousCount) {
      root.dataset.psbSetSize = String(nextCount);
      applyStageAspectForSetSize();
      updateSetSizeSelector();
      refreshStageMetricsAfterLayoutChange();
      if (shouldSyncVariant && !isMultiProductMode()) syncVariantToSetSize(nextCount);
      return false;
    }

    if (shouldRecordHistory) pushHistory();
    if (state.isSurprising) {
      clearSurpriseTimers();
      setSurpriseLoading(false);
      els.slotButtons.forEach(button => button.classList.remove('is-surprise-spinning'));
    }

    const previousPrints = state.selectedPrints.slice();
    state.setSize = nextCount;
    state.selectedPrints = Array.from({ length: nextCount }, (_, index) => previousPrints[index] || null);
    state.hasCompletedFirstSet = isSetComplete();

    if (state.activeSlot === null || state.activeSlot >= nextCount) {
      const firstEmpty = getFirstEmptySlotIndex();
      state.activeSlot = firstEmpty >= 0 ? firstEmpty : Math.max(0, nextCount - 1);
    }

    state.lastSurpriseSourceId = null;
    state.lastSurpriseResultKey = null;
    resetApiSurpriseSkipList('');
    root.dataset.psbSetSize = String(nextCount);
    applyStageAspectForSetSize();
    updateSetSizeSelector();

    if (shouldRender) {
      renderSlots();
      refreshStageMetricsAfterLayoutChange();
      updateRenderedThumbSelectionState();
      retryVisibleThumbImages(false);
      updateHiddenProperties();
      requestDesktopLayoutUpdate(true);
      updateMobilePopupMetrics();
    }

    if (shouldSyncVariant && !isMultiProductMode()) syncVariantToSetSize(nextCount);
    return true;
  }

  function getFirstEmptySlotIndex() {
    return state.selectedPrints.findIndex(print => !print);
  }

  function areAllSlotsEmpty() {
    return state.selectedPrints.every(print => !print);
  }

  function getLastUsableSlotIndex(fallback = 0) {
    const current = Number(state.activeSlot);
    if (Number.isFinite(current) && current >= 0 && current < state.selectedPrints.length) return current;
    const firstEmpty = getFirstEmptySlotIndex();
    if (firstEmpty >= 0) return firstEmpty;
    const safeFallback = Number(fallback);
    return Number.isFinite(safeFallback) && safeFallback >= 0 && safeFallback < state.selectedPrints.length ? safeFallback : 0;
  }

  function ensureActiveSlot(fallback = 0) {
    state.activeSlot = getLastUsableSlotIndex(fallback);
    return state.activeSlot;
  }

  function getNextEmptySlotAfter(index) {
    const start = Number(index);
    if (!Number.isFinite(start)) return getFirstEmptySlotIndex();

    for (let offset = 1; offset <= state.selectedPrints.length; offset += 1) {
      const candidate = (start + offset) % state.selectedPrints.length;
      if (!state.selectedPrints[candidate]) return candidate;
    }

    return -1;
  }

  function updateGuidedActiveSlotAfterInsert(targetSlot) {
    if (isSetComplete()) {
      state.hasCompletedFirstSet = true;
      state.activeSlot = targetSlot;
      hideToast();
      return;
    }

    state.hasCompletedFirstSet = false;
    const nextEmpty = getNextEmptySlotAfter(targetSlot);
    state.activeSlot = nextEmpty >= 0 ? nextEmpty : null;
  }

  function isSetComplete() {
    return state.selectedPrints.every(Boolean);
  }

  function getSelectedSlotIndexes(printId) {
    return state.selectedPrints
      .map((selectedPrint, index) => selectedPrint && selectedPrint.id === printId ? index : null)
      .filter(index => index !== null);
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function escapeAttr(value) { return escapeHtml(value); }

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  function normalizeTokens(value) {
    return normalize(value).replaceAll(',', ' ').replaceAll('-', ' ').split(' ').filter(Boolean);
  }

  function getProductForm() {
    const formId = root.dataset.productFormId;
    const byId = formId ? document.getElementById(formId) : null;
    if (byId) return byId;

    const productRoot = root.closest('.product') || root.closest('.product-section') || root.closest('section') || document;
    return productRoot.querySelector('product-form form[action*="/cart/add"]')
      || productRoot.querySelector('form[action*="/cart/add"]')
      || document.querySelector('product-form form[action*="/cart/add"]')
      || document.querySelector('form[action*="/cart/add"]');
  }

  function getProductScope(form = null) {
    return (form && (form.closest('.product') || form.closest('.product-section') || form.closest('section')))
      || root.closest('.product')
      || root.closest('.product-section')
      || root.closest('section')
      || document;
  }

  function getEventPath(event) {
    if (event && typeof event.composedPath === 'function') return event.composedPath();
    const path = [];
    let node = event && event.target ? event.target : null;
    while (node) {
      path.push(node);
      node = node.parentNode || (node.host || null);
    }
    path.push(window);
    return path;
  }

  function elementMatches(element, selector) {
    return Boolean(element && element.nodeType === 1 && typeof element.matches === 'function' && element.matches(selector));
  }

  function closestElement(element, selector) {
    return element && element.nodeType === 1 && typeof element.closest === 'function' ? element.closest(selector) : null;
  }

  function getPurchaseTargetFromEvent(event) {
    const path = getEventPath(event);
    for (const node of path) {
      if (elementMatches(node, PURCHASE_SELECTOR)) return node;
      const closest = closestElement(node, PURCHASE_SELECTOR);
      if (closest) return closest;
    }

    const target = event && event.target && event.target.nodeType === 1 ? event.target : null;
    return closestElement(target, PURCHASE_SELECTOR);
  }

  function isProductPurchaseTarget(target, form = getProductForm()) {
    if (!target || target.nodeType !== 1) return false;

    const scope = getProductScope(form);
    if (form && form.contains(target)) return true;

    const formId = form && form.getAttribute ? form.getAttribute('id') : '';
    const targetFormId = target.getAttribute ? target.getAttribute('form') : '';
    if (formId && targetFormId === formId) return true;

    const paymentContainer = closestElement(target, '.shopify-payment-button');
    if (paymentContainer && scope.contains(paymentContainer)) return true;

    if (scope.contains(target) && elementMatches(target, '.shopify-payment-button__button, [data-shopify="payment-button"], shop-pay-wallet-button, shopify-accelerated-checkout')) return true;

    return false;
  }

  function isProductCartForm(form) {
    if (!form || form.nodeType !== 1 || !elementMatches(form, 'form[action*="/cart/add"]')) return false;
    const expectedForm = getProductForm();
    if (expectedForm && form === expectedForm) return true;
    return getProductScope(expectedForm).contains(form);
  }

  function getOptimizedImageSrc(src, width, forceWidth = false) {
    const raw = String(src || '').trim();
    if (!raw || !width) return raw;

    try {
      const url = new URL(raw, window.location.href);
      const host = url.hostname || '';
      if (!/cdn\.shopify\.com$|shopifycdn\.net$/i.test(host)) return raw;
      if (forceWidth || !url.searchParams.has('width')) url.searchParams.set('width', String(width));
      return url.toString();
    } catch (error) {
      return raw;
    }
  }

  function getOptimizedImageSrcset(src, widths) {
    const raw = String(src || '').trim();
    if (!raw || !Array.isArray(widths)) return '';
    return widths
      .map(width => {
        const optimized = getOptimizedImageSrc(raw, width, true);
        return optimized && optimized !== raw || /cdn\.shopify\.com|shopifycdn\.net/i.test(raw) ? `${optimized} ${width}w` : '';
      })
      .filter(Boolean)
      .join(', ');
  }

  function ensurePropertyInput(form, propertyName) {
    const selector = `input[name="properties[${propertyName}]"]`;
    let input = form.querySelector(selector);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${propertyName}]`;
      form.appendChild(input);
    }
    return input;
  }

  function removeBuilderPropertyInputs(form) {
    if (!form) return;

    const exactLegacyProperties = [
      'Builder',
      'Layout',
      'Selected Theme',
      'Prints',
      'Builder Version',
      '_Builder Version',
      'Set Size'
    ];

    exactLegacyProperties.forEach(propertyName => {
      form.querySelectorAll(`input[name="properties[${propertyName}]"]`).forEach(input => input.remove());
    });

    form.querySelectorAll('input[name^="properties[Print "]').forEach(input => {
      if (/^properties\[Print\s+\d+(?:[^\]]*)?\]$/.test(input.name || '')) input.remove();
    });
  }

  function getPrintPropertyName(slotIndex, printId) {
    const slotNumber = Number(slotIndex) + 1;
    return printId ? `Print ${slotNumber} (${printId})` : `Print ${slotNumber}`;
  }

  function getCatalogMode() {
    return String(CATALOG_CONFIG.mode || 'json').toLowerCase();
  }

  function getPickerHiddenCollectionHandles() {
    return new Set((CATALOG_CONFIG.pickerHiddenHandles || []).filter(Boolean));
  }

  function normalizeCollectionEntry(handle, collection = {}) {
    return {
      handle,
      title: collection.title || handle,
      hiddenInPicker: Boolean(collection.hidden_in_picker || collection.hiddenInPicker || collection.picker_hidden)
    };
  }

  function getCollectionEntriesFromCatalog(catalog) {
    const collections = catalog && catalog.collections ? catalog.collections : {};
    return Object.entries(collections).map(([handle, collection]) => normalizeCollectionEntry(handle, collection));
  }

  function getVisibleCollectionEntries() {
    const hiddenHandles = getPickerHiddenCollectionHandles();
    return (state.collectionEntries || []).filter(entry => {
      if (!entry || !entry.handle) return false;
      if (hiddenHandles.has(entry.handle)) return false;
      return !entry.hiddenInPicker;
    });
  }

  function createLocalJsonCatalogProvider() {
    return {
      mode: 'json',
      async bootstrap() {
        const response = await fetch(CATALOG_CONFIG.jsonUrl, { headers: { 'Accept': 'application/json' } });
        if (!response.ok) throw new Error('Catalog JSON was not found.');
        const catalog = await response.json();
        return {
          catalog,
          collections: getCollectionEntriesFromCatalog(catalog)
        };
      }
    };
  }

  function buildApiUrl(path, params = {}) {
    const base = CATALOG_CONFIG.apiBaseUrl || '';
    if (!base) throw new Error('Catalog API URL is empty.');
    const url = new URL(path, `${base}/`);
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      url.searchParams.set(key, String(value));
    });
    return url.toString();
  }

  async function fetchCatalogApi(path, params = {}, options = {}) {
    const response = await fetch(buildApiUrl(path, params), {
      headers: { 'Accept': 'application/json' },
      credentials: 'omit',
      signal: options.signal
    });
    if (!response.ok) {
      const message = response.status === 429
        ? 'Too many catalog requests. Please wait a moment.'
        : `Catalog API request failed (${response.status}).`;
      throw new Error(message);
    }
    return response.json();
  }

  function createApiCatalogProvider() {
    const limit = Math.max(PAGE_SIZE, Math.min(100, Number(CATALOG_CONFIG.apiLimit) || 80));
    return {
      mode: 'api',
      async bootstrap() {
        const payload = await fetchCatalogApi('v1/bootstrap', {
          collection: root.dataset.defaultCollection || ALL_HANDLE,
          limit
        });
        return {
          catalog: { collections: {}, sources: {}, prints: {} },
          collections: Array.isArray(payload.collections)
            ? payload.collections.map(entry => normalizeCollectionEntry(entry.handle, entry))
            : [],
          initial: payload.initial || null
        };
      },
      async search({ collection, query, cursor, limit: requestedLimit, includeMeta, signal } = {}) {
        return fetchCatalogApi('v1/prints', {
          collection: collection && collection !== ALL_HANDLE ? collection : '',
          q: query || '',
          cursor: cursor || '',
          limit: requestedLimit || limit,
          include_meta: includeMeta ? '1' : ''
        }, { signal });
      },
      async surprise({ collection, skipSourceId, skipSourceIds, selectedPrintIds, defaultCollection } = {}) {
        const skipList = Array.isArray(skipSourceIds) ? skipSourceIds.filter(Boolean) : [];
        return fetchCatalogApi('v1/surprise', {
          collection: collection && collection !== ALL_HANDLE ? collection : '',
          default_collection: defaultCollection || root.dataset.defaultCollection || '',
          skip: skipSourceId || '',
          skip_sources: skipList.join(','),
          selected_ids: Array.isArray(selectedPrintIds) ? selectedPrintIds.filter(Boolean).join(',') : '',
          set_size: getSetSize()
        });
      }
    };
  }

  function createCatalogProvider() {
    return getCatalogMode() === 'api' ? createApiCatalogProvider() : createLocalJsonCatalogProvider();
  }

  function isApiCatalogMode() {
    return state.catalogProvider && state.catalogProvider.mode === 'api';
  }

  function normalizeApiPrintItem(item) {
    if (!item) return null;
    const id = String(item.id || item.print_id || '').trim();
    const src = item.src || item.preview || item.thumb || item.image || '';
    if (!id || !src) return null;
    return {
      id,
      src,
      thumb: item.thumb || src,
      preview: item.preview || src,
      sourceIds: Array.isArray(item.sourceIds) ? item.sourceIds : (Array.isArray(item.source_ids) ? item.source_ids : []),
      collectionHandles: Array.isArray(item.collectionHandles) ? item.collectionHandles : (Array.isArray(item.collection_handles) ? item.collection_handles : [])
    };
  }

  function ingestApiItems(items = []) {
    if (!state.catalog) state.catalog = { collections: {}, sources: {}, prints: {} };
    if (!state.catalog.prints) state.catalog.prints = {};
    const ids = [];
    items.forEach(item => {
      const print = normalizeApiPrintItem(item);
      if (!print) return;
      state.catalog.prints[print.id] = { src: print.preview || print.src, thumb: print.thumb || print.src };
      if (print.sourceIds.length || print.collectionHandles.length) {
        state.apiPrintMetaById[print.id] = {
          sourceIds: uniqueIds(print.sourceIds.filter(Boolean)),
          collectionHandles: uniqueIds(print.collectionHandles.filter(Boolean))
        };
      }
      ids.push(print.id);
    });
    return ids;
  }

  function getApiQuerySignature() {
    return `${state.selectedCollectionHandle || ALL_HANDLE}|${state.searchQuery || ''}`;
  }

  function applyApiPage(payload = {}, options = {}) {
    const ids = ingestApiItems(payload.items || []);
    state.apiCursor = payload.nextCursor || payload.cursor || null;
    state.apiTotal = Number(payload.total || ids.length || 0);
    state.apiHasMore = Boolean(state.apiCursor);
    state.apiQuerySignature = getApiQuerySignature();

    if (options.append) {
      state.apiResultIds = uniqueIds([...(state.apiResultIds || []), ...ids]);
    } else {
      state.apiResultIds = uniqueIds(ids);
      if (options.preserveScroll) {
        state.shouldRestoreGridScroll = true;
      } else {
        state.gridScrollTop = 0;
        if (els.thumbs) els.thumbs.scrollTop = 0;
      }
    }

    invalidateFilteredResults();
  }

  function scheduleApiEmptyResultConfirmation(querySignature) {
    window.clearTimeout(state.apiEmptyConfirmTimer);
    state.apiEmptyConfirmTimer = null;
    if (!isApiCatalogMode() || !querySignature) return;

    state.apiEmptyConfirmTimer = window.setTimeout(() => {
      state.apiEmptyConfirmTimer = null;
      if (state.apiLoading) return;
      if (getApiQuerySignature() !== querySignature) return;
      if ((state.apiResultIds || []).length || Number(state.apiTotal || 0) > 0) return;
      invalidateFilteredResults();
      renderResults({ reset: true, force: true });
    }, 140);
  }

  async function loadApiResults({ reset = false, preserveScroll = false } = {}) {
    if (!isApiCatalogMode() || !state.catalogProvider) return;
    if (!reset && (state.apiLoading || !state.apiHasMore)) return;

    if (reset && state.apiAbortController && typeof state.apiAbortController.abort === 'function') {
      state.apiAbortController.abort();
    }

    window.clearTimeout(state.apiEmptyConfirmTimer);
    state.apiEmptyConfirmTimer = null;

    const requestSeq = state.apiRequestSeq + 1;
    state.apiRequestSeq = requestSeq;
    const requestSignature = getApiQuerySignature();

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    state.apiAbortController = controller;
    state.apiLoading = true;
    let settledRenderOptions = null;

    try {
      const payload = await state.catalogProvider.search({
        collection: state.selectedCollectionHandle,
        query: state.searchQuery,
        cursor: reset ? '' : state.apiCursor,
        limit: CATALOG_CONFIG.apiLimit || 80,
        signal: controller ? controller.signal : undefined
      });
      if (requestSeq !== state.apiRequestSeq) return;
      applyApiPage(payload, { append: !reset, preserveScroll });
      setStatus('');
      settledRenderOptions = { reset, virtualRefresh: !reset, append: !reset, force: Boolean(reset && preserveScroll) };
    } catch (error) {
      if (error && error.name === 'AbortError') return;
      if (requestSeq !== state.apiRequestSeq) return;
      setStatus(error.message || 'Catalog API request failed.', true);
    } finally {
      if (requestSeq === state.apiRequestSeq) {
        state.apiLoading = false;
        if (state.apiAbortController === controller) state.apiAbortController = null;
        if (settledRenderOptions) {
          renderResults(settledRenderOptions);
          if (!(state.apiResultIds || []).length && Number(state.apiTotal || 0) === 0) {
            scheduleApiEmptyResultConfirmation(requestSignature);
          }
        }
      }
    }
  }

  function maybeLoadMoreApiResults() {
    if (!isApiCatalogMode() || state.apiLoading || !state.apiHasMore || !els.thumbs) return;
    const remaining = els.thumbs.scrollHeight - (els.thumbs.scrollTop + els.thumbs.clientHeight);
    const rowHeight = Math.max(40, estimateThumbRowHeight());
    if (remaining < rowHeight * Math.max(4, Number(VIRTUAL_RESULTS.overscanRows) || 6)) {
      loadApiResults({ reset: false });
    }
  }


  function getCollectionsObject() { return state.catalog && state.catalog.collections ? state.catalog.collections : {}; }
  function getSourcesObject() { return state.catalog && state.catalog.sources ? state.catalog.sources : {}; }
  function getPrintsObject() { return state.catalog && state.catalog.prints ? state.catalog.prints : {}; }

  function getPrint(printId) {
    const print = getPrintsObject()[printId];
    return print ? { id: printId, ...print } : null;
  }

  function getSource(sourceId) {
    const source = getSourcesObject()[sourceId];
    return source ? { id: sourceId, ...source } : null;
  }

  function findCatalogKeyById(map, requestedId) {
    const normalized = String(requestedId || '').trim();
    if (!normalized || !map) return '';
    if (Object.prototype.hasOwnProperty.call(map, normalized)) return normalized;

    const normalizedUpper = normalized.toUpperCase();
    return Object.keys(map).find(key => String(key).toUpperCase() === normalizedUpper) || '';
  }

  function sanitizeDeeplinkPrintId(value) {
    const id = String(value || '').trim();
    if (!id || id.length > MAX_PRINT_ID_LENGTH) return '';
    return PRINT_ID_PATTERN.test(id) ? id : '';
  }

  function getPrintIdLimit(limit = getSetSize()) {
    const numericLimit = Number(limit);
    if (Number.isFinite(numericLimit) && numericLimit > 0) {
      return Math.min(Math.ceil(numericLimit), Math.max(...SUPPORTED_SET_SIZES));
    }
    return getSetSize();
  }

  function parseDeeplinkPrintIds(value, limit = getSetSize()) {
    return String(value || '')
      .split(/[|,]/)
      .map(sanitizeDeeplinkPrintId)
      .slice(0, getPrintIdLimit(limit));
  }

  function normalizePrintUrlValue(value, limit = getSetSize()) {
    const ids = parseDeeplinkPrintIds(value, limit);
    return ids.some(Boolean) ? ids.join(',') : '';
  }

  function getUrlPrintParamState(limit = getSetSize()) {
    try {
      const params = new URLSearchParams(window.location.search || '');
      const raw = params.get('psb_prints') || '';
      const hasRaw = params.has('psb_prints');
      const normalized = normalizePrintUrlValue(raw, limit);
      return { raw, hasRaw, normalized, isInvalid: hasRaw && Boolean(String(raw || '').trim()) && !normalized };
    } catch (error) {
      return { raw: '', hasRaw: false, normalized: '', isInvalid: false };
    }
  }

  function getCurrentSourceUrlValue() {
    try {
      const params = new URLSearchParams(window.location.search || '');
      return sanitizeDeeplinkPrintId(params.get('psb_source') || '');
    } catch (error) {
      return '';
    }
  }

  function rememberKnownPrintUrlValue(value, limit = getSetSize()) {
    const normalized = normalizePrintUrlValue(value, limit);
    if (!normalized) return '';
    state.lastKnownPrintUrlValue = normalized;
    state.hasExplicitlyClearedPrints = false;
    return normalized;
  }

  function clearKnownPrintUrlValue() {
    state.lastKnownPrintUrlValue = '';
    state.hasExplicitlyClearedPrints = true;
  }

  function rememberCurrentUrlPrintValue(limit = getSetSize()) {
    const urlState = getUrlPrintParamState(limit);
    if (urlState.normalized) return rememberKnownPrintUrlValue(urlState.normalized, limit);
    return '';
  }

  function applyDeeplinkPrintIds(printIds, sourceTitle = '') {
    const selectedPrints = (printIds || [])
      .slice(0, getSetSize())
      .map(printId => printId ? getPrint(printId) : null);
    while (selectedPrints.length < getSetSize()) selectedPrints.push(null);
    if (!selectedPrints.some(Boolean)) return false;

    state.selectedPrints = selectedPrints;
    state.hasCompletedFirstSet = isSetComplete();
    state.activeSlot = getFirstEmptySlotIndex();
    if (state.activeSlot < 0) state.activeSlot = 0;
    if (sourceTitle && els.previewTitle) els.previewTitle.textContent = sourceTitle;
    dismissInitialPrompt();
    rememberKnownPrintUrlValue(getRuntimeSelectedPrintUrlValue(), getSetSize());
    setStatus('');
    return true;
  }

  function sortPrintIdsLikeSource(printIds, sourceId) {
    const sourcePrefix = String(sourceId || '').toUpperCase();
    return (printIds || []).slice().sort((a, b) => {
      const aText = String(a || '').toUpperCase();
      const bText = String(b || '').toUpperCase();
      const aStarts = sourcePrefix && aText.startsWith(`${sourcePrefix}-`);
      const bStarts = sourcePrefix && bText.startsWith(`${sourcePrefix}-`);
      if (aStarts !== bStarts) return aStarts ? -1 : 1;

      const aTail = Number(aText.split('-').pop());
      const bTail = Number(bText.split('-').pop());
      if (Number.isFinite(aTail) && Number.isFinite(bTail) && aTail !== bTail) return aTail - bTail;
      return aText.localeCompare(bText, undefined, { numeric: true, sensitivity: 'base' });
    });
  }

  function filterPrintIdsByExactSourcePrefix(printIds, sourceId) {
    const sourcePrefix = String(sourceId || '').trim().toUpperCase();
    if (!sourcePrefix) return [];
    const exactPrefix = `${sourcePrefix}-`;
    return (printIds || []).filter(printId => String(printId || '').trim().toUpperCase().startsWith(exactPrefix));
  }

  async function getApiPrintIdFromQuery(query, exactId = '') {
    if (!state.catalogProvider || typeof state.catalogProvider.search !== 'function') return '';
    const payload = await state.catalogProvider.search({
      collection: ALL_HANDLE,
      query,
      cursor: '',
      limit: 20,
      includeMeta: true
    });
    const ids = ingestApiItems(payload.items || []);
    if (!exactId) return ids[0] || '';
    return findCatalogKeyById(getPrintsObject(), exactId) || ids.find(id => String(id).toUpperCase() === String(exactId).toUpperCase()) || '';
  }

  function chooseApiDeeplinkCollectionHandle(printIds = []) {
    const visibleEntries = getVisibleCollectionEntries().filter(entry => entry && entry.handle && entry.handle !== ALL_HANDLE);
    if (!visibleEntries.length) return '';

    const visibleRank = new Map(visibleEntries.map((entry, index) => [entry.handle, index]));
    const counts = new Map();

    (printIds || []).filter(Boolean).forEach(printId => {
      const meta = state.apiPrintMetaById && state.apiPrintMetaById[printId];
      const handles = uniqueIds((meta && Array.isArray(meta.collectionHandles)) ? meta.collectionHandles : []);
      handles.forEach(handle => {
        if (!handle || handle === ALL_HANDLE || !visibleRank.has(handle)) return;
        counts.set(handle, (counts.get(handle) || 0) + 1);
      });
    });

    if (!counts.size) return '';

    return Array.from(counts.entries())
      .sort((a, b) => {
        if (b[1] !== a[1]) return b[1] - a[1];
        return (visibleRank.get(a[0]) || 0) - (visibleRank.get(b[0]) || 0);
      })[0][0] || '';
  }

  async function applyApiDeeplinkCollection(printIds = []) {
    // On a set-size transition the collection/search controls are authoritative.
    // Do not replace them with a collection inferred from the transferred prints.
    if (state.hasExplicitCatalogState) return;

    const handle = chooseApiDeeplinkCollectionHandle(printIds);
    if (!handle || handle === state.selectedCollectionHandle) return;

    state.selectedCollectionHandle = handle;
    state.searchQuery = '';
    if (els.collectionSelect) els.collectionSelect.value = handle;
    if (els.search) els.search.value = '';
    syncSearchClearButton();
    resetApiSurpriseSkipList('');
    await loadApiResults({ reset: true });
  }

  async function applyApiDeeplinkSelection(printsParam, sourceParam) {
    let printIds = [];

    if (printsParam) {
      for (const requestedId of parseDeeplinkPrintIds(printsParam)) {
        if (!requestedId) {
          printIds.push(null);
          continue;
        }
        const cachedId = findCatalogKeyById(getPrintsObject(), requestedId);
        const printId = cachedId || await getApiPrintIdFromQuery(requestedId, requestedId);
        printIds.push(printId || null);
      }
    }

    if (!printIds.length && sourceParam) {
      const payload = await state.catalogProvider.search({
        collection: ALL_HANDLE,
        query: sourceParam,
        cursor: '',
        limit: Math.max(20, getSetSize()),
        includeMeta: true
      });
      const matchedIds = filterPrintIdsByExactSourcePrefix(ingestApiItems(payload.items || []), sourceParam);
      printIds = sortPrintIdsLikeSource(matchedIds, sourceParam).slice(0, getSetSize());
    }

    if (printIds.some(Boolean)) await applyApiDeeplinkCollection(printIds);
    return applyDeeplinkPrintIds(printIds);
  }

  async function applyDeeplinkSelection() {
    const urlPrintState = getUrlPrintParamState(getSetSize());
    const sourceParam = getCurrentSourceUrlValue();
    const cachedPrintsParam = getCachedPrintUrlValue(getSetSize())
      || normalizePrintUrlValue(state.lastKnownPrintUrlValue, getSetSize());
    let printsParam = urlPrintState.normalized;

    // Recover from a missing/invalid psb_prints URL only when no product-source
    // deeplink is present. Source links from regular product pages must keep
    // priority over short-lived transfer cache.
    if (!printsParam && !sourceParam) printsParam = cachedPrintsParam;
    if (!printsParam && urlPrintState.isInvalid && cachedPrintsParam) printsParam = cachedPrintsParam;
    if (!printsParam && !sourceParam) return false;

    if (isApiCatalogMode()) {
      try {
        return await applyApiDeeplinkSelection(printsParam, sourceParam);
      } catch (error) {
        console.warn('Product Set Builder deeplink failed:', error);
        return false;
      }
    }

    const prints = getPrintsObject();
    const sources = getSourcesObject();
    let printIds = [];
    let source = null;

    if (printsParam) {
      printIds = parseDeeplinkPrintIds(printsParam)
        .map(printId => printId ? findCatalogKeyById(prints, printId) : null);
    }

    if (!printIds.length && sourceParam) {
      const sourceId = findCatalogKeyById(sources, sourceParam);
      source = sourceId ? getSource(sourceId) : null;
      if (source && Array.isArray(source.prints)) {
        printIds = source.prints
          .slice(0, getSetSize())
          .map(printId => findCatalogKeyById(prints, printId))
          .filter(Boolean);
      }
    }

    return applyDeeplinkPrintIds(printIds, source && source.title ? source.title : '');
  }

  function getSourceForPrint(printId) {
    if (!printId) return null;
    const sourceId = state.printToSource[printId];
    return sourceId ? getSource(sourceId) : null;
  }

  function updateHiddenProperties() {
    const form = getProductForm();
    if (!form) return;

    removeBuilderPropertyInputs(form);

    const setSizeInput = ensurePropertyInput(form, 'Set Size');
    setSizeInput.value = getSetSizeCopy();

    state.selectedPrints.forEach((print, index) => {
      if (!print || !print.id || !print.src) return;

      const propertyName = getPrintPropertyName(index, print.id);
      const input = ensurePropertyInput(form, propertyName);
      input.value = getOptimizedImageSrc(print.src, PROPERTY_IMAGE_WIDTH, true);
    });

    syncBuilderUrl();
  }

  function getRuntimeSelectedPrintUrlValue(limit = getSetSize()) {
    const ids = state.selectedPrints
      .slice(0, getPrintIdLimit(limit))
      .map(print => print && print.id ? String(print.id) : '');

    return ids.some(Boolean) ? ids.join(',') : '';
  }

  function getCachedPrintUrlValue(limit = getSetSize()) {
    const cachedIds = state.cachedTransferState && Array.isArray(state.cachedTransferState.selectedIds)
      ? state.cachedTransferState.selectedIds
      : [];
    if (!cachedIds.length) return '';
    return normalizePrintUrlValue(cachedIds.slice(0, getPrintIdLimit(limit)).join(','), limit);
  }

  function getPersistentPrintUrlValue(limit = getSetSize()) {
    if (state.hasExplicitlyClearedPrints) return '';

    const runtimeValue = getRuntimeSelectedPrintUrlValue(limit);
    if (runtimeValue) return rememberKnownPrintUrlValue(runtimeValue, limit);

    const urlState = getUrlPrintParamState(limit);
    if (urlState.normalized) return rememberKnownPrintUrlValue(urlState.normalized, limit);

    const rememberedValue = normalizePrintUrlValue(state.lastKnownPrintUrlValue, limit);
    if (rememberedValue) return rememberedValue;

    const cachedValue = getCachedPrintUrlValue(limit);
    if (cachedValue) return rememberKnownPrintUrlValue(cachedValue, limit);

    return '';
  }

  function getPersistentPrintIdsForCount(limit = getSetSize()) {
    const value = getPersistentPrintUrlValue(limit);
    const ids = value ? parseDeeplinkPrintIds(value, limit) : [];
    while (ids.length < getPrintIdLimit(limit)) ids.push('');
    return ids;
  }

  function getSelectedPrintUrlValue() {
    return getPersistentPrintUrlValue(getSetSize());
  }


  function buildShareUrl() {
    rememberGridPosition();

    const url = new URL(window.location.href);
    deleteAttributionParams(url.searchParams);
    const variantId = getVariantIdFromForm() || state.selectedVariantId || '';
    const printValue = getSelectedPrintUrlValue();
    const selectedCollection = String(
      (els.collectionSelect && els.collectionSelect.value) || state.selectedCollectionHandle || ALL_HANDLE
    ).trim() || ALL_HANDLE;
    const searchQuery = els.search ? String(els.search.value || '') : String(state.searchQuery || '');
    const options = getTransferOptionState();

    if (variantId) url.searchParams.set('variant', variantId);
    url.searchParams.set('psb_count', String(getSetSize()));

    if (printValue) {
      url.searchParams.set('psb_prints', printValue);
      url.searchParams.delete('psb_source');
    } else {
      url.searchParams.delete('psb_prints');
    }

    if (Object.keys(options).length) url.searchParams.set('psb_options', JSON.stringify(options));
    else url.searchParams.delete('psb_options');

    url.searchParams.set('psb_collection', selectedCollection);
    url.searchParams.set('psb_search', searchQuery);

    if (state.gridScrollTop > 0) url.searchParams.set('psb_scroll', String(Math.round(state.gridScrollTop)));
    else url.searchParams.delete('psb_scroll');

    if (state.activeSlot !== null && state.activeSlot !== undefined) {
      url.searchParams.set('psb_slot', String(state.activeSlot));
    } else {
      url.searchParams.delete('psb_slot');
    }

    if (state.sheetMode === 'expanded') url.searchParams.set('psb_sheet', '1');
    else url.searchParams.delete('psb_sheet');

    url.searchParams.delete('psb_transition');
    url.searchParams.delete('psb_loaded');
    url.searchParams.delete('psb_hint');
    return url.toString();
  }

  function shouldUseNativeShare() {
    const ua = String(navigator.userAgent || '');
    const isMobileUA = /Android|iPhone|iPad|iPod|Mobile|Tablet|Silk|Kindle/i.test(ua);
    const isCoarsePointer = Boolean(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
    // Capability/device signal only: resizing a mouse-only desktop window must not
    // switch Share / Get link into the native share-sheet path.
    return Boolean(isMobileUA || isCoarsePointer || isTouchInteractionDevice());
  }

  async function copyShareUrl(shareUrl) {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      try {
        await navigator.clipboard.writeText(shareUrl);
        showToast('Link copied.');
        return true;
      } catch (error) {}
    }

    const textarea = document.createElement('textarea');
    textarea.value = shareUrl;
    textarea.setAttribute('readonly', '');
    textarea.setAttribute('aria-hidden', 'true');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);

    let copied = false;
    try {
      copied = Boolean(document.execCommand && document.execCommand('copy'));
    } catch (error) {}
    textarea.remove();

    if (copied) {
      showToast('Link copied.');
      return true;
    }

    showToast('Could not copy link.');
    return false;
  }

  async function shareBuilderLink() {
    const shareUrl = buildShareUrl();

    // Mouse/keyboard desktop copies directly. Real touch phones/tablets use the native
    // share sheet in either orientation when available. The payload contains URL only.
    if (!shouldUseNativeShare()) {
      await copyShareUrl(shareUrl);
      return;
    }

    const shareData = { url: shareUrl };
    try {
      if (navigator.share && (!navigator.canShare || navigator.canShare(shareData))) {
        await navigator.share(shareData);
        return;
      }
    } catch (error) {
      if (error && error.name === 'AbortError') return;
    }

    await copyShareUrl(shareUrl);
  }

  function syncBuilderUrl() {
    if (!state.urlSyncReady || !window.history || typeof window.history.replaceState !== 'function') return;

    try {
      const url = new URL(window.location.href);
      const variantId = getVariantIdFromForm() || state.selectedVariantId || '';
      const runtimePrintValue = getRuntimeSelectedPrintUrlValue(getSetSize());
      const urlPrintState = getUrlPrintParamState(getSetSize());
      const printValue = runtimePrintValue
        ? rememberKnownPrintUrlValue(runtimePrintValue, getSetSize())
        : getPersistentPrintUrlValue(getSetSize());

      if (variantId) url.searchParams.set('variant', variantId);

      if (isMultiProductMode() || getSetSize() !== DEFAULT_SET_SIZE) {
        url.searchParams.set('psb_count', String(getSetSize()));
      } else {
        url.searchParams.delete('psb_count');
      }

      if (printValue) {
        url.searchParams.set('psb_prints', printValue);
        url.searchParams.delete('psb_source');
      } else if (state.hasExplicitlyClearedPrints || urlPrintState.isInvalid) {
        url.searchParams.delete('psb_prints');
        if (state.hasExplicitlyClearedPrints) url.searchParams.delete('psb_source');
      }

      url.searchParams.delete('psb_loaded');

      if (isMultiProductMode()) {
        const options = state.transferredOptions && Date.now() <= state.transferredVariantLockUntil
          ? state.transferredOptions
          : getTransferOptionState();
        if (Object.keys(options).length) url.searchParams.set('psb_options', JSON.stringify(options));
        else url.searchParams.delete('psb_options');
      }

      const nextUrl = `${url.pathname}${url.search}${url.hash}`;
      if (nextUrl === state.lastSyncedBuilderUrl || nextUrl === `${window.location.pathname}${window.location.search}${window.location.hash}`) {
        state.lastSyncedBuilderUrl = nextUrl;
        if (isMultiProductMode() && state.cachedTransferState) writeMultiProductSessionState(getSetSize());
        return;
      }

      window.history.replaceState(window.history.state, document.title, nextUrl);
      state.lastSyncedBuilderUrl = nextUrl;
      if (isMultiProductMode() && state.cachedTransferState) writeMultiProductSessionState(getSetSize());
    } catch (error) {
      // URL sync is only a convenience for reload/back-to-builder flows.
    }
  }

  function getHistorySnapshot() {
    return {
      setSize: getSetSize(),
      printIds: state.selectedPrints.map(print => print && print.id ? print.id : null),
      activeSlot: state.activeSlot,
      hasCompletedFirstSet: state.hasCompletedFirstSet,
      selectedCollectionHandle: state.selectedCollectionHandle,
      searchQuery: state.searchQuery,
      shuffleIndexByCollection: { ...state.shuffleIndexByCollection },
      lastSurpriseSourceId: state.lastSurpriseSourceId,
      lastSurpriseResultKey: state.lastSurpriseResultKey,
      lastSurpriseContextKey: state.lastSurpriseContextKey,
      apiSurpriseSkipSourceIds: Array.isArray(state.apiSurpriseSkipSourceIds) ? state.apiSurpriseSkipSourceIds.slice() : [],
      gridScrollTop: state.gridScrollTop || 0
    };
  }

  function snapshotKey(snapshot) {
    return JSON.stringify(snapshot);
  }

  function updateHistoryButtons() {
    if (els.undo) els.undo.disabled = state.historyPast.length === 0;
    if (els.redo) els.redo.disabled = state.historyFuture.length === 0;
  }

  function getActiveSlotIndex() {
    if (state.activeSlot === null || state.activeSlot === undefined) return null;
    const index = Number(state.activeSlot);
    return Number.isFinite(index) && index >= 0 && index < state.selectedPrints.length ? index : null;
  }

  function updateStageActionButtons() {
    const activeIndex = getActiveSlotIndex();
    const hasSelection = state.selectedPrints.some(Boolean);
    const hasSelectedPrint = activeIndex !== null && Boolean(state.selectedPrints[activeIndex]);

    if (els.clear) {
      els.clear.textContent = activeIndex !== null ? 'Clear Selected' : 'Clear All';
      els.clear.disabled = state.isSurprising || (activeIndex !== null ? !hasSelectedPrint : !hasSelection);
    }

    if (els.shuffle) {
      els.shuffle.disabled = state.isSurprising || !hasSelection;
      els.shuffle.setAttribute('aria-disabled', els.shuffle.disabled ? 'true' : 'false');
    }
  }

  function formatBuilderMoney(cents) {
    const numeric = Number(cents);
    if (!Number.isFinite(numeric)) return '';
    const currency = root.dataset.psbCurrency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    const locale = document.documentElement.lang || 'en-US';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numeric / 100);
    } catch (error) {
      return `$${(numeric / 100).toFixed(2)}`;
    }
  }

  function getVariantDiscountPercent(variant) {
    if (!variant) return 0;
    const price = Number(variant.price) || 0;
    const compareAt = Number(variant.compare_at_price) || 0;
    if (!(compareAt > price) || compareAt <= 0) return 0;
    return Math.max(0, Math.round(((compareAt - price) * 100) / compareAt));
  }

  function formatCompactSizeValue(value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '';
    return Number.isInteger(numeric) ? String(numeric) : String(Number(numeric.toFixed(2)));
  }

  function getCompactStageConfigurationLabel() {
    let styleLabel = '';
    if (root.classList.contains('psb-style-canvas')) styleLabel = 'Canvas';
    else if (root.classList.contains('psb-style-paper')) styleLabel = 'Unframed';
    else if (root.classList.contains('psb-style-framed')) styleLabel = 'Framed';

    const size = state.selectedSizeInches;
    const sizeLabel = size
      ? `${formatCompactSizeValue(size.width)}×${formatCompactSizeValue(size.height)} in`
      : '';

    return [styleLabel, sizeLabel].filter(Boolean).join(' · ');
  }

  function getSelectedPrintSignature() {
    return state.selectedPrints
      .slice(0, getSetSize())
      .map(print => print && print.id ? String(print.id) : '')
      .join('|');
  }

  function markCatalogEngagementPending() {
    if (!IS_COMPACT_UI || state.catalogPurchaseCueArmed) return;
    state.catalogEngagementPending = true;
  }

  function maybeArmCatalogPurchaseCue() {
    if (!IS_COMPACT_UI || state.catalogPurchaseCueArmed || !state.catalogEngagementPending) return;
    if (state.sheetMode !== 'expanded' || !state.currentResultIds.length) return;

    state.catalogPurchaseCueArmed = true;
    state.catalogEngagementPending = false;
    state.catalogPurchaseSlotsChanged = false;
    state.catalogPurchaseLastSignature = getSelectedPrintSignature();
    updateCompactPurchaseControls();
  }

  function noteCatalogSlotMutation() {
    if (!IS_COMPACT_UI || !state.catalogPurchaseCueArmed) return;
    const signature = getSelectedPrintSignature();
    if (state.catalogPurchaseLastSignature && signature !== state.catalogPurchaseLastSignature) {
      state.catalogPurchaseSlotsChanged = true;
    }
    state.catalogPurchaseLastSignature = signature;
  }

  function shouldPromoteCompactCart(canPurchase) {
    return Boolean(
      SHOW_QUICK_CART_BUTTONS
      && canPurchase
      && state.catalogPurchaseCueArmed
      && state.catalogPurchaseSlotsChanged
    );
  }

  function updateCompactPurchaseControls() {
    const variant = getCurrentVariant();
    const canPurchase = Boolean(variant && variant.available !== false && !hasIncompleteSelection());

    if (els.stageSummary) {
      els.stageSummary.hidden = !SHOW_STAGE_PRICE;
      if (SHOW_STAGE_PRICE) {
        if (els.stageSummaryConfig) els.stageSummaryConfig.textContent = getCompactStageConfigurationLabel();
        if (variant && els.stageSummaryValue) els.stageSummaryValue.textContent = formatBuilderMoney(variant.price);
        const discount = getVariantDiscountPercent(variant);
        if (els.stageSummaryDiscount) {
          els.stageSummaryDiscount.hidden = discount <= 0;
          els.stageSummaryDiscount.textContent = discount > 0 ? `-${discount}%` : '';
        }
        if (els.stageSummaryAvailability) {
          const unavailable = Boolean(variant && variant.available === false);
          els.stageSummaryAvailability.hidden = !unavailable;
          els.stageSummaryAvailability.textContent = unavailable ? 'Sold out' : '';
        }
      }
    }

    if (els.footerAddCart) {
      els.footerAddCart.hidden = !SHOW_QUICK_CART_BUTTONS;
      if (SHOW_QUICK_CART_BUTTONS) {
        els.footerAddCart.disabled = !canPurchase;
        els.footerAddCart.setAttribute('aria-disabled', canPurchase ? 'false' : 'true');
      }
    }

    if (IS_COMPACT_UI) {
      const promoteCart = shouldPromoteCompactCart(canPurchase);
      if (els.catalogOpen) els.catalogOpen.classList.toggle('psb-primary-action', !promoteCart);
      if (els.footerAddCart) els.footerAddCart.classList.toggle('psb-primary-action', promoteCart);
    }
  }

  function dismissInitialPrompt() {
    root.classList.remove('psb-has-initial-prompt');
  }

  function pushHistory() {
    if (state.isRestoringHistory || !state.catalog) return;

    const snapshot = getHistorySnapshot();
    const last = state.historyPast[state.historyPast.length - 1];
    if (last && snapshotKey(last) === snapshotKey(snapshot)) return;

    state.historyPast.push(snapshot);
    if (state.historyPast.length > state.maxHistory) state.historyPast.shift();
    state.historyFuture = [];
    updateHistoryButtons();
  }

  function applyHistorySnapshot(snapshot) {
    if (!snapshot) return;
    if (state.isSurprising) {
      clearSurpriseTimers();
      setSurpriseLoading(false);
      els.slotButtons.forEach(button => button.classList.remove('is-surprise-spinning'));
    }

    state.isRestoringHistory = true;
    state.setSize = normalizeSetSize(snapshot.setSize || (snapshot.printIds || []).length || DEFAULT_SET_SIZE);
    state.selectedPrints = (snapshot.printIds || [])
      .slice(0, getSetSize())
      .map(printId => printId ? getPrint(printId) : null);
    while (state.selectedPrints.length < getSetSize()) state.selectedPrints.push(null);
    root.dataset.psbSetSize = String(getSetSize());
    applyStageAspectForSetSize();
    updateSetSizeSelector();
    state.activeSlot = snapshot.activeSlot === null || snapshot.activeSlot === undefined ? null : Number(snapshot.activeSlot);
    state.hasCompletedFirstSet = Boolean(snapshot.hasCompletedFirstSet);
    if (state.activeSlot === null && state.selectedPrints.some(Boolean)) ensureActiveSlot();
    state.selectedCollectionHandle = snapshot.selectedCollectionHandle || ALL_HANDLE;
    state.searchQuery = snapshot.searchQuery || '';
    state.shuffleIndexByCollection = { ...(snapshot.shuffleIndexByCollection || {}) };
    state.lastSurpriseSourceId = snapshot.lastSurpriseSourceId || null;
    state.lastSurpriseResultKey = snapshot.lastSurpriseResultKey || null;
    state.lastSurpriseContextKey = snapshot.lastSurpriseContextKey || '';
    state.apiSurpriseSkipSourceIds = Array.isArray(snapshot.apiSurpriseSkipSourceIds) ? snapshot.apiSurpriseSkipSourceIds.slice() : [];
    state.gridScrollTop = snapshot.gridScrollTop || 0;
    state.shouldRestoreGridScroll = true;

    if (els.collectionSelect) els.collectionSelect.value = state.selectedCollectionHandle;
    if (els.search) els.search.value = state.searchQuery;
    syncSearchClearButton();
    renderSlots();
    refreshStageMetricsAfterLayoutChange();

    if (isApiCatalogMode() && state.apiQuerySignature !== getApiQuerySignature()) {
      resetResultsAndRender({ preserveScroll: true });
    } else {
      renderResults({ preserveWindow: true, force: true });
      restoreGridPosition();
      updateRenderedThumbSelectionState();
      retryVisibleThumbImages(false);
    }

    updateHiddenProperties();
    if (!isMultiProductMode()) syncVariantToSetSize(getSetSize());
    setStatus('');
    state.isRestoringHistory = false;
    updateHistoryButtons();
  }

  function undoHistory() {
    if (!state.historyPast.length) return;
    const current = getHistorySnapshot();
    const previous = state.historyPast.pop();
    state.historyFuture.push(current);
    applyHistorySnapshot(previous);
  }

  function redoHistory() {
    if (!state.historyFuture.length) return;
    const current = getHistorySnapshot();
    const next = state.historyFuture.pop();
    state.historyPast.push(current);
    applyHistorySnapshot(next);
  }

  function getVariantIdFromForm() {
    const form = getProductForm();
    const input = form ? form.querySelector('[name="id"]') : null;
    return input ? String(input.value) : null;
  }

  function getCurrentVariant() {
    const variantId = getVariantIdFromForm();
    if (variantId) {
      const byId = state.product.variants.find(variant => String(variant.id) === variantId);
      if (byId) return byId;
    }
    return state.product.variants.find(variant => variant.available) || state.product.variants[0];
  }

  function getProductOptionNames() {
    const product = state.product || {};
    const optionCandidates = Array.isArray(product.options_with_values) && product.options_with_values.length
      ? product.options_with_values
      : (Array.isArray(product.options) ? product.options : []);

    const names = optionCandidates.map((option, index) => {
      if (typeof option === 'string') return option;
      if (option && typeof option.name === 'string') return option.name;
      return `Option ${index + 1}`;
    }).filter(Boolean);

    if (names.length) return names;

    const firstVariant = Array.isArray(product.variants) ? product.variants[0] : null;
    if (firstVariant && Array.isArray(firstVariant.options)) {
      return firstVariant.options.map((_, index) => `Option ${index + 1}`);
    }

    return [];
  }

  function getVariantOptionValue(variant, optionIndex) {
    if (!variant) return '';
    if (Array.isArray(variant.options) && variant.options[optionIndex] !== undefined) {
      return String(variant.options[optionIndex] || '');
    }
    const key = `option${optionIndex + 1}`;
    return variant[key] !== undefined ? String(variant[key] || '') : '';
  }

  function getOptionValuesFromVariants(optionIndex) {
    const seen = new Set();
    const values = [];
    (state.product.variants || []).forEach(variant => {
      const value = getVariantOptionValue(variant, optionIndex);
      if (!value || seen.has(value)) return;
      seen.add(value);
      values.push(value);
    });
    return values;
  }

  function getCurrentVariantOptions() {
    const names = getProductOptionNames();
    const variant = getCurrentVariant();
    return names.map((_, index) => getVariantOptionValue(variant, index));
  }

  function doesVariantMatchOptions(variant, options) {
    if (!variant || !Array.isArray(options)) return false;
    return options.every((value, index) => !value || getVariantOptionValue(variant, index) === value);
  }

  function findVariantForOptions(options, preferredOptionIndex = null) {
    const availableVariants = (state.product.variants || []).filter(variant => variant.available !== false);
    if (!availableVariants.length) return null;

    const preferredIndex = Number(preferredOptionIndex);
    const preferredValue = Number.isFinite(preferredIndex) ? options[preferredIndex] : '';

    const exactAvailable = availableVariants.find(variant => doesVariantMatchOptions(variant, options));
    if (exactAvailable) return exactAvailable;

    const preferredCandidates = preferredValue
      ? availableVariants.filter(variant => getVariantOptionValue(variant, preferredIndex) === preferredValue)
      : [];

    if (preferredCandidates.length) {
      const ranked = preferredCandidates.slice().sort((a, b) => {
        const scoreVariant = variant => options.reduce((score, value, index) => {
          if (!value || index === preferredIndex) return score;
          return score + (getVariantOptionValue(variant, index) === value ? 1 : 0);
        }, 0);

        return scoreVariant(b) - scoreVariant(a);
      });
      if (ranked[0]) return ranked[0];
    }

    return availableVariants[0] || null;
  }

  function getOptionValueStates(optionIndex, selectedOptions = []) {
    const values = getOptionValuesFromVariants(optionIndex);
    return values.map(value => {
      const candidateOptions = selectedOptions.slice();
      candidateOptions[optionIndex] = value;
      const available = (state.product.variants || []).some(variant => (
        variant.available !== false && doesVariantMatchOptions(variant, candidateOptions)
      ));
      return { value, available };
    });
  }

  function getSetSizeOptionIndex() {
    const optionNames = getProductOptionNames();
    return optionNames.findIndex(optionName => {
      const name = normalize(optionName).replace(/[^a-z0-9]+/g, ' ').trim();
      return name === 'set size'
        || name === 'number of prints'
        || name === 'print count'
        || name === 'prints in set'
        || name === 'number of pieces'
        || name === 'pieces'
        || (name.includes('print') && (name.includes('set') || name.includes('count') || name.includes('number') || name.includes('quantity')))
        || (name.includes('piece') && (name.includes('set') || name.includes('count') || name.includes('number') || name.includes('quantity')));
    });
  }

  function parseSetSizeOptionValue(value) {
    const match = String(value || '').match(/\d{1,2}/);
    return match ? normalizeSetSize(Number(match[0]), 0) : 0;
  }

  function getVariantSetSize(variant) {
    const optionIndex = getSetSizeOptionIndex();
    if (optionIndex < 0 || !variant) return 0;
    return parseSetSizeOptionValue(getVariantOptionValue(variant, optionIndex));
  }

  function findVariantForSetSize(count) {
    const optionIndex = getSetSizeOptionIndex();
    if (optionIndex < 0) return null;
    const targetCount = normalizeSetSize(count, 0);
    if (!targetCount) return null;

    const currentOptions = getCurrentVariantOptions();
    const values = getOptionValuesFromVariants(optionIndex);
    const targetValue = values.find(value => parseSetSizeOptionValue(value) === targetCount);
    if (!targetValue) return null;

    currentOptions[optionIndex] = targetValue;
    const exactAvailable = (state.product.variants || []).find(variant => variant.available !== false && doesVariantMatchOptions(variant, currentOptions));
    if (exactAvailable) return exactAvailable;

    const sameCountAvailable = (state.product.variants || []).find(variant => (
      variant.available !== false && getVariantSetSize(variant) === targetCount
    ));
    return sameCountAvailable || null;
  }

  function syncVariantToSetSize(count) {
    const optionIndex = getSetSizeOptionIndex();
    if (optionIndex < 0) return false;
    const variant = findVariantForSetSize(count);
    if (!variant || String(variant.id) === String(state.selectedVariantId || getVariantIdFromForm() || '')) return Boolean(variant);
    setNativeOptionControlsForVariant(variant);
    setVariantIdInProductForm(variant);
    state.selectedVariantId = String(variant.id);
    updatePreviewFromVariant();
    window.setTimeout(updatePreviewFromVariant, 120);
    return true;
  }

  function setVariantIdInProductForm(variant) {
    const form = getProductForm();
    if (!form || !variant) return null;
    const input = form.querySelector('[name="id"]');
    if (!input) return null;
    const nextValue = String(variant.id);
    if (String(input.value) !== nextValue) input.value = nextValue;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return input;
  }

  function setNativeOptionControlsForVariant(variant) {
    const optionNames = getProductOptionNames();
    if (!variant || !optionNames.length) return null;

    const form = getProductForm();
    const scope = getProductScope(form);
    let changedControl = null;

    optionNames.forEach((optionName, optionIndex) => {
      const value = getVariantOptionValue(variant, optionIndex);
      if (!value) return;

      const exactSelects = Array.from(scope.querySelectorAll('select'))
        .filter(select => !root.contains(select) && select.name === `options[${optionName}]`);
      const variantSelects = Array.from(scope.querySelectorAll('variant-selects select, variant-radios select'))
        .filter(select => !root.contains(select));
      const select = exactSelects[0] || variantSelects[optionIndex] || null;

      if (select && Array.from(select.options || []).some(option => option.value === value)) {
        if (select.value !== value) {
          select.value = value;
          changedControl = select;
        }
      }

      const radios = Array.from(scope.querySelectorAll('input[type="radio"]'))
        .filter(input => !root.contains(input) && input.name === `options[${optionName}]`);
      const radio = radios.find(input => input.value === value);
      if (radio && !radio.checked) {
        radio.checked = true;
        changedControl = radio;
      }
    });

    if (changedControl) {
      changedControl.dispatchEvent(new Event('change', { bubbles: true }));
    }

    return changedControl;
  }

  function applyVariantFromMobileSelector(variant) {
    if (!variant) return;
    setNativeOptionControlsForVariant(variant);
    setVariantIdInProductForm(variant);
    state.selectedVariantId = String(variant.id);
    updatePreviewFromVariant();
    window.setTimeout(updatePreviewFromVariant, 120);
    window.setTimeout(renderMobileVariantSelectors, 140);
  }

  function renderMobileVariantSelectors() {
    if (!els.mobileVariantBar) return;

    const optionNames = getProductOptionNames();
    const setSizeOptionIndex = getSetSizeOptionIndex();
    const visibleOptions = optionNames
      .map((optionName, optionIndex) => ({ optionName, optionIndex }))
      .filter(entry => entry.optionIndex !== setSizeOptionIndex)
      .slice(0, 2);
    const currentOptions = getCurrentVariantOptions();
    const statesByIndex = new Map(visibleOptions.map(entry => [
      entry.optionIndex,
      getOptionValueStates(entry.optionIndex, currentOptions)
    ]));

    if (!visibleOptions.length) {
      els.mobileVariantBar.hidden = true;
      els.mobileVariantBar.innerHTML = '';
      return;
    }

    els.mobileVariantBar.hidden = false;
    els.mobileVariantBar.innerHTML = visibleOptions.map(({ optionName, optionIndex }) => {
      const valueStates = statesByIndex.get(optionIndex) || [];
      if (!valueStates.length) return '';

      const selectedValue = currentOptions[optionIndex] || '';
      const selectedState = valueStates.find(entry => entry.value === selectedValue) || null;
      const selectedSoldOut = Boolean(selectedValue && selectedState && !selectedState.available);
      const optionsHtml = valueStates.map(({ value, available }) => {
        const selected = value === selectedValue;
        const soldOutSuffix = available ? '' : ' — Sold out';
        return `<option value="${escapeAttr(value)}"${selected ? ' selected' : ''}${available ? '' : ' disabled'}>${escapeHtml(value)}${soldOutSuffix}</option>`;
      }).join('');

      return `<label class="psb-mobile-variant-field"><select class="psb-mobile-variant-select${selectedSoldOut ? ' psb-mobile-variant-select--sold-out' : ''}" data-psb-mobile-option-index="${optionIndex}" aria-label="${escapeAttr(optionName)}">${optionsHtml}</select></label>`;
    }).join('');

    updateMobilePopupMetrics();
  }

  function handleMobileVariantSelectorChange(event) {
    const select = event.target && event.target.closest ? event.target.closest('[data-psb-mobile-option-index]') : null;
    if (!select || !els.mobileVariantBar || !els.mobileVariantBar.contains(select)) return;

    const optionIndex = Number(select.dataset.psbMobileOptionIndex);
    if (!Number.isFinite(optionIndex)) return;

    const nextOptions = getCurrentVariantOptions();
    nextOptions[optionIndex] = select.value;
    const variant = findVariantForOptions(nextOptions, optionIndex);
    applyVariantFromMobileSelector(variant);
  }

  function getImageUrlFromVariant(variant) {
    if (variant && variant.featured_image) return variant.featured_image.src || variant.featured_image.url || null;
    return state.product.featured_image || null;
  }

  function parseSizeInches(value) {
    const text = normalize(value).replaceAll('×', 'x').replaceAll(' by ', 'x');
    const match = text.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/);
    if (!match) return null;

    const first = Number(match[1]);
    const second = Number(match[2]);
    if (!Number.isFinite(first) || !Number.isFinite(second) || first <= 0 || second <= 0) return null;

    return {
      width: Math.min(first, second),
      height: Math.max(first, second)
    };
  }

  function getVariantSizeInches(variant) {
    if (!variant) return null;

    const values = [];
    if (Array.isArray(variant.options)) values.push(...variant.options);
    values.push(variant.public_title, variant.title);

    for (const value of values) {
      const parsed = parseSizeInches(value);
      if (parsed) return parsed;
    }

    return null;
  }

  function sizeKey(size) {
    if (!size) return '';
    return `${Number(size.width)}x${Number(size.height)}`;
  }

  function normalizeSize(size, fallback = DEFAULT_PRINT_SIZE) {
    if (!size || !Number.isFinite(size.width) || !Number.isFinite(size.height) || size.width <= 0 || size.height <= 0) {
      return { ...fallback };
    }
    return {
      width: Math.min(Number(size.width), Number(size.height)),
      height: Math.max(Number(size.width), Number(size.height))
    };
  }

  function getMattedPrintSize(frameSize) {
    const frame = normalizeSize(frameSize, DEFAULT_MATTED_FRAME_SIZE);
    const mapped = MATTED_PRINT_BY_FRAME[sizeKey(frame)];
    if (mapped) return { ...mapped };

    const sideMat = frame.width <= 12 ? 1.5 : 2;
    const verticalMat = frame.height <= 14 ? 1.5 : 2;
    return normalizeSize({
      width: Math.max(1, frame.width - sideMat * 2),
      height: Math.max(1, frame.height - verticalMat * 2)
    }, DEFAULT_MATTED_PRINT_SIZE);
  }

  function updatePreviewStyleFromVariant(variant) {
    const title = normalize([variant.title, variant.public_title].filter(Boolean).join(' '));
    const selectedSize = normalizeSize(getVariantSizeInches(variant), DEFAULT_PRINT_SIZE);
    state.selectedSizeInches = selectedSize;

    root.classList.remove(
      'psb-frame-black',
      'psb-frame-white',
      'psb-frame-wood',
      'psb-style-framed',
      'psb-style-canvas',
      'psb-style-paper',
      'psb-has-mat',
      'psb-no-mat'
    );

    state.previewOuterSize = selectedSize;
    state.previewPrintSize = selectedSize;
    state.hasMat = false;
    state.isFramed = false;

    if (title.includes('black')) root.classList.add('psb-frame-black');
    else if (title.includes('white')) root.classList.add('psb-frame-white');
    else if (title.includes('wood') || title.includes('oak') || title.includes('natural')) root.classList.add('psb-frame-wood');
    else root.classList.add('psb-frame-wood');

    if (title.includes('canvas')) {
      root.classList.add('psb-style-canvas');
      updateStageMetrics();
      return;
    }

    if (title.includes('unframed') || title.includes('print -') || title === 'print') {
      root.classList.add('psb-style-paper');
      updateStageMetrics();
      return;
    }

    root.classList.add('psb-style-framed');
    state.isFramed = true;

    const hasNoMat = title.includes('no mat') || title.includes('without mat');
    const hasMat = !hasNoMat && (title.includes('w/ mat') || title.includes('with mat') || title.includes(' mat'));

    if (hasMat) {
      const frameSize = normalizeSize(selectedSize, DEFAULT_MATTED_FRAME_SIZE);
      state.previewOuterSize = frameSize;
      state.previewPrintSize = getMattedPrintSize(frameSize);
      state.hasMat = true;
      root.classList.add('psb-has-mat');
    } else {
      state.previewOuterSize = selectedSize;
      state.previewPrintSize = selectedSize;
      state.hasMat = false;
      root.classList.add('psb-no-mat');
    }

    updateStageMetrics();
  }

  function updatePreviewFromVariant() {
    const variant = getCurrentVariant();
    if (!variant) return;
    const variantSetSize = getVariantSetSize(variant);
    if (variantSetSize && variantSetSize !== getSetSize()) {
      applySetSize(variantSetSize, { recordHistory: false, syncVariant: false });
    }
    const imageUrl = getImageUrlFromVariant(variant);
    if (imageUrl && els.bg) els.bg.src = imageUrl;
    if (els.variantLabel) els.variantLabel.textContent = variant.public_title || variant.title || '';
    updatePreviewStyleFromVariant(variant);
    state.selectedVariantId = String(variant.id);
    syncBuilderUrl();
    renderMobileVariantSelectors();
    updateSetSizeSelector();
    updateCompactPurchaseControls();
  }

  function uniqueIds(ids) {
    const seen = new Set();
    const out = [];
    ids.forEach(id => {
      if (!id || seen.has(id)) return;
      seen.add(id);
      out.push(id);
    });
    return out;
  }

  function getPrintIdsForCollection(handle) {
    const prints = getPrintsObject();
    const sources = getSourcesObject();
    if (handle === ALL_HANDLE) return Object.keys(prints);
    const collection = getCollectionsObject()[handle];
    if (!collection) return [];
    const result = [];
    (collection.sources || []).forEach(sourceId => {
      const source = sources[sourceId];
      if (source && Array.isArray(source.prints)) result.push(...source.prints);
    });
    if (Array.isArray(collection.prints)) result.push(...collection.prints);
    return uniqueIds(result).filter(printId => Boolean(prints[printId]));
  }

  function getAutofillSourcesForCollection(handle) {
    const sources = getSourcesObject();
    const sourceIds = handle === ALL_HANDLE
      ? Object.keys(sources)
      : ((getCollectionsObject()[handle] && getCollectionsObject()[handle].sources) || []);
    return sourceIds
      .map(sourceId => getSource(sourceId))
      .filter(source => source && Array.isArray(source.prints) && source.prints.length > 0);
  }

  function buildIndexes() {
    state.printToSource = {};
    state.searchIndex = {};
    invalidateFilteredResults();
    Object.entries(getSourcesObject()).forEach(([sourceId, source]) => {
      (source.prints || []).forEach(printId => {
        if (!state.printToSource[printId]) state.printToSource[printId] = sourceId;
      });
    });
    Object.keys(getPrintsObject()).forEach(printId => {
      const print = getPrint(printId);
      const source = getSourceForPrint(printId);
      state.searchIndex[printId] = [
        printId,
        ...(print.tags || []),
        source && source.id,
        source && source.title,
        ...((source && source.tags) || [])
      ].filter(Boolean).join(' ').toLowerCase();
    });
  }

  function getFilteredResultSignature() {
    
    if (isApiCatalogMode()) return `${getApiQuerySignature()}|total:${state.apiTotal || 0}`;
    const printCount = state.catalog && state.catalog.prints ? Object.keys(state.catalog.prints).length : 0;
    return `${state.selectedCollectionHandle || ALL_HANDLE}|${state.searchQuery || ''}|${printCount}`;
  }

  function invalidateFilteredResults() {
    state.filteredResultSignature = '';
    state.filteredResultIds = [];
  }

  function getFilteredResultIds() {
    const signature = getFilteredResultSignature();
    if (state.filteredResultSignature === signature && Array.isArray(state.filteredResultIds)) {
      return state.filteredResultIds;
    }

    if (isApiCatalogMode()) {
      state.filteredResultSignature = signature;
      state.filteredResultIds = Array.isArray(state.apiResultIds) ? state.apiResultIds : [];
      return state.filteredResultIds;
    }

    const baseIds = getPrintIdsForCollection(state.selectedCollectionHandle);
    const tokens = normalizeTokens(state.searchQuery);
    const resultIds = !tokens.length
      ? baseIds
      : baseIds.filter(printId => {
          const haystack = state.searchIndex[printId] || '';
          return tokens.every(token => haystack.includes(token));
        });

    state.filteredResultSignature = signature;
    state.filteredResultIds = resultIds;
    return resultIds;
  }

  function renderCollectionSelect() {
    const collections = getCollectionsObject();
    const visibleEntries = getVisibleCollectionEntries();
    const options = [`<option value="${ALL_HANDLE}">All Prints</option>`];

    visibleEntries.forEach(entry => {
      options.push(`<option value="${escapeAttr(entry.handle)}">${escapeHtml(entry.title || entry.handle)}</option>`);
    });

    els.collectionSelect.innerHTML = options.join('');

    const requested = state.selectedCollectionHandle && state.selectedCollectionHandle !== ALL_HANDLE
      ? state.selectedCollectionHandle
      : '';
    const preferred = requested || root.dataset.defaultCollection;
    const preferredIsVisible = preferred === ALL_HANDLE || visibleEntries.some(entry => entry.handle === preferred);
    const defaultExists = isApiCatalogMode() ? (preferred === ALL_HANDLE || (state.collectionEntries || []).some(entry => entry.handle === preferred)) : Boolean(collections[preferred]);
    const defaultHandle = defaultExists && preferredIsVisible ? preferred : ALL_HANDLE;
    state.selectedCollectionHandle = defaultHandle;
    els.collectionSelect.value = defaultHandle;
    updateHiddenProperties();
  }

  function rememberGridPosition() {
    if (!els.thumbs) return;
    state.gridScrollTop = els.thumbs.scrollTop || 0;
  }

  function restoreGridPosition() {
    if (!els.thumbs) return;
    if (!state.shouldRestoreGridScroll) return;
    state.shouldRestoreGridScroll = false;
    const targetTop = Math.max(0, state.gridScrollTop || 0);
    const align = () => {
      if (!els.thumbs) return;
      const maxTop = Math.max(0, els.thumbs.scrollHeight - els.thumbs.clientHeight);
      els.thumbs.scrollTop = Math.min(maxTop, targetTop);
    };
    window.requestAnimationFrame(() => {
      align();
      window.requestAnimationFrame(align);
      window.setTimeout(align, 80);
    });
  }

  function openPickerAtCurrentPosition(shouldScroll = true) {
    state.shouldRestoreGridScroll = true;
    expandPrintSheet(shouldScroll);

    window.requestAnimationFrame(() => {
      renderResults({ preserveWindow: true });
      restoreGridPosition();
    });
  }

  function getResultSignature(resultIds) {
    return `${state.selectedCollectionHandle || ALL_HANDLE}|${state.searchQuery || ''}|${resultIds.length}|${resultIds[0] || ''}|${resultIds[resultIds.length - 1] || ''}`;
  }

  function getThumbWindow() {
    return els.thumbs ? els.thumbs.querySelector('[data-psb-thumbs-window]') : null;
  }

  function getThumbGridGap() {
    const windowEl = getThumbWindow();
    if (!windowEl) return 8;
    const styles = window.getComputedStyle(windowEl);
    const rowGap = parseFloat(styles.rowGap || styles.gap || '8');
    return Number.isFinite(rowGap) ? rowGap : 8;
  }

  function getThumbColumns() {
    const windowEl = getThumbWindow();
    if (windowEl) {
      const styles = window.getComputedStyle(windowEl);
      const columns = (styles.gridTemplateColumns || '').split(' ').filter(Boolean).length;
      if (columns > 0) {
        state.virtualColumns = columns;
        return columns;
      }
    }

    const fallback = isMobileLayout()
      ? 5
      : Math.max(1, Number(root.style.getPropertyValue('--psb-desktop-thumb-columns')) || 5);
    state.virtualColumns = fallback;
    return fallback;
  }

  function estimateThumbRowHeight() {
    const windowEl = getThumbWindow();
    const firstThumb = windowEl ? windowEl.querySelector('.psb-thumb') : null;
    const gap = getThumbGridGap();

    if (firstThumb) {
      const rect = firstThumb.getBoundingClientRect();
      if (rect.height > 10) {
        state.virtualRowHeight = rect.height + gap;
        return state.virtualRowHeight;
      }
    }

    if (state.virtualRowHeight > 10) return state.virtualRowHeight;

    if (isMobileLayout()) {
      const cssHeight = parseFloat(window.getComputedStyle(root).getPropertyValue('--psb-mobile-thumb-height') || '0');
      if (cssHeight > 10) {
        state.virtualRowHeight = cssHeight + gap;
        return state.virtualRowHeight;
      }
    }

    const columns = getThumbColumns();
    const width = els.thumbs ? Math.max(1, els.thumbs.clientWidth) : 520;
    const columnWidth = Math.max(40, (width - gap * Math.max(0, columns - 1)) / columns);
    state.virtualRowHeight = Math.round(columnWidth * 1.4 + gap);
    return state.virtualRowHeight;
  }

  function getVisibleResultWindow(resultIds, options = {}) {
    const total = resultIds.length;
    if (!total) return { start: 0, end: 0, topPad: 0, bottomPad: 0, columns: 1, rowHeight: 0 };

    const columns = Math.max(1, getThumbColumns());
    const rowHeight = Math.max(24, estimateThumbRowHeight());
    const totalRows = Math.ceil(total / columns);
    const scrollTop = els.thumbs ? Math.max(0, els.thumbs.scrollTop || 0) : 0;
    const viewportHeight = els.thumbs ? Math.max(rowHeight, els.thumbs.clientHeight || rowHeight) : rowHeight * 3;
    const visibleFirstRow = Math.max(0, Math.floor(scrollTop / rowHeight));
    const visibleLastRow = Math.min(totalRows, Math.max(visibleFirstRow + 1, Math.ceil((scrollTop + viewportHeight) / rowHeight)));
    const visibleRows = Math.max(1, visibleLastRow - visibleFirstRow);
    const overscanRows = Math.max(2, Number(VIRTUAL_RESULTS.overscanRows) || 5);
    const maxRendered = Math.max(PAGE_SIZE, Number(VIRTUAL_RESULTS.maxRenderedThumbs) || 180);
    const desiredRows = Math.max(visibleRows + overscanRows * 2, visibleRows + 4);
    const maxRowsByDom = Math.max(visibleRows + 2, Math.floor(maxRendered / columns));
    const maxRows = Math.min(totalRows, Math.max(1, Math.min(desiredRows, maxRowsByDom)));

    if (!options.forceStart && state.renderedWindowEnd > state.renderedWindowStart) {
      const currentStartRow = Math.max(0, Math.floor(state.renderedWindowStart / columns));
      const currentEndRow = Math.min(totalRows, Math.ceil(state.renderedWindowEnd / columns));
      const edgeRows = Math.max(2, Math.floor(overscanRows / 2));
      const hasValidCurrentWindow = currentEndRow > currentStartRow && state.renderedWindowEnd <= total;
      const topIsSafe = currentStartRow === 0
        ? visibleFirstRow >= 0
        : visibleFirstRow >= currentStartRow + edgeRows;
      const bottomIsSafe = currentEndRow >= totalRows
        ? visibleLastRow <= totalRows
        : visibleLastRow <= currentEndRow - edgeRows;

      if (hasValidCurrentWindow && topIsSafe && bottomIsSafe) {
        return {
          start: state.renderedWindowStart,
          end: state.renderedWindowEnd,
          topPad: Math.max(0, currentStartRow * rowHeight),
          bottomPad: Math.max(0, (totalRows - currentEndRow) * rowHeight),
          columns,
          rowHeight
        };
      }
    }

    if (options.forceStart) {
      const startRow = 0;
      const endRow = Math.min(totalRows, maxRows);
      return {
        start: 0,
        end: Math.min(total, endRow * columns),
        topPad: 0,
        bottomPad: Math.max(0, (totalRows - endRow) * rowHeight),
        columns,
        rowHeight
      };
    }

    const extraRows = Math.max(overscanRows, Math.floor((maxRows - visibleRows) / 2));
    let startRow = Math.max(0, visibleFirstRow - extraRows);
    let endRow = Math.min(totalRows, startRow + maxRows);

    if (endRow - startRow < maxRows) {
      startRow = Math.max(0, endRow - maxRows);
    }

    const start = Math.min(total, startRow * columns);
    const end = Math.min(total, endRow * columns);
    return {
      start,
      end,
      topPad: Math.max(0, startRow * rowHeight),
      bottomPad: Math.max(0, (totalRows - endRow) * rowHeight),
      columns,
      rowHeight
    };
  }

  function buildThumbHtml(printId) {
    const print = getPrint(printId);
    if (!print || !print.src) return '';
    const selectedSlotIndexes = getSelectedSlotIndexes(printId);
    const selectedSlots = selectedSlotIndexes.map(index => index + 1);
    const selected = selectedSlots.length ? ' is-selected' : '';
    const selectedColor = selectedSlotIndexes.length ? getSlotColor(selectedSlotIndexes[0]) : '';
    const style = selectedColor ? ` style="--psb-selected-color:${selectedColor}"` : '';
    const badges = selectedSlots.length
      ? `<span class="psb-thumb-badges" aria-label="Selected in slot ${escapeAttr(selectedSlots.join(', '))}">${selectedSlotIndexes.map(index => `<span class="psb-thumb-badge" aria-hidden="true" style="--psb-badge-color:${getSlotColor(index)}">${index + 1}</span>`).join('')}</span>`
      : '';
    const thumbBaseSrc = print.thumb || print.src;
    const thumbSrc = getOptimizedImageSrc(thumbBaseSrc, THUMB_IMAGE_WIDTH);
    const thumbSrcset = getOptimizedImageSrcset(thumbBaseSrc, [140, 220, 320]);
    const srcsetAttr = thumbSrcset ? ` srcset="${escapeAttr(thumbSrcset)}" data-psb-srcset="${escapeAttr(thumbSrcset)}" sizes="(max-width: 749px) 19vw, 110px"` : '';
    return `<button type="button" class="psb-thumb${selected}" data-psb-print-id="${escapeAttr(printId)}" title="${escapeAttr(printId)}"${style}>${badges}<img src="${escapeAttr(thumbSrc)}" data-psb-src="${escapeAttr(thumbSrc)}"${srcsetAttr} alt="${escapeAttr(printId)}" width="300" height="420" loading="lazy" decoding="async" fetchpriority="low" draggable="false"></button>`;
  }

  function createThumbElement(printId) {
    const html = buildThumbHtml(printId);
    if (!html) return null;
    const template = document.createElement('template');
    template.innerHTML = html.trim();
    return template.content.firstElementChild;
  }

  function ensureThumbWindowStructure() {
    if (!els.thumbs) return null;
    let topPad = els.thumbs.querySelector('[data-psb-virtual-pad-top]');
    let windowEl = getThumbWindow();
    let bottomPad = els.thumbs.querySelector('[data-psb-virtual-pad-bottom]');

    if (topPad && windowEl && bottomPad) {
      return { topPad, windowEl, bottomPad };
    }

    els.thumbs.innerHTML = '<div class="psb-virtual-pad psb-virtual-pad-top" data-psb-virtual-pad-top></div><div class="psb-thumbs-window" data-psb-thumbs-window></div><div class="psb-virtual-pad psb-virtual-pad-bottom" data-psb-virtual-pad-bottom></div>';
    topPad = els.thumbs.querySelector('[data-psb-virtual-pad-top]');
    windowEl = getThumbWindow();
    bottomPad = els.thumbs.querySelector('[data-psb-virtual-pad-bottom]');
    return { topPad, windowEl, bottomPad };
  }

  function setVirtualPadHeights(structure, topPad, bottomPad) {
    if (!structure) return;
    structure.topPad.style.height = `${Math.max(0, Math.round(topPad || 0))}px`;
    structure.bottomPad.style.height = `${Math.max(0, Math.round(bottomPad || 0))}px`;
  }

  function patchThumbWindow(visibleIds) {
    const structure = ensureThumbWindowStructure();
    if (!structure) return null;

    const existing = new Map();
    structure.windowEl.querySelectorAll('[data-psb-print-id]').forEach(button => {
      existing.set(button.dataset.psbPrintId, button);
    });

    const fragment = document.createDocumentFragment();
    visibleIds.forEach(printId => {
      let button = existing.get(printId);
      if (!button) button = createThumbElement(printId);
      if (button) fragment.appendChild(button);
    });

    structure.windowEl.replaceChildren(fragment);
    return structure;
  }

  function bindThumbsOnce() {
    if (!els.thumbs || state.thumbsEventsBound) return;
    state.thumbsEventsBound = true;

    els.thumbs.addEventListener('pointerdown', event => {
      const button = event.target.closest('[data-psb-print-id]');
      if (!button || !els.thumbs.contains(button)) return;
      const print = getPrint(button.dataset.psbPrintId);
      if (!print) return;
      prepareDragFromThumb(event, button, print);
    });

    els.thumbs.addEventListener('click', event => {
      const button = event.target.closest('[data-psb-print-id]');
      if (!button || !els.thumbs.contains(button)) return;

      if (state.suppressNextClick) {
        event.preventDefault();
        state.suppressNextClick = false;
        return;
      }

      const printId = button.dataset.psbPrintId;
      const print = getPrint(printId);
      if (!print) return;

      const targetSlot = getTargetSlotForSelection();
      const isSameAsTargetSlot = targetSlot !== null
        && state.selectedPrints[targetSlot]
        && state.selectedPrints[targetSlot].id === printId;

      if (isSameAsTargetSlot) {
        removePrintSelection(printId);
        return;
      }

      selectPrintForActiveSlot(print);
    });

    els.thumbs.addEventListener('scroll', () => {
      rememberGridPosition();
      requestVirtualResultsRender();
    }, { passive: true });
  }

  function retryThumbImage(img, force = false) {
    if (!img) return;
    const src = img.dataset.psbSrc || img.currentSrc || img.src;
    if (!src) return;

    if (!navigator.onLine && !force) return;

    const count = Number(img.dataset.psbRetryCount || 0);
    const maxCount = Number(VIRTUAL_RESULTS.imageRetryCount) || 2;
    if (!force && count >= maxCount) return;

    img.dataset.psbRetryCount = String(force ? 0 : count + 1);
    const srcset = img.dataset.psbSrcset || '';
    img.removeAttribute('srcset');
    img.removeAttribute('src');
    window.requestAnimationFrame(() => {
      if (srcset) img.setAttribute('srcset', srcset);
      img.src = src;
    });
  }

  function retryVisibleThumbImages(force = false) {
    if (!els.thumbs) return;
    const viewport = els.thumbs.getBoundingClientRect();
    els.thumbs.querySelectorAll('img[data-psb-load-error="1"]').forEach(img => {
      const rect = img.getBoundingClientRect();
      if (rect.bottom < viewport.top - 160 || rect.top > viewport.bottom + 160) return;
      retryThumbImage(img, force);
    });
  }

  function bindThumbImageEvents() {
    if (!els.thumbs || state.thumbImageEventsBound) return;
    state.thumbImageEventsBound = true;

    els.thumbs.addEventListener('error', event => {
      const img = event.target;
      if (!img || img.tagName !== 'IMG') return;
      img.dataset.psbLoadError = '1';
      retryThumbImage(img, false);
    }, true);

    els.thumbs.addEventListener('load', event => {
      const img = event.target;
      if (!img || img.tagName !== 'IMG') return;
      delete img.dataset.psbLoadError;
      img.dataset.psbRetryCount = '0';
    }, true);

    window.addEventListener('online', () => retryVisibleThumbImages(true));
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) retryVisibleThumbImages(false);
    });
  }

  function updateRenderedThumbSelectionState() {
    if (!els.thumbs) return;

    els.thumbs.querySelectorAll('[data-psb-print-id]').forEach(button => {
      const printId = button.dataset.psbPrintId;
      const selectedSlotIndexes = getSelectedSlotIndexes(printId);
      const selectedSlots = selectedSlotIndexes.map(index => index + 1);
      const selectedColor = selectedSlotIndexes.length ? getSlotColor(selectedSlotIndexes[0]) : '';

      button.classList.toggle('is-selected', selectedSlotIndexes.length > 0);
      if (selectedColor) {
        button.style.setProperty('--psb-selected-color', selectedColor);
      } else {
        button.style.removeProperty('--psb-selected-color');
      }

      const currentBadges = button.querySelector('.psb-thumb-badges');
      if (!selectedSlotIndexes.length) {
        if (currentBadges) currentBadges.remove();
        return;
      }

      const badgesHtml = `<span class="psb-thumb-badges" aria-label="Selected in slot ${escapeAttr(selectedSlots.join(', '))}">${selectedSlotIndexes.map(index => `<span class="psb-thumb-badge" aria-hidden="true" style="--psb-badge-color:${getSlotColor(index)}">${index + 1}</span>`).join('')}</span>`;
      if (currentBadges) {
        currentBadges.outerHTML = badgesHtml;
      } else {
        button.insertAdjacentHTML('afterbegin', badgesHtml);
      }
    });
  }

  function updateResultsMeta(useVirtual, start, end, total) {
    if (!els.resultsMeta) return;
    const shownStart = total ? (start + 1) : 0;
    const shownEnd = Math.min(end, total);
    const text = total
      ? (useVirtual ? `${shownStart}–${shownEnd} of ${total} prints` : `${shownEnd} of ${total} prints`)
      : '';
    if (state.lastResultsMetaText !== text) {
      state.lastResultsMetaText = text;
      els.resultsMeta.textContent = text;
    }
  }

  function renderResults(options = {}) {
    cancelPendingTouchDrag();
    bindThumbsOnce();
    bindThumbImageEvents();

    const resultIds = getFilteredResultIds();
    const signature = getResultSignature(resultIds);
    const resultsChanged = signature !== state.renderedResultSignature;
    const apiAppendRefresh = isApiCatalogMode() && Boolean(options.append);
    const forceReset = Boolean(options.reset) || (resultsChanged && !apiAppendRefresh);

    state.currentResultIds = resultIds;
    if (resultIds.length) maybeArmCatalogPurchaseCue();

    if (resultIds.length) {
      state.emptySearchResetQuery = '';
      window.clearTimeout(state.apiEmptyConfirmTimer);
      state.apiEmptyConfirmTimer = null;
    }

    if (!els.thumbs) return;

    if (!resultIds.length && isApiCatalogMode() && state.apiLoading) {
      els.thumbs.innerHTML = '<div class="psb-empty">Loading prints...</div>';
      if (els.resultsMeta) els.resultsMeta.textContent = '';
      return;
    }

    if (!resultIds.length) {
      state.renderedResultSignature = signature;
      state.renderedWindowStart = 0;
      state.renderedWindowEnd = 0;
      state.lastResultsMetaText = '';
      els.thumbs.innerHTML = '<div class="psb-empty psb-empty--hint">No prints found. Try changing your search or choose another category.</div>';
      if (state.sheetMode === 'expanded' && String(state.searchQuery || '').trim()) {
        state.emptySearchResetQuery = String(state.searchQuery);
      }
      if (els.resultsMeta) els.resultsMeta.textContent = '';
      requestDesktopLayoutUpdate();
      return;
    }

    const totalForMeta = isApiCatalogMode() && state.apiTotal ? Math.max(state.apiTotal, resultIds.length) : resultIds.length;
    const useVirtual = resultIds.length > Math.max(PAGE_SIZE, Number(VIRTUAL_RESULTS.enabledAfter) || 180);
    const windowInfo = useVirtual
      ? getVisibleResultWindow(resultIds, { forceStart: forceReset && !state.shouldRestoreGridScroll })
      : { start: 0, end: resultIds.length, topPad: 0, bottomPad: 0 };

    const nextStart = useVirtual ? windowInfo.start : 0;
    const nextEnd = useVirtual ? windowInfo.end : resultIds.length;
    const windowEl = getThumbWindow();
    const hasRenderedWindow = Boolean(windowEl && windowEl.children.length);

    const sameWindow = hasRenderedWindow
      && !forceReset
      && state.renderedWindowStart === nextStart
      && state.renderedWindowEnd === nextEnd
      && state.renderedResultSignature === signature;

    if (sameWindow && !options.force) {
      updateResultsMeta(useVirtual, nextStart, nextEnd, totalForMeta);
      updateRenderedThumbSelectionState();
      retryVisibleThumbImages(false);
      restoreGridPosition();
      return;
    }

    const previousScrollTop = els.thumbs.scrollTop || 0;
    const visibleIds = resultIds.slice(nextStart, nextEnd);
    const structure = patchThumbWindow(visibleIds);
    if (structure) {
      setVirtualPadHeights(structure, useVirtual ? windowInfo.topPad : 0, useVirtual ? windowInfo.bottomPad : 0);
    }

    state.renderedResultSignature = signature;
    state.renderedWindowStart = nextStart;
    state.renderedWindowEnd = nextEnd;
    state.virtualResultCount = resultIds.length;

    if (forceReset && !state.shouldRestoreGridScroll) {
      els.thumbs.scrollTop = 0;
    } else if (options.virtualRefresh) {
      const maxTop = Math.max(0, els.thumbs.scrollHeight - els.thumbs.clientHeight);
      els.thumbs.scrollTop = Math.min(maxTop, previousScrollTop);
    }

    updateResultsMeta(useVirtual, nextStart, nextEnd, totalForMeta);

    window.requestAnimationFrame(() => {
      getThumbColumns();
      estimateThumbRowHeight();
      retryVisibleThumbImages(false);
    });

    restoreGridPosition();
    if (!options.virtualRefresh) {
      requestDesktopLayoutUpdate();
    }
  }

  function requestVirtualResultsRender() {
    if (!els.thumbs || state.sheetMode !== 'expanded') return;
    if (state.drag && state.drag.isActive) return;
    if (!state.currentResultIds || !state.currentResultIds.length) return;

    if (isApiCatalogMode()) maybeLoadMoreApiResults();

    if (state.currentResultIds.length <= Math.max(PAGE_SIZE, Number(VIRTUAL_RESULTS.enabledAfter) || 180)) return;

    if (state.virtualScrollTimer) return;
    state.virtualScrollTimer = window.setTimeout(() => {
      state.virtualScrollTimer = null;
      if (state.virtualRenderRaf) return;
      state.virtualRenderRaf = window.requestAnimationFrame(() => {
        state.virtualRenderRaf = null;
        renderResults({ virtualRefresh: true });
      });
    }, Number(VIRTUAL_RESULTS.scrollRenderThrottleMs) || 140);
  }

  function resetResultsAndRender(options = {}) {
    const preserveScroll = Boolean(options.preserveScroll);
    invalidateFilteredResults();
    if (!preserveScroll) state.gridScrollTop = 0;
    state.renderedResultSignature = '';
    state.renderedWindowStart = 0;
    state.renderedWindowEnd = 0;
    state.lastResultsMetaText = '';
    if (els.thumbs && !preserveScroll) els.thumbs.scrollTop = 0;

    if (isApiCatalogMode()) {
      state.apiCursor = null;
      state.apiTotal = 0;
      state.apiHasMore = false;
      state.apiResultIds = [];
      const loadingPromise = loadApiResults({ reset: true, preserveScroll });
      renderResults({ reset: true, force: true });
      return loadingPromise;
    }

    renderResults({ reset: true });
  }

  function getTargetSlotForSelection() {
    if (state.activeSlot !== null && Number.isFinite(Number(state.activeSlot))) {
      return Number(state.activeSlot);
    }

    const firstEmpty = getFirstEmptySlotIndex();
    if (!state.hasCompletedFirstSet && firstEmpty >= 0) return firstEmpty;
    if (firstEmpty >= 0) return firstEmpty;

    return ensureActiveSlot();
  }

  function removePrintFromSlot(slotIndex) {
    const safeIndex = Number(slotIndex);
    if (!Number.isFinite(safeIndex) || safeIndex < 0 || safeIndex >= state.selectedPrints.length) return;
    if (!state.selectedPrints[safeIndex]) return;

    pushHistory();
    state.selectedPrints[safeIndex] = null;
    if (!isSetComplete()) state.hasCompletedFirstSet = false;
    state.activeSlot = areAllSlotsEmpty() ? null : safeIndex;
    renderSlots();
    updateRenderedThumbSelectionState();
    retryVisibleThumbImages(false);
    if (areAllSlotsEmpty()) clearKnownPrintUrlValue();
    updateHiddenProperties();
    setStatus('');
  }

  function removePrintSelection(printId) {
    const selectedIndexes = getSelectedSlotIndexes(printId);
    if (!selectedIndexes.length) return;

    const indexToRemove = Math.max(...selectedIndexes);
    removePrintFromSlot(indexToRemove);
  }

  function selectPrintForActiveSlot(print) {
    if (!print) return;

    const targetSlot = getTargetSlotForSelection();
    if (targetSlot === null) return;

    if (state.selectedPrints[targetSlot] && state.selectedPrints[targetSlot].id === print.id) {
      return;
    }

    pushHistory();
    state.selectedPrints[targetSlot] = print;
    updateGuidedActiveSlotAfterInsert(targetSlot);
    renderSlots();
    updateRenderedThumbSelectionState();
    retryVisibleThumbImages(false);
    updateHiddenProperties();
    setStatus('');
  }

  function renderSlots() {
    const nextEmpty = !state.hasCompletedFirstSet ? getFirstEmptySlotIndex() : -1;

    els.slotButtons.forEach((slotButton, index) => {
      if (!slotButton) return;
      const isVisible = index < getSetSize();
      slotButton.hidden = !isVisible;
      slotButton.style.setProperty('--psb-current-slot-color', getSlotColor(index));
      if (!isVisible) {
        slotButton.classList.remove('has-print', 'is-active', 'is-next', 'is-drop-target', 'is-drag-source');
        const hiddenImg = slotButton.querySelector('[data-psb-slot-img]');
        if (hiddenImg) hiddenImg.removeAttribute('src');
      }
    });

    state.selectedPrints.forEach((print, index) => {
      const slotButton = root.querySelector(`[data-psb-slot="${index}"]`);
      const slotTab = root.querySelector(`[data-psb-slot-tab="${index}"]`);
      const slotImg = slotButton ? slotButton.querySelector('[data-psb-slot-img]') : null;
      const slotCaption = slotButton ? slotButton.querySelector('[data-psb-slot-caption]') : null;

      const isActive = state.activeSlot !== null && index === Number(state.activeSlot);

      if (slotButton) {
        slotButton.classList.toggle('has-print', Boolean(print));
        slotButton.classList.toggle('is-active', isActive);
        slotButton.classList.toggle('is-next', !print && index === nextEmpty);
        slotButton.setAttribute('aria-label', print ? `Replace Print ${index + 1}` : `Add Print ${index + 1}`);
      }

      if (slotTab) slotTab.classList.toggle('is-active', isActive);

      if (slotCaption) {
        slotCaption.textContent = `Print ${index + 1}`;
      }

      if (slotImg) {
        if (print && print.src) {
          slotImg.src = getOptimizedImageSrc(print.src, SLOT_IMAGE_WIDTH);
          slotImg.alt = print.id;
          slotImg.style.display = 'block';
        } else {
          slotImg.removeAttribute('src');
          slotImg.alt = '';
          slotImg.style.display = '';
        }
      }
    });

    updateStageActionButtons();
    noteCatalogSlotMutation();
    updateCompactPurchaseControls();
    updateStageZoomAvailability();
  }

  function setActiveSlot(index, options = {}) {
    const safeIndex = Number(index);
    if (!Number.isFinite(safeIndex) || safeIndex < 0 || safeIndex > state.selectedPrints.length - 1) return;
    state.activeSlot = safeIndex;
    renderSlots();

    if (options.expand !== false) {
      openPickerAtCurrentPosition(true);
    } else {
      updateRenderedThumbSelectionState();
      retryVisibleThumbImages(false);
      requestDesktopLayoutUpdate();
    }
  }

  function getOrderedAutofillSourcesForCollection(handle) {
    return getAutofillSourcesForCollection(handle);
  }

  function getNextAutofillSourceForCollection(advance = true, skipSourceIds = []) {
    const sources = getOrderedAutofillSourcesForCollection(state.selectedCollectionHandle);
    if (!sources.length) return null;

    const skipSet = new Set((skipSourceIds || []).filter(Boolean));
    const currentIndex = state.shuffleIndexByCollection[state.selectedCollectionHandle] || 0;
    let selectedSource = null;
    let selectedOffset = 0;

    for (let offset = 0; offset < sources.length; offset += 1) {
      const candidate = sources[(currentIndex + offset) % sources.length];
      if (!candidate || skipSet.has(candidate.id)) continue;
      selectedSource = candidate;
      selectedOffset = offset;
      break;
    }

    if (!selectedSource) return null;

    if (advance) {
      state.shuffleIndexByCollection[state.selectedCollectionHandle] = currentIndex + selectedOffset + 1;
    }

    return selectedSource;
  }

  function getSourceRankInCollection(sourceId, handle = state.selectedCollectionHandle) {
    if (!sourceId) return Number.MAX_SAFE_INTEGER;
    if (handle === ALL_HANDLE) return Number.MAX_SAFE_INTEGER - 1;

    const collection = getCollectionsObject()[handle];
    const sourceIds = collection && Array.isArray(collection.sources) ? collection.sources : [];
    const rank = sourceIds.indexOf(sourceId);
    return rank >= 0 ? rank : Number.MAX_SAFE_INTEGER;
  }

  function getAllAutofillSources() {
    return Object.keys(getSourcesObject())
      .map(sourceId => getSource(sourceId))
      .filter(source => source && Array.isArray(source.prints) && source.prints.length > 0);
  }

  function getSelectedPrintIdsForSurprise() {
    return state.selectedPrints
      .filter(Boolean)
      .map(print => print.id)
      .filter(Boolean);
  }

  function findContextualSurpriseSource() {
    const selectedIds = getSelectedPrintIdsForSurprise();
    if (!selectedIds.length) return null;

    const candidates = getAllAutofillSources()
      .map((source, globalIndex) => {
        const sourcePrintIds = Array.isArray(source.prints) ? source.prints : [];
        const sourceSet = new Set(sourcePrintIds);

        const overlap = selectedIds.filter(printId => sourceSet.has(printId)).length;
        if (!overlap) return null;

        const uniqueOverlap = uniqueIds(selectedIds.filter(printId => sourceSet.has(printId))).length;
        const collectionRank = getSourceRankInCollection(source.id);
        const inCurrentCollection = collectionRank < Number.MAX_SAFE_INTEGER;

        return {
          source,
          overlap,
          uniqueOverlap,
          collectionRank,
          inCurrentCollection,
          globalIndex
        };
      })
      .filter(Boolean);

    if (!candidates.length) return null;

    candidates.sort((a, b) => {
      if (b.overlap !== a.overlap) return b.overlap - a.overlap;
      if (b.uniqueOverlap !== a.uniqueOverlap) return b.uniqueOverlap - a.uniqueOverlap;
      if (Number(b.inCurrentCollection) !== Number(a.inCurrentCollection)) {
        return Number(b.inCurrentCollection) - Number(a.inCurrentCollection);
      }
      if (a.collectionRank !== b.collectionRank) return a.collectionRank - b.collectionRank;
      return a.globalIndex - b.globalIndex;
    });

    return candidates[0].source;
  }

  function getExpandedSourcePrintIds(source) {
    const targetCount = getSetSize();
    const sourceIds = source && Array.isArray(source.prints) ? source.prints : [];
    const selectedIds = state.selectedPrints.filter(Boolean).map(print => print.id);
    const collectionIds = getFilteredResultIds();
    const allIds = Object.keys(getPrintsObject());
    return uniqueIds([...sourceIds, ...selectedIds, ...collectionIds, ...allIds])
      .filter(printId => Boolean(getPrint(printId)))
      .slice(0, targetCount);
  }

  function getAnimationPoolIds(finalPrintIds = []) {
    const finalSet = new Set(finalPrintIds.filter(Boolean));
    let pool = getFilteredResultIds().filter(printId => !finalSet.has(printId));

    if (pool.length < 6) {
      pool = Object.keys(getPrintsObject()).filter(printId => !finalSet.has(printId));
    }

    if (!pool.length) pool = finalPrintIds.filter(Boolean);
    return pool;
  }

  function getRandomPrintFromPool(poolIds) {
    if (!poolIds || !poolIds.length) return null;
    const printId = poolIds[Math.floor(Math.random() * poolIds.length)];
    return printId ? getPrint(printId) : null;
  }

  function clearSurpriseTimers() {
    (state.surpriseTimers || []).forEach(timer => window.clearTimeout(timer));
    state.surpriseTimers = [];
  }

  function setSurpriseLoading(isLoading) {
    state.isSurprising = Boolean(isLoading);
    if (els.shuffle) {
      els.shuffle.classList.toggle('is-loading', Boolean(isLoading));
      els.shuffle.disabled = Boolean(isLoading);
    }
    updateStageActionButtons();
  }

  function renderTemporarySlotPrint(slotIndex, print) {
    const slotButton = root.querySelector(`[data-psb-slot="${slotIndex}"]`);
    const slotImg = slotButton ? slotButton.querySelector('[data-psb-slot-img]') : null;
    if (!slotButton || !slotImg || !print || !print.src) return;

    slotButton.classList.add('has-print', 'is-surprise-spinning');
    slotImg.src = getOptimizedImageSrc(print.src, SLOT_IMAGE_WIDTH);
    slotImg.alt = print.id || '';
    slotImg.style.display = 'block';
  }

  function finishSurpriseWithPrints(finalPrints, source, recordHistory = true) {
    clearSurpriseTimers();

    state.selectedPrints = finalPrints.slice(0, getSetSize());
    while (state.selectedPrints.length < getSetSize()) state.selectedPrints.push(null);

    state.lastSurpriseSourceId = source && source.id ? source.id : null;
    state.lastSurpriseResultKey = getSelectionKey(state.selectedPrints);

    state.hasCompletedFirstSet = isSetComplete();
    ensureActiveSlot();
    if (els.previewTitle && source) els.previewTitle.textContent = source.title || `Build your set of ${getSetSize()}`;

    els.slotButtons.forEach(button => button.classList.remove('is-surprise-spinning'));

    renderSlots();
    updateRenderedThumbSelectionState();
    retryVisibleThumbImages(false);
    updateHiddenProperties();
    setStatus('');
    hideToast();
    hideGuidance();
    setSurpriseLoading(false);
    expandPrintSheet(true);
  }

  function animateSourceToSlots(source, recordHistory = true) {
    if (!source || !Array.isArray(source.prints) || source.prints.length < 1) return false;

    const expandedPrintIds = getExpandedSourcePrintIds(source);
    const finalPrints = expandedPrintIds.map(printId => getPrint(printId)).filter(Boolean);
    while (finalPrints.length < getSetSize()) finalPrints.push(null);
    const finalIds = finalPrints.map(print => print && print.id).filter(Boolean);

    if (recordHistory) pushHistory();

    clearSurpriseTimers();
    setSurpriseLoading(true);

    const poolIds = getAnimationPoolIds(finalIds);
    const stepsPerSlot = 4;
    const stepDelay = 78;
    const slotDelay = 120;

    finalPrints.forEach((finalPrint, slotIndex) => {
      for (let step = 0; step < stepsPerSlot; step += 1) {
        const timer = window.setTimeout(() => {
          const randomPrint = getRandomPrintFromPool(poolIds) || finalPrint;
          if (randomPrint) renderTemporarySlotPrint(slotIndex, randomPrint);
        }, slotIndex * slotDelay + step * stepDelay);
        state.surpriseTimers.push(timer);
      }

      const finalTimer = window.setTimeout(() => {
        if (finalPrint) renderTemporarySlotPrint(slotIndex, finalPrint);
      }, slotIndex * slotDelay + stepsPerSlot * stepDelay);
      state.surpriseTimers.push(finalTimer);
    });

    const finishDelay = (getSetSize() - 1) * slotDelay + (stepsPerSlot + 1) * stepDelay + 70;
    const finishTimer = window.setTimeout(() => {
      finishSurpriseWithPrints(finalPrints, source, recordHistory);
    }, finishDelay);
    state.surpriseTimers.push(finishTimer);

    return true;
  }

  function getApiSurpriseContextKey(selectedIds = state.selectedPrints.map(print => print && print.id).filter(Boolean)) {
    return [
      state.selectedCollectionHandle || ALL_HANDLE,
      root.dataset.defaultCollection || 'bestsellers',
      selectedIds.join('|')
    ].join('::');
  }

  function resetApiSurpriseSkipList(contextKey) {
    state.lastSurpriseContextKey = contextKey || '';
    state.apiSurpriseSkipSourceIds = [];
  }

  function rememberApiSurpriseSource(sourceId, contextKey) {
    if (!sourceId) return;
    if (contextKey && state.lastSurpriseContextKey !== contextKey) resetApiSurpriseSkipList(contextKey);
    if (!Array.isArray(state.apiSurpriseSkipSourceIds)) state.apiSurpriseSkipSourceIds = [];
    if (!state.apiSurpriseSkipSourceIds.includes(sourceId)) state.apiSurpriseSkipSourceIds.push(sourceId);
    if (state.apiSurpriseSkipSourceIds.length > 80) state.apiSurpriseSkipSourceIds = state.apiSurpriseSkipSourceIds.slice(-80);
  }

  function isUnchangedAfterLastSurprise() {
    if (!state.lastSurpriseSourceId || !state.lastSurpriseResultKey) return false;
    return getSelectionKey(state.selectedPrints) === state.lastSurpriseResultKey;
  }

  async function smartSurpriseMe() {
    if (state.isSurprising) return;

    if (isApiCatalogMode() && state.catalogProvider && typeof state.catalogProvider.surprise === 'function') {
      try {
        setSurpriseLoading(true);
        const selectedPrintIds = state.selectedPrints.map(print => print && print.id).filter(Boolean);
        const unchangedAfterLastSurprise = isUnchangedAfterLastSurprise();
        const apiContextKey = unchangedAfterLastSurprise
          ? (state.lastSurpriseContextKey || getApiSurpriseContextKey([]))
          : getApiSurpriseContextKey(selectedPrintIds);

        if (!unchangedAfterLastSurprise && state.lastSurpriseContextKey !== apiContextKey) resetApiSurpriseSkipList(apiContextKey);
        if (unchangedAfterLastSurprise && state.lastSurpriseSourceId) rememberApiSurpriseSource(state.lastSurpriseSourceId, apiContextKey);

        const payload = await state.catalogProvider.surprise({
          collection: state.selectedCollectionHandle,
          defaultCollection: root.dataset.defaultCollection || 'bestsellers',
          selectedPrintIds,
          skipSourceId: unchangedAfterLastSurprise ? state.lastSurpriseSourceId : '',
          skipSourceIds: unchangedAfterLastSurprise ? state.apiSurpriseSkipSourceIds : []
        });
        const ids = ingestApiItems(payload.items || []);
        const source = {
          id: payload.sourceId || payload.source_id || '',
          title: payload.sourceTitle || payload.source_title || `Build your set of ${getSetSize()}`,
          prints: ids.slice(0, getSetSize())
        };
        rememberApiSurpriseSource(source.id, apiContextKey);
        setSurpriseLoading(false);
        if (source.prints.length > 0 && animateSourceToSlots(source, true)) return;
      } catch (error) {
        setSurpriseLoading(false);
        setStatus(error.message || 'Could not load matching prints.', true);
        updateHiddenProperties();
        showMatchingFailureGuidance();
        return;
      }
    }

    const skipSourceIds = [];
    const unchangedAfterLastSurprise = isUnchangedAfterLastSurprise();

    if (!unchangedAfterLastSurprise) {
      const contextualSource = findContextualSurpriseSource();
      if (contextualSource && animateSourceToSlots(contextualSource, true)) return;
    } else if (state.lastSurpriseSourceId) {
      skipSourceIds.push(state.lastSurpriseSourceId);
    }

    const fallbackSource = getNextAutofillSourceForCollection(true, skipSourceIds);
    if (!fallbackSource) {
      updateHiddenProperties();
      showMatchingFailureGuidance();
      return;
    }

    animateSourceToSlots(fallbackSource, true);
  }

  function getSelectionKey(selection) {
    return selection.map(print => print && print.id ? print.id : '').join('|');
  }

  function clearSelection() {
    if (state.isSurprising) {
      clearSurpriseTimers();
      setSurpriseLoading(false);
      els.slotButtons.forEach(button => button.classList.remove('is-surprise-spinning'));
    }

    const activeIndex = getActiveSlotIndex();
    const shouldClearSelected = activeIndex !== null;
    const hasSomethingToClear = shouldClearSelected
      ? Boolean(state.selectedPrints[activeIndex])
      : state.selectedPrints.some(Boolean);

    if (!hasSomethingToClear) {
      updateStageActionButtons();
      return;
    }

    pushHistory();

    if (shouldClearSelected) {
      state.selectedPrints[activeIndex] = null;
      state.hasCompletedFirstSet = false;
    } else {
      state.selectedPrints = Array(getSetSize()).fill(null);
      state.hasCompletedFirstSet = false;
      ensureActiveSlot(0);
    }

    if (els.previewTitle) els.previewTitle.textContent = `Build your set of ${getSetSize()}`;
    renderSlots();
    updateRenderedThumbSelectionState();
    retryVisibleThumbImages(false);
    if (areAllSlotsEmpty()) clearKnownPrintUrlValue();
    updateHiddenProperties();
    setStatus('');
  }

  function hasIncompleteSelection() {
    return state.selectedPrints.some(print => !print);
  }

  function warnIncompleteSelection() {
    const firstEmpty = state.selectedPrints.findIndex(print => !print);
    if (firstEmpty >= 0) setActiveSlot(firstEmpty);
    expandPrintSheet({ reason: 'incomplete-selection' });
    showGuidance(`Please choose all ${getSetSize()} ${getSetSize() === 1 ? 'print' : 'prints'} before adding this set to cart.`, { title: 'Complete your set' });
    setStatus('', false);
    if (!isMobileLayout()) {
      scrollBuilderForPrintChoice({ behavior: 'smooth' });
      requestDesktopLayoutUpdate(true);
    }
  }

  function blockIncompletePurchase(event) {
    if (!hasIncompleteSelection()) return false;
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    }
    warnIncompleteSelection();
    return true;
  }

  function guardPurchaseEvent(event) {
    const purchaseTarget = getPurchaseTargetFromEvent(event);
    if (!isProductPurchaseTarget(purchaseTarget)) return false;
    return blockIncompletePurchase(event);
  }

  function validateBeforeSubmit(event) {
    if (event && !isProductCartForm(event.target)) return true;
    if (blockIncompletePurchase(event)) return false;
    updateHiddenProperties();
    return true;
  }

  function findPrimaryCartSubmitter(form) {
    if (!form) return null;
    return form.querySelector('button[name="add"]:not([disabled]), input[name="add"]:not([disabled])')
      || form.querySelector('.product-form__submit:not([disabled])')
      || form.querySelector('button[type="submit"]:not([disabled]), input[type="submit"]:not([disabled])');
  }

  function submitBuilderSelectionToCart(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    }

    if (blockIncompletePurchase(event)) return false;

    const form = getProductForm();
    if (!form) {
      showGuidance('Add to cart is unavailable. Please use the main product button.', { title: 'Add to cart unavailable' });
      return false;
    }

    updateHiddenProperties();

    const submitProductForm = () => {
      const submitter = findPrimaryCartSubmitter(form);
      // Use the real product Add to cart button when possible. This keeps
      // click-based Shopify/GA4/Google Ads listeners attached to the native
      // submitter in the normal event path, instead of only dispatching a
      // programmatic form submit from the custom mobile catalog CTA.
      if (submitter && typeof submitter.click === 'function') {
        submitter.click();
      } else if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else if (typeof form.submit === 'function') {
        form.submit();
      }
    };

    if (isMobileLayout() && state.sheetMode === 'expanded') {
      collapsePrintSheet();
      window.requestAnimationFrame(submitProductForm);
    } else {
      submitProductForm();
    }

    return true;
  }

  function clampNumber(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function getLayoutPreset() {
    return LAYOUT_MODULE.getPreset(getSetSize());
  }

  function setSlotScale(value, refresh = true) {
    const next = clampNumber(Number(value) || 1, 0.35, 1);
    if (Math.abs(next - state.slotScale) < 0.001) return false;
    state.slotScale = next;
    if (refresh) updateStageMetrics();
    return true;
  }

  function setTouchSearchViewportLock(active) {
    const shouldLock = Boolean(active && !isMobileLayout() && isTouchInteractionDevice());
    if (shouldLock === state.touchSearchViewportLocked) return;

    if (shouldLock) {
      if (!state.touchSearchScrollCaptured) {
        state.touchSearchScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      }
      document.documentElement.classList.add('psb-touch-search-open');
      if (document.body) document.body.classList.add('psb-touch-search-open');
      state.touchSearchViewportLocked = true;
      return;
    }

    const restoreY = Number(state.touchSearchScrollY) || 0;
    document.documentElement.classList.remove('psb-touch-search-open');
    if (document.body) document.body.classList.remove('psb-touch-search-open');
    state.touchSearchViewportLocked = false;
    state.touchSearchScrollCaptured = false;
    window.requestAnimationFrame(() => window.scrollTo(0, restoreY));
  }

  function setMobileSearchMode(active) {
    const next = Boolean(active && usesTouchSearchMechanics() && state.sheetMode === 'expanded');
    const wideTouchSearch = Boolean(next && !isMobileLayout() && isTouchInteractionDevice());
    window.clearTimeout(state.mobileSearchRestoreTimer);
    state.mobileSearchActive = next;
    root.classList.toggle('psb-mobile-search-active', next);
    root.classList.toggle('psb-touch-search-active', wideTouchSearch);
    setTouchSearchViewportLock(wideTouchSearch);
    updateMobilePopupMetrics();
    updateTouchSearchPopupMetrics();
  }

  function setPxVar(name, value) {
    const pxValue = `${Math.round(value * 100) / 100}px`;
    root.style.setProperty(name, pxValue);
    if (els.stage) els.stage.style.setProperty(name, pxValue);
  }

  function setPctVar(name, value) {
    const pctValue = `${Math.round(value * 1000) / 1000}%`;
    root.style.setProperty(name, pctValue);
    if (els.stage) els.stage.style.setProperty(name, pctValue);
  }

  function applyStageAspectForSetSize() {
    const aspect = isDesktopLayout()
      ? '4 / 3'
      : LAYOUT_MODULE.getStageAspectCss(getSetSize());
    root.style.setProperty('--psb-stage-aspect', aspect);
    if (els.stage) els.stage.style.setProperty('--psb-stage-aspect', aspect);
  }

  function applySlotGeometry(layout) {
    const useLegacyImageThree = root.classList.contains('psb-preview-image') && getSetSize() === 3;
    const legacyImagePositions = [
      { left: '19%', top: '24%', width: '16.5%' },
      { left: '42%', top: '24%', width: '16.5%' },
      { left: '65%', top: '24%', width: '16.5%' }
    ];

    els.slotButtons.forEach((slotButton, index) => {
      if (!slotButton) return;
      if (index >= getSetSize()) {
        slotButton.style.removeProperty('left');
        slotButton.style.removeProperty('top');
        slotButton.style.removeProperty('width');
        slotButton.style.removeProperty('z-index');
        return;
      }
      const position = layout.positions[index];
      if (!position) return;

      if (useLegacyImageThree) {
        const legacy = legacyImagePositions[index];
        slotButton.style.left = legacy.left;
        slotButton.style.top = legacy.top;
        slotButton.style.width = legacy.width;
      } else {
        slotButton.style.left = `${Math.round(position.left * 100) / 100}px`;
        slotButton.style.top = `${Math.round(position.top * 100) / 100}px`;
        slotButton.style.width = `${Math.round(position.width * 100) / 100}px`;
      }
      slotButton.style.zIndex = index === 2 && useLegacyImageThree ? '3' : '2';
    });
  }

  let stageMetricsRefreshRaf = null;
  let stageMetricsRefreshTimer = null;

  function refreshStageMetricsAfterLayoutChange() {
    updateStageMetrics();

    if (stageMetricsRefreshRaf !== null) {
      window.cancelAnimationFrame(stageMetricsRefreshRaf);
    }
    window.clearTimeout(stageMetricsRefreshTimer);

    stageMetricsRefreshRaf = window.requestAnimationFrame(() => {
      updateStageMetrics();
      stageMetricsRefreshRaf = window.requestAnimationFrame(() => {
        stageMetricsRefreshRaf = null;
        updateStageMetrics();
      });
    });

    stageMetricsRefreshTimer = window.setTimeout(() => {
      stageMetricsRefreshTimer = null;
      updateStageMetrics();
    }, 120);
  }

  function updateStageMetrics() {
    if (!els.stage) return;
    const rect = els.stage.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const w = rect.width;
    const outer = normalizeSize(state.previewOuterSize, DEFAULT_PRINT_SIZE);
    const print = normalizeSize(state.previewPrintSize, outer);
    const layout = LAYOUT_MODULE.calculateLayout({
      count: getSetSize(),
      stageWidth: rect.width,
      stageHeight: rect.height,
      outer,
      isFramed: state.isFramed,
      frameProfileIn: FRAME_PROFILE_IN,
      baseSlotWidthPct: BASE_SLOT_WIDTH_PCT,
      wallGapIn: WALL_GAP_IN,
      baseDisplayWidthIn: BASE_DISPLAY_WIDTH_IN,
      maxGroupWidthPct: MAX_OUTER_GROUP_WIDTH_PCT,
      maxGroupHeightPct: MAX_OUTER_GROUP_HEIGHT_PCT,
      scale: state.slotScale
    });

    const slotW = layout.slotWidth;
    const slotH = layout.slotHeight;
    const frameSize = layout.frameSize;
    if (!slotW || !slotH) return;

    root.style.setProperty('--psb-stage-w', `${w}px`);
    root.style.setProperty('--psb-stage-h', `${rect.height}px`);
    els.stage.style.setProperty('--psb-stage-w', `${w}px`);
    els.stage.style.setProperty('--psb-stage-h', `${rect.height}px`);
    root.style.setProperty('--psb-slot-ratio', `${outer.width} / ${outer.height}`);
    els.stage.style.setProperty('--psb-slot-ratio', `${outer.width} / ${outer.height}`);
    applySlotGeometry(layout);

    let matLeft = 0;
    let matTop = 0;
    let printW = slotW;
    let printH = slotH;

    if (state.isFramed && state.hasMat) {
      matLeft = Math.max(0, slotW * (((outer.width - print.width) / 2) / outer.width));
      matTop = Math.max(0, slotH * (((outer.height - print.height) / 2) / outer.height));
      printW = Math.max(1, slotW - matLeft * 2);
      printH = Math.max(1, slotH - matTop * 2);
    }

    setPxVar('--psb-frame-size', frameSize);
    setPxVar('--psb-mat-size', Math.max(matLeft, matTop));
    setPxVar('--psb-print-bleed', clampNumber(slotW * 0.006, 1, 2));
    setPxVar('--psb-inner-shadow-size', clampNumber(slotW * 0.018, 3, 6.5));

    const bleed = state.hasMat ? clampNumber(slotW * 0.006, 1, 2) : 0;
    setPxVar('--psb-print-left', matLeft - bleed);
    setPxVar('--psb-print-top', matTop - bleed);
    setPxVar('--psb-print-width', printW + bleed * 2);
    setPxVar('--psb-print-height', printH + bleed * 2);

    setPxVar('--psb-shadow-1x', clampNumber(slotW * 0.008, 1.4, 3));
    setPxVar('--psb-shadow-1y', clampNumber(slotW * 0.010, 1.8, 4));
    setPxVar('--psb-shadow-1b', clampNumber(slotW * 0.011, 2, 4.5));
    setPxVar('--psb-shadow-2x', clampNumber(slotW * 0.020, 4, 8));
    setPxVar('--psb-shadow-2y', clampNumber(slotW * 0.025, 4.5, 9));
    setPxVar('--psb-shadow-2b', clampNumber(slotW * 0.028, 5, 10));

    const canvasDepth = clampNumber(slotW * 0.026, 4, 8);
    setPxVar('--psb-canvas-depth', canvasDepth);
    const captionGap = state.isFramed ? frameSize + 2 : (root.classList.contains('psb-style-canvas') ? canvasDepth + 2 : 2);
    setPxVar('--psb-caption-gap', captionGap);
  }

  function observeStageMetrics() {
    updateStageMetrics();

    if (window.ResizeObserver && els.stage) {
      const stageResizeObserver = new ResizeObserver(() => updateStageMetrics());
      stageResizeObserver.observe(els.stage);
    }

    window.addEventListener('resize', updateStageMetrics, { passive: true });
    window.addEventListener('load', updateStageMetrics, { once: true });
  }

  function isTouchPortraitMobileLayout() {
    if (!window.matchMedia || !window.matchMedia('(orientation: portrait)').matches) return false;
    return isTouchInteractionDevice();
  }

  function isDesktopLayout() {
    const wideViewport = Boolean(window.matchMedia && window.matchMedia('(min-width: 750px)').matches);
    return wideViewport && !isTouchPortraitMobileLayout();
  }

  function isMobileLayout() {
    return !isDesktopLayout();
  }

  /*
    Interaction mode is capability-based rather than viewport-based. This keeps a
    resized desktop window in desktop interaction mode, while iPad/Android tablets
    and phones in landscape continue to get touch-safe behavior above 750px.
    A real touch tablet in portrait deliberately switches to the mobile builder layout.
  */
  function isTouchInteractionDevice() {
    const touchPoints = Math.max(0, Number(navigator.maxTouchPoints || 0));
    if (!touchPoints) return false;

    const coarsePrimary = Boolean(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
    const noHover = Boolean(window.matchMedia && window.matchMedia('(hover: none)').matches);
    const userAgent = String(navigator.userAgent || '');
    const mobileOrTabletUa = /Android|iPhone|iPad|iPod|Mobile|Tablet|Silk|Kindle/i.test(userAgent);
    const iPadDesktopUa = /Macintosh/i.test(userAgent) && touchPoints > 1;

    return coarsePrimary || noHover || mobileOrTabletUa || iPadDesktopUa;
  }

  function syncInteractionModeClass() {
    const touchMode = isTouchInteractionDevice();
    const touchPortraitMobile = Boolean(
      touchMode
      && window.matchMedia
      && window.matchMedia('(min-width: 750px)').matches
      && window.matchMedia('(orientation: portrait)').matches
    );

    root.classList.toggle('psb-touch-interaction', touchMode);
    root.classList.toggle('psb-touch-portrait-mobile', touchPortraitMobile);
    root.dataset.psbInteractionMode = touchMode ? 'touch' : 'pointer';
    root.dataset.psbLayoutMode = touchPortraitMobile ? 'touch-portrait-mobile' : (isMobileLayout() ? 'mobile' : 'desktop');

    const productRoot = root.closest('.product');
    if (productRoot) productRoot.classList.toggle('psb-tablet-portrait-mobile', touchPortraitMobile);

    return touchMode;
  }

  function usesTouchSearchMechanics() {
    return isMobileLayout() || isTouchInteractionDevice();
  }

  function findProductInfoWrapper() {
    const productRoot = root.closest('.product') || root.closest('.product-section') || document;
    return productRoot.querySelector('.product__info-wrapper') || productRoot.querySelector('.product__info-container') || null;
  }

  function getVisualViewportRect() {
    const viewport = window.visualViewport || null;
    const top = viewport && Number.isFinite(Number(viewport.offsetTop)) ? Number(viewport.offsetTop) : 0;
    const height = viewport && viewport.height ? Number(viewport.height) : window.innerHeight;
    const width = viewport && viewport.width ? Number(viewport.width) : window.innerWidth;
    return {
      top,
      bottom: top + height,
      height,
      width
    };
  }

  function getSafeViewportBottom() {
    const viewport = getVisualViewportRect();
    return Math.max(220, Math.floor(viewport.bottom - 4));
  }

  function getElementOuterHeight(element) {
    if (!element) return 0;
    const rect = element.getBoundingClientRect();
    if (!rect.height) return 0;

    const style = window.getComputedStyle(element);
    const marginTop = parseFloat(style.marginTop) || 0;
    const marginBottom = parseFloat(style.marginBottom) || 0;
    return Math.ceil(rect.height + marginTop + marginBottom);
  }

  function getElementVerticalMargins(element) {
    if (!element || element.hidden) return 0;
    const style = window.getComputedStyle(element);
    return (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
  }

  function getElementVerticalPadding(element) {
    if (!element) return 0;
    const style = window.getComputedStyle(element);
    return (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
  }

  function getMobileControlsChromeHeight(isSearchActive) {
    if (!els.controls) return 142;

    let height = getElementVerticalPadding(els.controls);
    height += getElementOuterHeight(els.collectionSelect);
    height += getElementOuterHeight(els.search);

    if (els.resultsMeta && !els.resultsMeta.hidden && window.getComputedStyle(els.resultsMeta).display !== 'none') {
      height += getElementOuterHeight(els.resultsMeta);
    }

    if (!isSearchActive && els.mobileVariantBar && !els.mobileVariantBar.hidden) {
      height += getElementOuterHeight(els.mobileVariantBar);
    }

    if (els.status && els.status.textContent.trim()) {
      height += getElementOuterHeight(els.status);
    }

    height += getElementVerticalMargins(els.thumbs);
    return Math.max(88, Math.ceil(height));
  }

  function estimateThumbRowHeightForColumns(containerWidth, gap, columns) {
    const safeColumns = Math.max(1, Number(columns) || 1);
    const width = Math.max(0, Number(containerWidth) || 0);
    if (!width) return 0;
    const columnWidth = Math.max(38, (width - gap * Math.max(0, safeColumns - 1)) / safeColumns);
    return Math.max(54, columnWidth * 1.4);
  }

  function getDesktopPreferredThumbColumns(containerWidth, gap, fallbackColumns) {
    const minColumns = Math.max(1, Number(BUILDER_CONFIG.system.desktopPicker.minThumbColumns) || 6);
    const minColumnWidth = Math.max(64, Number(BUILDER_CONFIG.system.desktopPicker.minThumbColumnWidthPx) || 88);
    const fallback = Math.max(1, Number(fallbackColumns) || 5);
    const width = Math.max(0, Number(containerWidth) || 0);
    if (!width || fallback >= minColumns) return fallback;

    const columnWidth = (width - gap * Math.max(0, minColumns - 1)) / minColumns;
    return columnWidth >= minColumnWidth ? minColumns : fallback;
  }

  function getLandscapeMultiRowMinSheetTop(lowerLimit) {
    if (!els.stage || state.mobileSearchActive) return null;

    const preset = getLayoutPreset();
    if (!preset || Number(preset.rows || 1) < 2) return null;

    const viewport = getVisualViewportRect();
    const isLandscape = viewport.width >= viewport.height;
    if (!isLandscape) return null;

    const configuredShare = Number(BUILDER_CONFIG.system.desktopPicker.multiRowLandscapeMinPreviewShare);
    const previewShare = clampNumber(Number.isFinite(configuredShare) ? configuredShare : 0.5, 0, 0.9);
    if (previewShare <= 0) return null;

    const stageRect = els.stage.getBoundingClientRect();
    if (!stageRect.width || !stageRect.height) return null;

    const visibleTop = Math.max(viewport.top, stageRect.top);
    const combinedHeight = Math.max(0, Number(lowerLimit) - visibleTop);
    if (combinedHeight <= 0) return null;

    // Do not enlarge a naturally short stage on tall/high-resolution screens.
    // The floor only prevents an already-larger preview from being compressed too far.
    const naturalPreviewHeight = Math.max(0, stageRect.bottom - visibleTop);
    const targetPreviewHeight = Math.min(naturalPreviewHeight, combinedHeight * previewShare);
    if (targetPreviewHeight <= 0) return null;

    return visibleTop + targetPreviewHeight;
  }

  function updateDesktopStageMaxHeight() {
    if (!els.stage || !isDesktopLayout() || state.sheetMode === 'expanded') {
      root.style.removeProperty('--psb-stage-max-height');
      if (els.stage) els.stage.style.removeProperty('--psb-stage-max-height');
      return false;
    }

    const stageRect = els.stage.getBoundingClientRect();
    if (!stageRect.width || !stageRect.top && stageRect.top !== 0) return false;

    const selectorHeight = getElementOuterHeight(els.setSizeSelector);
    const footerHeight = getElementOuterHeight(els.stageFooterActions);
    const reserveBelowStage = selectorHeight + footerHeight + 22;
    const viewport = getVisualViewportRect();
    const preset = getLayoutPreset();

    // Closed desktop preview should be sized for the builder viewport itself,
    // not for the page slice currently left below the site header/title area.
    // The user can scroll the stage top to the viewport top; this budget keeps
    // the footer controls fitting in that useful area without crushing the stage
    // on first load when the header is still visible above it.
    const naturalStageHeight = stageRect.width * 0.75;
    const availableStageHeight = Math.floor(viewport.height - reserveBelowStage - 4);

    const rows = Math.max(1, Number(preset.rows) || 1);
    const hardMinByRows = rows >= 3 ? 330 : rows === 2 ? 300 : 260;
    const preferredMinByRows = rows >= 3 ? 460 : rows === 2 ? 390 : 320;
    const hardMinHeight = Math.min(naturalStageHeight, Math.max(hardMinByRows, stageRect.width * (rows >= 3 ? 0.40 : rows === 2 ? 0.38 : 0.34)));
    const preferredMinHeight = Math.min(naturalStageHeight, Math.max(hardMinHeight, Math.min(preferredMinByRows, stageRect.width * (rows >= 3 ? 0.56 : rows === 2 ? 0.50 : 0.42))));

    if (!Number.isFinite(availableStageHeight) || availableStageHeight <= 0 || availableStageHeight >= naturalStageHeight - 3) {
      root.style.removeProperty('--psb-stage-max-height');
      els.stage.style.removeProperty('--psb-stage-max-height');
      return false;
    }

    const mustFitControls = availableStageHeight >= hardMinHeight;
    const cappedHeight = mustFitControls
      ? Math.max(hardMinHeight, Math.min(naturalStageHeight, availableStageHeight))
      : preferredMinHeight;
    const nextValue = `${Math.round(cappedHeight)}px`;
    const previousValue = root.style.getPropertyValue('--psb-stage-max-height');
    root.style.setProperty('--psb-stage-max-height', nextValue);
    els.stage.style.setProperty('--psb-stage-max-height', nextValue);
    return previousValue !== nextValue;
  }

  function getFirstThumbMetrics() {
    const firstThumb = els.thumbs ? els.thumbs.querySelector('.psb-thumb') : null;
    const rect = firstThumb ? firstThumb.getBoundingClientRect() : null;
    const rowHeight = rect && rect.height ? rect.height : 118;
    const styles = els.thumbs ? window.getComputedStyle(els.thumbs) : null;
    const gap = styles ? parseFloat(styles.rowGap || styles.gap || '8') || 8 : 8;
    return { rowHeight, gap };
  }

  function getNonThumbsHeight() {
    if (!els.controls || !els.thumbs) return 120;
    const controlsRect = els.controls.getBoundingClientRect();
    const thumbsRect = els.thumbs.getBoundingClientRect();
    const thumbsHeight = thumbsRect.height || 0;
    return Math.max(84, controlsRect.height - thumbsHeight);
  }

  function getPreviewControlsBottom() {
    const controls = root.querySelector('.psb-preview-controls');
    if (!controls) return 0;
    const elements = Array.from(controls.querySelectorAll('button')).filter(element => {
      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });
    const rects = elements.length ? elements.map(element => element.getBoundingClientRect()) : [controls.getBoundingClientRect()];
    return rects.reduce((bottom, rect) => Math.max(bottom, rect.bottom || 0), 0);
  }

  function getTopmostSlotFrameTop() {
    const styles = window.getComputedStyle(root);
    const frameSize = parseFloat(styles.getPropertyValue('--psb-frame-size') || '0') || 0;
    let top = Infinity;
    els.slotButtons.forEach(button => {
      if (!button) return;
      const rect = button.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) top = Math.min(top, rect.top - frameSize);
    });
    return Number.isFinite(top) ? top : 0;
  }

  function getCurrentThumbColumnCount() {
    if (!els.thumbs) return 5;
    const styles = window.getComputedStyle(els.thumbs);
    const template = String(styles.gridTemplateColumns || '').trim();
    if (!template || template === 'none') return 5;
    return Math.max(1, template.split(/\s+/).length);
  }

  function getProtectedBottom() {
    if (!els.stage) return 0;
    let protectedBottom = els.stage.getBoundingClientRect().top;
    const protectedElements = [
      ...Array.from(root.querySelectorAll('[data-psb-slot]')),
      ...Array.from(root.querySelectorAll('[data-psb-slot-caption]'))
    ];
    protectedElements.forEach(element => {
      const rect = element.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) protectedBottom = Math.max(protectedBottom, rect.bottom);
    });
    return protectedBottom;
  }

  function getMobileScrollableThumbsFromEvent(event) {
    if (!event || !event.target || !els.thumbs) return null;
    return els.thumbs.contains(event.target) ? els.thumbs : null;
  }

  function isMobilePickerInteractiveTarget(event) {
    const target = event && event.target && event.target.nodeType === 1 ? event.target : null;
    if (!target) return false;
    if (els.mobileVariantBar && els.mobileVariantBar.contains(target)) return true;
    if (els.search && els.search.contains(target)) return true;
    if (els.collectionSelect && els.collectionSelect.contains(target)) return true;
    if (els.sheetAddCart && els.sheetAddCart.contains(target)) return true;
    if (els.sheetCollapse && els.sheetCollapse.contains(target)) return true;
    return Boolean(closestElement(target, 'select, input, textarea, button'));
  }

  function handleMobilePickerTouchStart(event) {
    if (!state.mobileViewportLocked) return;
    const touch = event.touches && event.touches[0];
    state.mobileTouchStartY = touch ? touch.clientY : null;
  }

  function handleMobilePickerTouchMove(event) {
    if (!state.mobileViewportLocked) return;

    const scroller = getMobileScrollableThumbsFromEvent(event);
    if (!scroller) {
      if (isMobilePickerInteractiveTarget(event)) return;
      event.preventDefault();
      return;
    }

    const touch = event.touches && event.touches[0];
    if (!touch || state.mobileTouchStartY === null) return;

    const deltaY = touch.clientY - state.mobileTouchStartY;
    const atTop = scroller.scrollTop <= 0;
    const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;

    if ((atTop && deltaY > 0) || (atBottom && deltaY < 0)) {
      event.preventDefault();
    }
  }

  function handleMobilePickerWheel(event) {
    if (!state.mobileViewportLocked) return;

    const scroller = getMobileScrollableThumbsFromEvent(event);
    if (!scroller) {
      if (isMobilePickerInteractiveTarget(event)) return;
      event.preventDefault();
      return;
    }

    const atTop = scroller.scrollTop <= 0;
    const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
    if ((atTop && event.deltaY < 0) || (atBottom && event.deltaY > 0)) {
      event.preventDefault();
    }
  }

  function updateTouchSearchPopupMetrics() {
    const active = Boolean(
      state.mobileSearchActive
      && state.sheetMode === 'expanded'
      && !isMobileLayout()
      && isTouchInteractionDevice()
      && document.activeElement === els.search
      && els.controls
    );

    if (!active) {
      root.classList.remove('psb-touch-search-active');
      root.style.removeProperty('--psb-touch-search-left');
      root.style.removeProperty('--psb-touch-search-top');
      root.style.removeProperty('--psb-touch-search-width');
      root.style.removeProperty('--psb-touch-search-height');
      return;
    }

    root.classList.add('psb-touch-search-active');
    window.requestAnimationFrame(() => {
      if (
        !state.mobileSearchActive
        || state.sheetMode !== 'expanded'
        || isMobileLayout()
        || !isTouchInteractionDevice()
        || document.activeElement !== els.search
      ) return;

      const viewport = window.visualViewport || null;
      const viewportLeft = Math.max(0, Math.round(viewport && Number.isFinite(Number(viewport.offsetLeft)) ? Number(viewport.offsetLeft) : 0));
      const viewportTop = Math.max(0, Math.round(viewport && Number.isFinite(Number(viewport.offsetTop)) ? Number(viewport.offsetTop) : 0));
      const viewportWidth = Math.max(280, Math.round(viewport && viewport.width ? viewport.width : window.innerWidth));
      const viewportHeight = Math.max(220, Math.round(viewport && viewport.height ? viewport.height : window.innerHeight));
      const rootRect = root.getBoundingClientRect();
      const sideGap = 10;
      const topGap = 8;
      const maxWidth = Math.max(260, viewportWidth - sideGap * 2);
      const preferredWidth = Math.max(260, Math.min(rootRect.width || maxWidth, maxWidth));
      const minLeft = viewportLeft + sideGap;
      const maxLeft = viewportLeft + viewportWidth - preferredWidth - sideGap;
      const preferredLeft = Number.isFinite(rootRect.left) ? rootRect.left : minLeft;
      const left = clampNumber(preferredLeft, minLeft, Math.max(minLeft, maxLeft));
      const top = viewportTop + topGap;
      const height = Math.max(190, viewportHeight - topGap * 2);

      root.style.setProperty('--psb-touch-search-left', `${Math.round(left)}px`);
      root.style.setProperty('--psb-touch-search-top', `${Math.round(top)}px`);
      root.style.setProperty('--psb-touch-search-width', `${Math.round(preferredWidth)}px`);
      root.style.setProperty('--psb-touch-search-height', `${Math.round(height)}px`);
    });
  }

  function updateMobilePopupMetrics() {
    if (!isMobileLayout() || state.sheetMode !== 'expanded' || !els.stage) return;
    window.requestAnimationFrame(() => {
      if (!isMobileLayout() || state.sheetMode !== 'expanded' || !els.stage) return;

      const viewport = window.visualViewport || null;
      const viewportHeight = viewport && viewport.height ? viewport.height : window.innerHeight;
      const viewportWidth = viewport && viewport.width ? viewport.width : window.innerWidth;
      const viewportTop = Math.max(0, Math.round(viewport && viewport.offsetTop ? viewport.offsetTop : 0));
      const viewportBottom = viewportTop + viewportHeight;
      const bottomGap = IS_COMPACT_UI ? 0 : BUILDER_CONFIG.system.mobilePicker.viewportBottomGapPx;
      const isSearchActive = Boolean(state.mobileSearchActive && document.activeElement === els.search);
      const useCompactPicker = !isSearchActive;
      const pickerConfig = BUILDER_CONFIG.system.mobilePicker;
      const preferredColumns = Math.max(1, Number(pickerConfig.preferredColumns) || 5);
      const narrowColumns = Math.max(1, Number(pickerConfig.narrowColumns) || 4);
      const emergencyColumns = Math.max(1, Number(pickerConfig.emergencyColumns || pickerConfig.compactColumns) || 6);
      const minColumnWidth = Math.max(40, Number(pickerConfig.minThumbColumnWidthPx) || 50);
      const preset = getLayoutPreset();
      const isPortraitTouchTablet = Boolean(
        root.classList.contains('psb-touch-portrait-mobile')
        && viewportWidth >= 750
      );
      const portraitTabletColumns = isPortraitTouchTablet
        ? Math.max(emergencyColumns, viewportWidth >= 950 ? 8 : (viewportWidth >= 800 ? 7 : 6))
        : 0;

      function getPlanForColumns(columns) {
        const safeColumns = Math.max(1, Number(columns) || preferredColumns);
        const horizontalReserve = safeColumns >= emergencyColumns ? 56 : 60;
        const rawColumnWidth = Math.max(1, (viewportWidth - horizontalReserve) / safeColumns);

        // The phone caps are deliberately small, but on an iPad-size portrait viewport
        // they make a 5:7 print cell wider than it is tall. Give portrait touch tablets
        // their own vertical 5:7 row geometry instead of stretching the phone cap.
        if (isPortraitTouchTablet) {
          const thumbHeight = Math.max(126, Math.min(196, Math.round(rawColumnWidth * 1.4)));
          return { columns: safeColumns, thumbHeight, thumbGap: 6, rawColumnWidth };
        }

        const minHeight = safeColumns >= emergencyColumns
          ? Math.max(48, Number(pickerConfig.compactThumbMinHeightPx) || 58)
          : Math.max(58, Number(pickerConfig.preferredThumbMinHeightPx) || 74);
        const maxHeight = safeColumns >= emergencyColumns
          ? Math.max(minHeight, Number(pickerConfig.compactThumbMaxHeightPx) || 104)
          : 116;
        const thumbHeight = Math.max(minHeight, Math.min(maxHeight, Math.round(rawColumnWidth * 1.35)));
        const thumbGap = safeColumns >= emergencyColumns ? 5 : 7;
        return { columns: safeColumns, thumbHeight, thumbGap, rawColumnWidth };
      }

      function getPreferredPlan() {
        if (isPortraitTouchTablet) return getPlanForColumns(portraitTabletColumns);
        if (isSearchActive) return getPlanForColumns(preferredColumns);
        const preferredColumnWidth = Math.max(1, (viewportWidth - 60) / preferredColumns);
        return getPlanForColumns(preferredColumnWidth < minColumnWidth ? narrowColumns : preferredColumns);
      }

      let mobilePlan = getPreferredPlan();
      let minThumbsHeight = 0;
      let searchMinThumbsHeight = 0;

      function rowsHeight(rows) {
        return Math.round(
          mobilePlan.thumbHeight * rows + mobilePlan.thumbGap * Math.max(0, rows - 1)
        );
      }

      function applyMobilePlan(plan) {
        mobilePlan = plan;
        minThumbsHeight = rowsHeight(pickerConfig.minReadableRows);
        searchMinThumbsHeight = rowsHeight(pickerConfig.searchMinReadableRows);
        root.style.setProperty('--psb-mobile-thumb-columns', String(mobilePlan.columns));
        root.style.setProperty('--psb-mobile-thumb-height', `${mobilePlan.thumbHeight}px`);
      }

      root.classList.toggle('psb-mobile-picker-compact', useCompactPicker);
      applyMobilePlan(mobilePlan);

      const getChromeReserve = () => getMobileControlsChromeHeight(isSearchActive);

      function measureCurrentFit() {
        const protectedBottom = getProtectedBottom();
        const controlsTop = Math.max(viewportTop + 8, Math.ceil(protectedBottom + 8));
        const availableHeight = viewportBottom - controlsTop - bottomGap;
        return {
          availableHeight,
          fits: availableHeight >= getChromeReserve() + minThumbsHeight
        };
      }

      function fitSlotsForCurrentPlan() {
        setSlotScale(1);
        updateMobileSlotLiftForPicker();

        let fit = measureCurrentFit();
        if (preset.rows > 1) {
          const minScale = pickerConfig.minSlotScale;
          const step = pickerConfig.scaleStep;
          for (let scale = 1; scale >= minScale - 0.001; scale -= step) {
            setSlotScale(scale);
            updateMobileSlotLiftForPicker();
            fit = measureCurrentFit();
            if (fit.fits) break;
          }
        }
        return fit;
      }

      if (isSearchActive) {
        setSlotScale(1);
      } else {
        let fit = fitSlotsForCurrentPlan();
        if (!fit.fits && mobilePlan.columns !== emergencyColumns) {
          const emergencyPlan = getPlanForColumns(emergencyColumns);
          if (emergencyPlan.thumbHeight >= (Number(pickerConfig.compactThumbMinHeightPx) || 58)) {
            applyMobilePlan(emergencyPlan);
            fit = fitSlotsForCurrentPlan();
          }
        }

        // Keep the stable v2.39 fitting pass above untouched. Only after it has
        // chosen the normal portrait-tablet geometry do we ask 3-row layouts for
        // ~0.5 more catalog row. The target is relative to whatever v2.39 already
        // managed to show on this exact visual viewport (e.g. ~1.5 -> ~2 rows,
        // ~2 -> ~2.5 rows). We only use the existing slot scale budget, so if the
        // safe minimum is reached first we keep the best safe gain instead.
        const extraPortraitRows = Math.max(0, Number(pickerConfig.portraitTabletThreeRowExtraRows) || 0);
        if (isPortraitTouchTablet && Number(preset.rows) === 3 && extraPortraitRows > 0) {
          updateMobileSlotLiftForPicker();

          const getCurrentThumbSpace = () => {
            const protectedBottom = getProtectedBottom();
            const controlsTop = Math.max(viewportTop + 8, Math.ceil(protectedBottom + 8));
            const controlsMaxHeight = Math.max(190, Math.floor(viewportBottom - controlsTop - bottomGap));
            return Math.max(72, Math.floor(controlsMaxHeight - getChromeReserve()));
          };

          const baselineThumbSpace = getCurrentThumbSpace();
          const extraHeightTarget = Math.round(
            (mobilePlan.thumbHeight + mobilePlan.thumbGap) * extraPortraitRows
          );
          const targetThumbSpace = baselineThumbSpace + extraHeightTarget;
          const minScale = Math.max(0.35, Number(pickerConfig.minSlotScale) || 0.44);
          const step = Math.max(0.01, Number(pickerConfig.scaleStep) || 0.04);
          const startScale = Math.max(minScale, Number(state.slotScale) || 1);

          for (let scale = startScale - step; scale >= minScale - 0.001; scale -= step) {
            setSlotScale(Math.max(minScale, scale));
            updateMobileSlotLiftForPicker();
            if (getCurrentThumbSpace() >= targetThumbSpace) break;
          }
        }
      }

      updateMobileSlotLiftForPicker();
      const protectedBottom = getProtectedBottom();
      const naturalControlsTop = Math.max(viewportTop + 8, Math.ceil(protectedBottom + 8));
      const chromeReserve = getChromeReserve();
      const highestAllowedSearchTop = Math.floor(
        viewportBottom - bottomGap - chromeReserve - searchMinThumbsHeight
      );
      const controlsTop = isSearchActive
        ? Math.max(viewportTop + 8, Math.min(naturalControlsTop, highestAllowedSearchTop))
        : naturalControlsTop;
      const controlsMaxHeight = Math.max(190, Math.floor(viewportBottom - controlsTop - bottomGap));
      const availableThumbsHeight = Math.max(72, Math.floor(controlsMaxHeight - chromeReserve));
      const thumbsHeight = Math.max(72, availableThumbsHeight);

      root.style.setProperty('--psb-mobile-controls-top', `${controlsTop}px`);
      root.style.setProperty('--psb-mobile-controls-max-height', `${controlsMaxHeight}px`);
      root.style.setProperty('--psb-mobile-thumbs-height', `${thumbsHeight}px`);
    });
  }

  function lockMobileViewport() {
    if (!document.body || state.mobileViewportLocked) {
      updateMobilePopupMetrics();
      return;
    }

    state.mobileScrollLockY = window.pageYOffset || document.documentElement.scrollTop || 0;
    state.mobileBodyLockStyles = null;

    document.documentElement.classList.add('psb-mobile-picker-open');
    document.body.classList.add('psb-mobile-picker-open');

    document.addEventListener('touchstart', handleMobilePickerTouchStart, { passive: true });
    document.addEventListener('touchmove', handleMobilePickerTouchMove, { passive: false });
    document.addEventListener('wheel', handleMobilePickerWheel, { passive: false });

    state.mobileViewportLocked = true;
    updateMobilePopupMetrics();
  }

  function unlockMobileViewport() {
    if (!document.body || !state.mobileViewportLocked) return;
    const restoreY = state.mobileScrollLockY || 0;

    document.documentElement.classList.remove('psb-mobile-picker-open');
    document.body.classList.remove('psb-mobile-picker-open');

    document.removeEventListener('touchstart', handleMobilePickerTouchStart);
    document.removeEventListener('touchmove', handleMobilePickerTouchMove);
    document.removeEventListener('wheel', handleMobilePickerWheel);

    state.mobileTouchStartY = null;
    state.mobileViewportLocked = false;
    state.mobileBodyLockStyles = null;

    window.requestAnimationFrame(() => window.scrollTo(0, restoreY));
  }

  function syncMobilePickerMode() {
    if (state.sheetMode === 'expanded' && isMobileLayout()) {
      lockMobileViewport();
    } else {
      unlockMobileViewport();
    }
  }

  function updateSheetHeaderButtons() {
    if (!els.sheetCollapse) return;

    if (IS_COMPACT_UI) {
      if (els.sheetAddCart) {
        els.sheetAddCart.setAttribute('aria-hidden', 'true');
        els.sheetAddCart.tabIndex = -1;
      }
      els.sheetCollapse.textContent = 'Continue to Preview';
      els.sheetCollapse.setAttribute('aria-label', 'Continue to preview');
      els.sheetCollapse.classList.add('psb-primary-action');
      updateCompactPurchaseControls();
      return;
    }

    if (els.sheetAddCart) {
      els.sheetAddCart.removeAttribute('aria-hidden');
      els.sheetAddCart.removeAttribute('tabindex');
    }
    els.sheetCollapse.textContent = 'Hide Catalog';
    els.sheetCollapse.setAttribute('aria-label', 'Hide print catalog');
    els.sheetCollapse.classList.remove('psb-primary-action');
  }

  function setSheetMode(mode) {
    const nextMode = mode === 'expanded' ? 'expanded' : 'closed';
    const previousMode = state.sheetMode;
    state.sheetMode = nextMode;

    if (previousMode !== nextMode) {
      state.sheetLayoutLocked = false;
      state.sheetLayoutMetrics = null;
    }

    if (nextMode === 'closed') {
      state.sheetLayoutLocked = false;
      state.sheetLayoutMetrics = null;
    }

    root.classList.toggle('psb-sheet-expanded', nextMode === 'expanded');
    root.classList.remove('psb-sheet-collapsed');
    if (nextMode === 'expanded') {
      root.style.removeProperty('--psb-stage-max-height');
      if (els.stage) els.stage.style.removeProperty('--psb-stage-max-height');
    }
    if (nextMode !== 'expanded' || !isMobileLayout()) root.style.setProperty('--psb-mobile-slot-lift', '0px');

    if (nextMode !== 'expanded') {
      state.mobileSearchActive = false;
      setTouchSearchViewportLock(false);
      root.classList.remove('psb-mobile-search-active', 'psb-touch-search-active', 'psb-mobile-picker-compact', 'psb-picker-compact');
      root.style.removeProperty('--psb-touch-search-left');
      root.style.removeProperty('--psb-touch-search-top');
      root.style.removeProperty('--psb-touch-search-width');
      root.style.removeProperty('--psb-touch-search-height');
      root.style.removeProperty('--psb-mobile-thumb-columns');
      root.style.removeProperty('--psb-desktop-thumb-columns');
      setSlotScale(1);
    }

    updateSheetHeaderButtons();
    syncShufflePlacement();

    syncMobilePickerMode();
  }

  function getCurrentSheetLift() {
    const raw = getComputedStyle(root).getPropertyValue('--psb-sheet-lift');
    const value = parseFloat(raw);
    return Number.isFinite(value) ? value : 0;
  }

  function getCurrentMobileSlotLift() {
    const raw = getComputedStyle(root).getPropertyValue('--psb-mobile-slot-lift');
    const value = parseFloat(raw);
    return Number.isFinite(value) ? value : 0;
  }

  function updateMobileSlotLiftForPicker() {
    if (!isMobileLayout() || state.sheetMode !== 'expanded' || !els.stage) {
      root.style.setProperty('--psb-mobile-slot-lift', '0px');
      return 0;
    }

    const buttonBottom = getPreviewControlsBottom();
    const currentLift = getCurrentMobileSlotLift();
    const naturalSlotTop = getTopmostSlotFrameTop() + currentLift;
    const slotLift = Math.max(0, Math.round(naturalSlotTop - buttonBottom - BUILDER_CONFIG.system.mobilePicker.slotSafeGapPx));
    root.style.setProperty('--psb-mobile-slot-lift', `${slotLift}px`);
    return slotLift;
  }

  function getControlsNaturalRect() {
    const rect = els.controls.getBoundingClientRect();
    const lift = getCurrentSheetLift();
    return {
      top: rect.top + lift,
      bottom: rect.bottom + lift,
      height: rect.height,
      left: rect.left,
      right: rect.right,
      width: rect.width
    };
  }

  function applySheetLayoutMetrics(metrics) {
    if (!metrics) return;
    root.style.setProperty('--psb-sheet-lift', `${Math.round(metrics.sheetLift)}px`);
    root.style.setProperty('--psb-thumbs-max-height', `${Math.max(72, Math.round(metrics.thumbsHeight))}px`);
    root.style.setProperty('--psb-controls-max-height', `${Math.max(150, Math.round(metrics.controlsMaxHeight))}px`);
    root.style.setProperty('--psb-desktop-slot-lift', `${Math.max(0, Math.round(metrics.slotLift || 0))}px`);
    if (metrics.thumbColumns) {
      root.style.setProperty('--psb-desktop-thumb-columns', String(metrics.thumbColumns));
    } else {
      root.style.removeProperty('--psb-desktop-thumb-columns');
    }
    root.classList.toggle('psb-picker-compact', Boolean(metrics.isCompact));
  }

  function updateDesktopLayout() {
    if (!els.stage || !els.controls || !els.thumbs) return;

    const mediaWrapper = root.closest('.product__media-wrapper');
    const infoWrapper = findProductInfoWrapper();

    const isOpenSheet = state.sheetMode === 'expanded';
    if (isOpenSheet && state.sheetLayoutLocked && state.sheetLayoutMetrics) {
      applySheetLayoutMetrics(state.sheetLayoutMetrics);
      return;
    }

    if (!isDesktopLayout()) {
      root.style.setProperty('--psb-sticky-top', '0px');
      root.style.setProperty('--psb-sheet-lift', '0px');
      root.style.setProperty('--psb-desktop-slot-lift', '0px');
      root.style.removeProperty('--psb-desktop-thumb-columns');
      root.classList.remove('psb-picker-compact');
      root.style.removeProperty('min-height');
      root.style.removeProperty('--psb-stage-max-height');
      if (els.stage) els.stage.style.removeProperty('--psb-stage-max-height');
      if (mediaWrapper) mediaWrapper.style.removeProperty('min-height');

      const { rowHeight, gap } = getFirstThumbMetrics();
      const mobileRowsHeight = Math.round(rowHeight * BUILDER_CONFIG.system.mobilePicker.resultRows + gap * 2.15);
      const viewportLimit = Math.round(window.innerHeight * BUILDER_CONFIG.system.mobilePicker.viewportLimitRatio);
      const thumbsHeight = Math.max(rowHeight * 1.6, Math.min(mobileRowsHeight, viewportLimit, BUILDER_CONFIG.system.mobilePicker.maxThumbsHeightPx));
      const nonThumbsHeight = getNonThumbsHeight();

      root.style.setProperty('--psb-thumbs-max-height', `${Math.max(160, Math.round(thumbsHeight))}px`);
      root.style.setProperty('--psb-controls-max-height', `${Math.max(240, Math.round(thumbsHeight + nonThumbsHeight + BUILDER_CONFIG.system.mobilePicker.controlsExtraPx))}px`);
      state.sheetLayoutLocked = false;
      state.sheetLayoutMetrics = null;
      updateMobilePopupMetrics();
      return;
    }

    root.style.setProperty('--psb-sticky-top', '0px');
    root.style.setProperty('--psb-desktop-slot-lift', '0px');
    root.style.removeProperty('--psb-desktop-thumb-columns');

    if (infoWrapper && mediaWrapper) {
      const infoHeight = infoWrapper.getBoundingClientRect().height;
      if (infoHeight > 0) {
        const minHeight = `${Math.ceil(infoHeight)}px`;
        root.style.minHeight = minHeight;
        mediaWrapper.style.minHeight = minHeight;
      }
    }

    if (updateDesktopStageMaxHeight()) {
      window.requestAnimationFrame(updateStageMetrics);
    }

    const controlsRect = getControlsNaturalRect();
    const lowerLimit = getSafeViewportBottom();
    const nonThumbsHeight = getNonThumbsHeight();
    const firstMetrics = getFirstThumbMetrics();
    const gap = firstMetrics.gap;
    const thumbsWidth = els.thumbs.getBoundingClientRect().width || els.thumbs.clientWidth || 0;
    const initialThumbColumns = getCurrentThumbColumnCount();
    const preferredThumbColumns = getDesktopPreferredThumbColumns(thumbsWidth, gap, initialThumbColumns);
    let rowHeight = preferredThumbColumns > initialThumbColumns
      ? estimateThumbRowHeightForColumns(thumbsWidth, gap, preferredThumbColumns)
      : firstMetrics.rowHeight;
    let normalMaxRowsHeight = Math.round(rowHeight * BUILDER_CONFIG.system.desktopPicker.visibleRows + gap * 2);
    let minReadableRowsHeight = Math.round(rowHeight * BUILDER_CONFIG.system.desktopPicker.minReadableRows + gap * 0.5);

    let sheetLift = 0;
    let slotLift = 0;
    let thumbColumns = preferredThumbColumns > initialThumbColumns ? preferredThumbColumns : null;
    let isCompact = false;
    let thumbsHeight = normalMaxRowsHeight;

    if (state.sheetMode === 'expanded') {
      const preset = getLayoutPreset();
      const minSheetTopByPreviewShare = getLandscapeMultiRowMinSheetTop(lowerLimit);
      const measureSheetSpace = () => {
        const protectedBottom = getProtectedBottom();
        const buttonBottom = getPreviewControlsBottom();
        const slotTop = getTopmostSlotFrameTop();
        const maxSlotLift = Math.max(0, slotTop - buttonBottom - BUILDER_CONFIG.system.desktopPicker.slotSafeGapPx);
        const nextSlotLift = maxSlotLift;
        const naturalSafeTopForSheet = protectedBottom - nextSlotLift + BUILDER_CONFIG.system.desktopPicker.sheetTopOffsetPx;
        const previewShareBound = Number.isFinite(minSheetTopByPreviewShare)
          && minSheetTopByPreviewShare > naturalSafeTopForSheet + 0.5;
        const safeTopForSheet = Number.isFinite(minSheetTopByPreviewShare)
          ? Math.max(naturalSafeTopForSheet, minSheetTopByPreviewShare)
          : naturalSafeTopForSheet;
        const nextSheetLift = Math.max(0, controlsRect.top - safeTopForSheet);
        const topAfterLift = controlsRect.top - nextSheetLift;
        const nextAvailable = lowerLimit - topAfterLift - nonThumbsHeight - BUILDER_CONFIG.system.desktopPicker.sheetBottomReservePx;
        return {
          slotLift: nextSlotLift,
          sheetLift: nextSheetLift,
          available: nextAvailable,
          previewShareBound
        };
      };

      setSlotScale(1);
      let placement = measureSheetSpace();

      if (preset.rows > 1 && placement.available < minReadableRowsHeight && !placement.previewShareBound) {
        const minScale = BUILDER_CONFIG.system.desktopPicker.minSlotScale;
        const step = BUILDER_CONFIG.system.desktopPicker.scaleStep;
        for (let scale = 1 - step; scale >= minScale - 0.001; scale -= step) {
          setSlotScale(scale);
          placement = measureSheetSpace();
          // Once the 50/50-style preview floor becomes the limiting boundary,
          // further slot shrinking cannot create more catalog space and would only
          // make the artwork unnecessarily small. Let thumbnail compaction handle it.
          if (placement.available >= minReadableRowsHeight || placement.previewShareBound) break;
        }
      }

      slotLift = placement.slotLift;
      sheetLift = placement.sheetLift;
      let available = placement.available;

      if (available < minReadableRowsHeight) {
        const currentColumns = Math.max(thumbColumns || 0, getCurrentThumbColumnCount());
        const minCompactRowHeight = Math.max(48, Number(BUILDER_CONFIG.system.desktopPicker.compactRowHeightMinPx) || 54);
        const maxCompactColumns = Math.max(currentColumns, Number(BUILDER_CONFIG.system.desktopPicker.maxCompactColumns) || 8);
        const targetRowHeight = Math.max(minCompactRowHeight, Math.floor((available - gap * 0.5) / BUILDER_CONFIG.system.desktopPicker.minReadableRows));
        const targetThumbWidth = Math.max(38, targetRowHeight / 1.4);
        const estimatedColumns = thumbsWidth > 0 ? Math.ceil((thumbsWidth + gap) / (targetThumbWidth + gap)) : currentColumns;
        const nextThumbColumns = Math.min(maxCompactColumns, Math.max(currentColumns, estimatedColumns));

        if (thumbsWidth > 0 && nextThumbColumns > currentColumns) {
          thumbColumns = nextThumbColumns;
          isCompact = true;
          rowHeight = estimateThumbRowHeightForColumns(thumbsWidth, gap, thumbColumns);
          normalMaxRowsHeight = Math.round(rowHeight * BUILDER_CONFIG.system.desktopPicker.visibleRows + gap * 2);
          minReadableRowsHeight = Math.round(rowHeight * BUILDER_CONFIG.system.desktopPicker.minReadableRows + gap * 0.5);
        }
      }

      if (available < minReadableRowsHeight) {
        thumbsHeight = Math.max(rowHeight * 1.05, available);
      } else {
        const viewportHeight = getVisualViewportRect().height;
        const hasMeaningfulSpareHeight = viewportHeight >= 900 && available > normalMaxRowsHeight + 72;
        const tallViewportHardMax = Math.max(840, Math.floor(viewportHeight * 0.78));

        if (hasMeaningfulSpareHeight) {
          root.style.setProperty('--psb-desktop-thumbs-hard-max', `${tallViewportHardMax}px`);
        } else {
          root.style.removeProperty('--psb-desktop-thumbs-hard-max');
        }

        const preferredMaxHeight = hasMeaningfulSpareHeight
          ? Math.min(available, tallViewportHardMax)
          : normalMaxRowsHeight;

        // On genuinely tall desktop viewports, use the otherwise empty browser area for
        // more results instead of enlarging/changing the stage. Normal-height screens keep
        // the proven 4.5-row behavior unchanged.
        thumbsHeight = Math.max(minReadableRowsHeight, Math.min(preferredMaxHeight, available));
      }
    } else {
      setSlotScale(1);
    }

    const metrics = {
      sheetLift,
      slotLift,
      thumbColumns,
      thumbsHeight,
      isCompact,
      controlsMaxHeight: thumbsHeight + nonThumbsHeight + BUILDER_CONFIG.system.desktopPicker.controlsExtraPx
    };

    applySheetLayoutMetrics(metrics);

    if (isOpenSheet) {
      state.sheetLayoutLocked = true;
      state.sheetLayoutMetrics = metrics;
    }
  }

  let desktopLayoutRaf = null;
  function requestDesktopLayoutUpdate(force = false) {
    if (force) {
      state.sheetLayoutLocked = false;
      state.sheetLayoutMetrics = null;
    }
    window.cancelAnimationFrame(desktopLayoutRaf);
    desktopLayoutRaf = window.requestAnimationFrame(updateDesktopLayout);
  }

  function clearEmptySearchBeforeReopen() {
    if (state.sheetMode === 'expanded') return false;
    const pendingQuery = String(state.emptySearchResetQuery || '');
    if (!pendingQuery || pendingQuery !== String(state.searchQuery || '')) return false;

    state.shouldRestoreGridScroll = false;
    clearSearchQuery({ render: true, focus: false });
    return true;
  }

  function expandPrintSheet(shouldScroll = false) {
    const shouldAlignStage = Boolean(shouldScroll && !isMobileLayout());
    clearEmptySearchBeforeReopen();

    setSheetMode('expanded');
    refreshStageMetricsAfterLayoutChange();

    if (shouldAlignStage) {
      scrollBuilderForPrintChoice({ behavior: 'auto' });
      updateStageMetrics();
    }

    requestDesktopLayoutUpdate(true);
    updateMobilePopupMetrics();
  }

  function collapsePrintSheet() {
    rememberGridPosition();
    if (!state.catalogPurchaseCueArmed) state.catalogEngagementPending = false;
    setSheetMode('closed');
    requestDesktopLayoutUpdate(true);
  }

  function scrollBuilderForPrintChoice(options = {}) {
    if (!els.stage) return false;
    const rect = els.stage.getBoundingClientRect();
    const headerOffset = 0;
    if (Math.abs(rect.top - headerOffset) <= 2) return false;

    const top = Math.max(0, Math.round(window.pageYOffset + rect.top - headerOffset));
    window.scrollTo({ top, behavior: options.behavior || 'smooth' });
    return true;
  }

  let nativeVariantScrollTimer = null;
  function scrollStageForNativeMobileVariantChange() {
    if (!isMobileLayout() || state.sheetMode === 'expanded' || !els.stage) return;

    window.clearTimeout(nativeVariantScrollTimer);
    nativeVariantScrollTimer = window.setTimeout(() => {
      if (!isMobileLayout() || state.sheetMode === 'expanded' || !els.stage) return;

      const rect = els.stage.getBoundingClientRect();
      const viewportHeight = window.visualViewport && window.visualViewport.height ? window.visualViewport.height : window.innerHeight;
      const isAlreadyComfortablyVisible = rect.top >= 4 && rect.bottom <= viewportHeight - 12;
      if (isAlreadyComfortablyVisible) return;

      const top = Math.max(0, Math.round(window.pageYOffset + rect.top - 8));
      window.scrollTo({ top, behavior: 'smooth' });
    }, 90);
  }

  function isNativeVariantControlTarget(target) {
    if (!target || target.nodeType !== 1 || root.contains(target)) return false;

    const form = getProductForm();
    const scope = getProductScope(form);
    if (!scope || !scope.contains(target)) return false;

    const name = target.getAttribute ? String(target.getAttribute('name') || '') : '';
    const tagName = target.tagName ? target.tagName.toLowerCase() : '';
    const type = target.getAttribute ? String(target.getAttribute('type') || '').toLowerCase() : '';
    const isSelect = tagName === 'select';
    const isOptionInput = tagName === 'input' && (type === 'radio' || type === 'checkbox' || type === 'hidden');
    const optionNameMatch = /^options\[.+\]$/.test(name);
    const variantIdMatch = name === 'id';

    if ((optionNameMatch || variantIdMatch) && (isSelect || isOptionInput)) return true;

    const variantWrapper = target.closest && target.closest('variant-selects, variant-radios, product-variants, .product-form__input, .product__variant-picker, .product-form__variants');
    if (!variantWrapper || root.contains(variantWrapper)) return false;

    return isSelect || isOptionInput;
  }

  function updateStageZoomAvailability() {
    if (!els.stageZoom) return;
    const hasArtwork = state.selectedPrints.some(print => Boolean(print && print.src));
    els.stageZoom.disabled = !hasArtwork;
    els.stageZoom.setAttribute('aria-disabled', hasArtwork ? 'false' : 'true');
  }

  function getOriginalImageSrc(src) {
    const raw = String(src || '').trim();
    if (!raw) return '';
    try {
      const url = new URL(raw, window.location.href);
      const host = String(url.hostname || '');
      if (/cdn\.shopify\.com$|shopifycdn\.net$/i.test(host)) {
        ['width', 'height', 'crop'].forEach(name => url.searchParams.delete(name));
      }
      return url.toString();
    } catch (error) {
      return raw;
    }
  }

  function ensureZoomLightboxTopLayer() {
    if (!els.zoomLightbox || els.zoomLightbox.parentElement === document.body) return;
    document.body.appendChild(els.zoomLightbox);
  }

  function lockZoomLightboxPageScroll() {
    if (!document.body || state.zoomBodyLockStyles) return;
    state.zoomScrollLockY = window.scrollY || window.pageYOffset || 0;
    state.zoomBodyLockStyles = {
      position: document.body.style.position || '',
      top: document.body.style.top || '',
      left: document.body.style.left || '',
      right: document.body.style.right || '',
      width: document.body.style.width || '',
      overflow: document.body.style.overflow || ''
    };
    document.body.style.position = 'fixed';
    document.body.style.top = `-${state.zoomScrollLockY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  }

  function unlockZoomLightboxPageScroll() {
    if (!document.body || !state.zoomBodyLockStyles) return;
    const previous = state.zoomBodyLockStyles;
    document.body.style.position = previous.position;
    document.body.style.top = previous.top;
    document.body.style.left = previous.left;
    document.body.style.right = previous.right;
    document.body.style.width = previous.width;
    document.body.style.overflow = previous.overflow;
    const y = state.zoomScrollLockY || 0;
    state.zoomBodyLockStyles = null;
    state.zoomScrollLockY = 0;
    window.scrollTo(0, y);
  }

  const ZOOM_SLOT_STYLE_PROPS = [
    'background', 'backgroundColor', 'backgroundImage', 'backgroundPosition', 'backgroundSize',
    'backgroundRepeat', 'borderRadius', 'boxShadow', 'transform', 'transformOrigin', 'opacity'
  ];
  const ZOOM_LAYER_STYLE_PROPS = [
    'position', 'inset', 'top', 'right', 'bottom', 'left', 'width', 'height', 'zIndex',
    'border', 'borderTop', 'borderRight', 'borderBottom', 'borderLeft', 'borderRadius',
    'background', 'backgroundColor', 'backgroundImage', 'backgroundPosition', 'backgroundSize',
    'backgroundRepeat', 'boxShadow', 'opacity', 'transform', 'transformOrigin'
  ];
  const ZOOM_IMAGE_STYLE_PROPS = [
    'position', 'top', 'right', 'bottom', 'left', 'width', 'height', 'zIndex', 'objectFit',
    'objectPosition', 'borderRadius', 'background', 'backgroundColor', 'boxShadow',
    'transform', 'transformOrigin', 'opacity'
  ];

  function copyComputedStyleProps(computed, target, properties) {
    if (!computed || !target) return;
    properties.forEach(property => {
      const value = computed[property];
      if (value !== undefined && value !== null && value !== '') target.style[property] = value;
    });
  }

  function captureCleanSlotStyles(slot, image) {
    const transientClasses = ['is-active', 'is-next', 'is-drop-target', 'is-drag-source', 'is-surprise-spinning'];
    const restoreClasses = transientClasses.filter(className => slot.classList.contains(className));
    restoreClasses.forEach(className => slot.classList.remove(className));

    const captured = {
      slot: window.getComputedStyle(slot),
      before: window.getComputedStyle(slot, '::before'),
      after: window.getComputedStyle(slot, '::after'),
      image: image ? window.getComputedStyle(image) : null,
      slotValues: {},
      beforeValues: {},
      afterValues: {},
      imageValues: {}
    };

    ZOOM_SLOT_STYLE_PROPS.forEach(property => { captured.slotValues[property] = captured.slot[property]; });
    ZOOM_LAYER_STYLE_PROPS.forEach(property => {
      captured.beforeValues[property] = captured.before[property];
      captured.afterValues[property] = captured.after[property];
    });
    if (captured.image) ZOOM_IMAGE_STYLE_PROPS.forEach(property => { captured.imageValues[property] = captured.image[property]; });
    captured.beforeContent = captured.before.content;
    captured.afterContent = captured.after.content;

    restoreClasses.forEach(className => slot.classList.add(className));
    return captured;
  }

  function applyCapturedValues(values, target) {
    if (!values || !target) return;
    Object.entries(values).forEach(([property, value]) => {
      if (value !== undefined && value !== null && value !== '') target.style[property] = value;
    });
  }

  function createZoomPseudoLayer(values, content, className) {
    if (!content || content === 'none' || content === 'normal') return null;
    const layer = document.createElement('span');
    layer.className = `psb-zoom-slot__layer ${className}`;
    applyCapturedValues(values, layer);
    return layer;
  }

  function getZoomViewportSize() {
    const viewport = els.zoomViewport;
    return {
      width: viewport ? (viewport.clientWidth || window.innerWidth) : window.innerWidth,
      height: viewport ? (viewport.clientHeight || window.innerHeight) : window.innerHeight
    };
  }

  function clampZoomTranslation(x, y, scale = state.zoomScale) {
    const viewport = getZoomViewportSize();
    const scaledWidth = Math.max(1, state.zoomSceneWidth * scale);
    const scaledHeight = Math.max(1, state.zoomSceneHeight * scale);
    const edge = 34;
    let nextX = x;
    let nextY = y;

    if (scaledWidth <= viewport.width) nextX = (viewport.width - scaledWidth) / 2;
    else nextX = clampNumber(nextX, viewport.width - scaledWidth - edge, edge);

    if (scaledHeight <= viewport.height) nextY = (viewport.height - scaledHeight) / 2;
    else nextY = clampNumber(nextY, viewport.height - scaledHeight - edge, edge);

    return { x: nextX, y: nextY };
  }

  function renderZoomTransform() {
    if (!els.zoomScene) return;
    const clamped = clampZoomTranslation(state.zoomTranslateX, state.zoomTranslateY, state.zoomScale);
    state.zoomTranslateX = clamped.x;
    state.zoomTranslateY = clamped.y;
    els.zoomScene.style.transform = `translate3d(${state.zoomTranslateX}px, ${state.zoomTranslateY}px, 0) scale(${state.zoomScale})`;
    if (els.zoomLightbox) els.zoomLightbox.classList.toggle('is-zoomed', state.zoomScale > state.zoomMinScale * 1.015);
  }

  function setZoomScaleAroundPoint(nextScale, clientX, clientY) {
    const safeScale = clampNumber(nextScale, state.zoomMinScale, state.zoomMaxScale);
    const oldScale = state.zoomScale || state.zoomMinScale || 1;
    if (Math.abs(safeScale - oldScale) < 0.0001) return;
    const ratio = safeScale / oldScale;
    state.zoomTranslateX = clientX - (clientX - state.zoomTranslateX) * ratio;
    state.zoomTranslateY = clientY - (clientY - state.zoomTranslateY) * ratio;
    state.zoomScale = safeScale;
    renderZoomTransform();
  }

  function recalculateZoomMaxScale() {
    if (!els.zoomScene) return;
    let nativeScale = state.zoomMinScale;
    els.zoomScene.querySelectorAll('.psb-zoom-slot__image').forEach(image => {
      if (!image.naturalWidth || !image.naturalHeight) return;
      const baseWidth = Number(image.dataset.psbBaseWidth) || 0;
      const baseHeight = Number(image.dataset.psbBaseHeight) || 0;
      if (baseWidth > 0) nativeScale = Math.max(nativeScale, image.naturalWidth / baseWidth);
      if (baseHeight > 0) nativeScale = Math.max(nativeScale, image.naturalHeight / baseHeight);
    });
    state.zoomMaxScale = Math.max(state.zoomMinScale * 4, nativeScale, state.zoomMinScale);
    state.zoomMaxScale = Math.min(32, state.zoomMaxScale);
  }

  function buildZoomComposition() {
    if (!els.stage || !els.zoomScene) return false;
    const stageRect = els.stage.getBoundingClientRect();
    const entries = [];

    state.selectedPrints.forEach((print, index) => {
      if (!print || !print.src) return;
      const slot = els.slotButtons[index];
      const image = slot ? slot.querySelector('[data-psb-slot-img]') : null;
      if (!slot || slot.hidden || !image) return;
      const slotRect = slot.getBoundingClientRect();
      if (!slotRect.width || !slotRect.height) return;
      entries.push({ print, slot, image, slotRect, imageRect: image.getBoundingClientRect() });
    });

    if (!entries.length) return false;

    const outerPad = 24;
    const minLeft = Math.min(...entries.map(entry => entry.slotRect.left - stageRect.left)) - outerPad;
    const minTop = Math.min(...entries.map(entry => entry.slotRect.top - stageRect.top)) - outerPad;
    const maxRight = Math.max(...entries.map(entry => entry.slotRect.right - stageRect.left)) + outerPad;
    const maxBottom = Math.max(...entries.map(entry => entry.slotRect.bottom - stageRect.top)) + outerPad;
    const boundsLeft = Math.max(-outerPad, minLeft);
    const boundsTop = Math.max(-outerPad, minTop);
    const sceneWidth = Math.max(1, maxRight - boundsLeft);
    const sceneHeight = Math.max(1, maxBottom - boundsTop);

    els.zoomScene.innerHTML = '';
    els.zoomScene.style.width = `${sceneWidth}px`;
    els.zoomScene.style.height = `${sceneHeight}px`;
    state.zoomSceneWidth = sceneWidth;
    state.zoomSceneHeight = sceneHeight;

    entries.forEach(entry => {
      const captured = captureCleanSlotStyles(entry.slot, entry.image);
      const zoomSlot = document.createElement('div');
      zoomSlot.className = 'psb-zoom-slot';
      zoomSlot.style.left = `${entry.slotRect.left - stageRect.left - boundsLeft}px`;
      zoomSlot.style.top = `${entry.slotRect.top - stageRect.top - boundsTop}px`;
      zoomSlot.style.width = `${entry.slotRect.width}px`;
      zoomSlot.style.height = `${entry.slotRect.height}px`;
      applyCapturedValues(captured.slotValues, zoomSlot);

      const beforeLayer = createZoomPseudoLayer(captured.beforeValues, captured.beforeContent, 'psb-zoom-slot__layer--before');
      if (beforeLayer) zoomSlot.appendChild(beforeLayer);

      const zoomImage = document.createElement('img');
      zoomImage.className = 'psb-zoom-slot__image';
      zoomImage.alt = '';
      zoomImage.draggable = false;
      zoomImage.setAttribute('draggable', 'false');
      applyCapturedValues(captured.imageValues, zoomImage);
      zoomImage.style.display = 'block';
      zoomImage.style.outline = 'none';
      zoomImage.dataset.psbBaseWidth = String(Math.max(1, entry.imageRect.width));
      zoomImage.dataset.psbBaseHeight = String(Math.max(1, entry.imageRect.height));
      zoomImage.addEventListener('load', recalculateZoomMaxScale, { once: true });
      zoomImage.src = getOriginalImageSrc(entry.print.src);
      zoomSlot.appendChild(zoomImage);

      const afterLayer = createZoomPseudoLayer(captured.afterValues, captured.afterContent, 'psb-zoom-slot__layer--after');
      if (afterLayer) zoomSlot.appendChild(afterLayer);
      els.zoomScene.appendChild(zoomSlot);
    });

    const viewport = getZoomViewportSize();
    const margin = viewport.width <= 749 ? 34 : 72;
    const fitScale = Math.min(
      Math.max(0.05, (viewport.width - margin) / sceneWidth),
      Math.max(0.05, (viewport.height - margin) / sceneHeight)
    );
    state.zoomMinScale = fitScale;
    state.zoomScale = fitScale;
    state.zoomMaxScale = Math.max(fitScale * 4, fitScale);
    state.zoomTranslateX = (viewport.width - sceneWidth * fitScale) / 2;
    state.zoomTranslateY = (viewport.height - sceneHeight * fitScale) / 2;
    recalculateZoomMaxScale();
    renderZoomTransform();
    return true;
  }

  function openZoomLightbox() {
    if (!els.zoomLightbox || !els.zoomViewport || !els.zoomScene || !state.selectedPrints.some(print => print && print.src)) return;
    ensureZoomLightboxTopLayer();
    els.zoomLightbox.hidden = false;
    els.zoomLightbox.setAttribute('aria-hidden', 'false');
    lockZoomLightboxPageScroll();
    document.documentElement.classList.add('psb-zoom-lightbox-open');
    if (document.body) document.body.classList.add('psb-zoom-lightbox-open');

    window.requestAnimationFrame(() => {
      if (!buildZoomComposition()) {
        closeZoomLightbox();
        return;
      }
      if (els.zoomClose) els.zoomClose.focus({ preventScroll: true });
    });
  }

  function closeZoomLightbox() {
    if (!els.zoomLightbox) return;
    els.zoomLightbox.hidden = true;
    els.zoomLightbox.setAttribute('aria-hidden', 'true');
    els.zoomLightbox.classList.remove('is-zoomed', 'is-panning');
    if (els.zoomScene) {
      els.zoomScene.innerHTML = '';
      els.zoomScene.style.transform = '';
    }
    document.documentElement.classList.remove('psb-zoom-lightbox-open');
    if (document.body) document.body.classList.remove('psb-zoom-lightbox-open');
    unlockZoomLightboxPageScroll();
    if (els.stageZoom && typeof els.stageZoom.focus === 'function') {
      window.setTimeout(() => els.stageZoom.focus({ preventScroll: true }), 0);
    }
  }

  const zoomPointers = new Map();
  let zoomGesture = null;

  function getZoomPointerCenter(points) {
    const values = Array.from(points.values());
    if (!values.length) return { x: 0, y: 0 };
    return {
      x: values.reduce((sum, point) => sum + point.x, 0) / values.length,
      y: values.reduce((sum, point) => sum + point.y, 0) / values.length
    };
  }

  function getZoomPointerDistance(points) {
    const values = Array.from(points.values());
    if (values.length < 2) return 0;
    return Math.hypot(values[1].x - values[0].x, values[1].y - values[0].y);
  }

  function resetZoomGesture() {
    if (!zoomPointers.size) {
      zoomGesture = null;
      if (els.zoomLightbox) els.zoomLightbox.classList.remove('is-panning');
      return;
    }
    const center = getZoomPointerCenter(zoomPointers);
    zoomGesture = {
      startScale: state.zoomScale,
      startX: state.zoomTranslateX,
      startY: state.zoomTranslateY,
      startCenter: center,
      startDistance: getZoomPointerDistance(zoomPointers)
    };
  }

  function handleZoomPointerDown(event) {
    if (!els.zoomLightbox || els.zoomLightbox.hidden || event.target.closest('[data-psb-zoom-close]')) return;
    event.preventDefault();
    zoomPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (els.zoomViewport && typeof els.zoomViewport.setPointerCapture === 'function') {
      try { els.zoomViewport.setPointerCapture(event.pointerId); } catch (error) {}
    }
    resetZoomGesture();
    if (state.zoomScale > state.zoomMinScale * 1.015 && els.zoomLightbox) els.zoomLightbox.classList.add('is-panning');
  }

  function handleZoomPointerMove(event) {
    if (!zoomPointers.has(event.pointerId) || !zoomGesture) return;
    event.preventDefault();
    zoomPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const center = getZoomPointerCenter(zoomPointers);

    if (zoomPointers.size >= 2) {
      const distance = Math.max(1, getZoomPointerDistance(zoomPointers));
      const startDistance = Math.max(1, zoomGesture.startDistance || distance);
      const nextScale = clampNumber(zoomGesture.startScale * (distance / startDistance), state.zoomMinScale, state.zoomMaxScale);
      const ratio = nextScale / zoomGesture.startScale;
      state.zoomScale = nextScale;
      state.zoomTranslateX = center.x - (zoomGesture.startCenter.x - zoomGesture.startX) * ratio;
      state.zoomTranslateY = center.y - (zoomGesture.startCenter.y - zoomGesture.startY) * ratio;
      renderZoomTransform();
      return;
    }

    if (state.zoomScale <= state.zoomMinScale * 1.015) return;
    state.zoomTranslateX = zoomGesture.startX + (center.x - zoomGesture.startCenter.x);
    state.zoomTranslateY = zoomGesture.startY + (center.y - zoomGesture.startCenter.y);
    renderZoomTransform();
  }

  function handleZoomPointerEnd(event) {
    if (!zoomPointers.has(event.pointerId)) return;
    zoomPointers.delete(event.pointerId);
    resetZoomGesture();
  }

  function handleZoomWheel(event) {
    if (!els.zoomLightbox || els.zoomLightbox.hidden) return;
    event.preventDefault();
    const factor = Math.exp(-event.deltaY * 0.0015);
    setZoomScaleAroundPoint(state.zoomScale * factor, event.clientX, event.clientY);
  }

  function handleZoomDoubleClick(event) {
    if (!els.zoomLightbox || els.zoomLightbox.hidden || event.target.closest('[data-psb-zoom-close]')) return;
    event.preventDefault();
    const zoomed = state.zoomScale > state.zoomMinScale * 1.08;
    const nextScale = zoomed
      ? state.zoomMinScale
      : Math.min(state.zoomMaxScale, state.zoomMinScale * 2.5);
    setZoomScaleAroundPoint(nextScale, event.clientX, event.clientY);
  }

  function refreshOpenZoomOnResize() {
    if (!els.zoomLightbox || els.zoomLightbox.hidden) return;
    buildZoomComposition();
  }

  function ensureSpecLightboxTopLayer() {
    if (!els.specsLightbox || els.specsLightbox.parentElement === document.body) return;
    document.body.appendChild(els.specsLightbox);
  }

  function lockSpecLightboxPageScroll() {
    if (!document.body || state.specBodyLockStyles) return;
    state.specScrollLockY = window.scrollY || window.pageYOffset || 0;
    state.specBodyLockStyles = {
      position: document.body.style.position || '',
      top: document.body.style.top || '',
      left: document.body.style.left || '',
      right: document.body.style.right || '',
      width: document.body.style.width || '',
      overflow: document.body.style.overflow || ''
    };
    document.body.style.position = 'fixed';
    document.body.style.top = `-${state.specScrollLockY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  }

  function unlockSpecLightboxPageScroll() {
    if (!document.body || !state.specBodyLockStyles) return;
    const previous = state.specBodyLockStyles;
    document.body.style.position = previous.position;
    document.body.style.top = previous.top;
    document.body.style.left = previous.left;
    document.body.style.right = previous.right;
    document.body.style.width = previous.width;
    document.body.style.overflow = previous.overflow;
    const y = state.specScrollLockY || 0;
    state.specBodyLockStyles = null;
    state.specScrollLockY = 0;
    window.scrollTo(0, y);
  }

  function updateSpecLightbox() {
    if (!els.specsLightbox || !els.specsSlides.length) return;
    const count = els.specsSlides.length;
    state.specSlideIndex = ((state.specSlideIndex % count) + count) % count;
    els.specsSlides.forEach((slide, index) => slide.classList.toggle('is-active', index === state.specSlideIndex));
    if (els.specsCounter) els.specsCounter.textContent = `${state.specSlideIndex + 1} / ${count}`;
    const hasMultiple = count > 1;
    if (els.specsPrev) els.specsPrev.hidden = !hasMultiple;
    if (els.specsNext) els.specsNext.hidden = !hasMultiple;
  }

  function openSpecLightbox(index = 0) {
    if (!els.specsLightbox || !els.specsSlides.length) return;
    ensureSpecLightboxTopLayer();
    state.specSlideIndex = index;
    updateSpecLightbox();
    els.specsLightbox.hidden = false;
    els.specsLightbox.setAttribute('aria-hidden', 'false');
    lockSpecLightboxPageScroll();
    document.documentElement.classList.add('psb-spec-lightbox-open');
    if (document.body) document.body.classList.add('psb-spec-lightbox-open');
    window.setTimeout(() => {
      const closeButton = els.specsCloseButtons && els.specsCloseButtons[0];
      if (closeButton) closeButton.focus({ preventScroll: true });
    }, 0);
  }

  function closeSpecLightbox() {
    if (!els.specsLightbox) return;
    els.specsLightbox.hidden = true;
    els.specsLightbox.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('psb-spec-lightbox-open');
    if (document.body) document.body.classList.remove('psb-spec-lightbox-open');
    unlockSpecLightboxPageScroll();
  }

  function showSpecSlide(delta) {
    if (!els.specsSlides.length) return;
    state.specSlideIndex += delta;
    updateSpecLightbox();
  }

  function openCatalogFromFooter() {
    ensureActiveSlot(Number.isFinite(state.activeSlot) ? state.activeSlot : 0);
    openPickerAtCurrentPosition(true);
  }

  function requestVariantSync(delay = 0) {
    const run = () => {
      state.variantSyncRaf = null;
      const currentId = getVariantIdFromForm();

      if (state.transferredVariantId && Date.now() <= state.transferredVariantLockUntil) {
        if (String(currentId || '') !== String(state.transferredVariantId)) {
          enforceTransferredVariant();
          return;
        }
      } else if (state.transferredVariantId) {
        state.transferredVariantId = '';
        state.transferredVariantLockUntil = 0;
      }

      if (currentId && currentId !== state.selectedVariantId) updatePreviewFromVariant();
    };

    if (delay > 0) {
      window.setTimeout(() => requestVariantSync(0), delay);
      return;
    }

    if (state.variantSyncRaf) return;
    state.variantSyncRaf = window.requestAnimationFrame(run);
  }

  function bindVariantIdObserver() {
    const form = getProductForm();
    const input = form ? form.querySelector('[name="id"]') : null;
    if (!input) return;

    input.addEventListener('input', () => requestVariantSync());
    input.addEventListener('change', () => requestVariantSync());

    if (window.MutationObserver && !state.variantIdObserver) {
      state.variantIdObserver = new MutationObserver(() => requestVariantSync());
      state.variantIdObserver.observe(input, { attributes: true, attributeFilter: ['value'] });
    }

    ['variant:change', 'variant:changed'].forEach(eventName => {
      document.addEventListener(eventName, () => requestVariantSync());
    });
  }

  function handleProductVariantChange(event) {
    const target = event && event.target ? event.target : null;
    const form = getProductForm();
    const isFormChange = Boolean(form && target && form.contains(target));
    const isNativeVariantChange = isNativeVariantControlTarget(target);

    if (!isFormChange && !isNativeVariantChange) return;

    requestVariantSync();
    requestVariantSync(150);

    if (isNativeVariantChange) scrollStageForNativeMobileVariantChange();
  }

  function getPrintImageRectForSlot(slotIndex) {
    const slotButton = root.querySelector(`[data-psb-slot="${slotIndex}"]`);
    const slotImg = slotButton ? slotButton.querySelector('[data-psb-slot-img]') : null;
    if (slotImg && slotImg.getAttribute('src')) {
      const imgRect = slotImg.getBoundingClientRect();
      if (imgRect.width > 4 && imgRect.height > 4) return imgRect;
    }
    return slotButton ? slotButton.getBoundingClientRect() : null;
  }

  function createDragGhost(print, sourceElement, sourceSlot = null) {
    const ghost = document.createElement('div');
    ghost.className = 'psb-drag-ghost';

    const img = document.createElement('img');
    img.src = getOptimizedImageSrc(print.src, DRAG_GHOST_IMAGE_WIDTH);
    img.alt = print.id || '';
    ghost.appendChild(img);

    let rect = null;
    if (sourceSlot !== null && sourceSlot !== undefined) rect = getPrintImageRectForSlot(sourceSlot);
    if (!rect && sourceElement) {
      const sourceImg = sourceElement.querySelector('img');
      rect = sourceImg ? sourceImg.getBoundingClientRect() : sourceElement.getBoundingClientRect();
    }

    const fallbackWidth = isTouchLikeInput() ? 78 : 88;
    const rawWidth = rect && rect.width ? rect.width : fallbackWidth;
    const rawHeight = rect && rect.height ? rect.height : fallbackWidth * 1.4;
    const maxWidth = isTouchLikeInput() ? 92 : 118;
    const maxHeight = isTouchLikeInput() ? 132 : 168;
    const scale = Math.min(1, maxWidth / Math.max(rawWidth, 1), maxHeight / Math.max(rawHeight, 1));
    const width = Math.max(48, Math.round(rawWidth * scale));
    const height = Math.max(66, Math.round(rawHeight * scale));
    ghost.style.width = `${width}px`;
    ghost.style.height = `${height}px`;
    document.body.appendChild(ghost);
    return ghost;
  }

  function positionDragGhost(event) {
    if (!state.drag || !state.drag.ghost) return;
    const width = state.drag.ghost.offsetWidth || 0;
    const height = state.drag.ghost.offsetHeight || 0;
    state.drag.ghost.style.transform = `translate3d(${event.clientX - width / 2}px, ${event.clientY - height / 2}px, 0)`;
  }

  function getSlotIndexAtPoint(clientX, clientY) {
    let bestIndex = null;
    let bestScore = 0;

    const targets = els.slotButtons;
    targets.forEach((slotButton) => {
      if (!slotButton) return;
      const style = window.getComputedStyle(slotButton);
      if (style.display === 'none' || style.visibility === 'hidden' || style.pointerEvents === 'none') return;
      const index = Number(slotButton.dataset.psbSlot);
      if (!Number.isFinite(index)) return;
      const rect = slotButton.getBoundingClientRect();
      const inside = clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
      if (!inside) return;

      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const distance = Math.hypot(clientX - centerX, clientY - centerY);
      const score = 1 / Math.max(distance, 1);
      if (bestIndex === null || score > bestScore) {
        bestIndex = index;
        bestScore = score;
      }
    });

    if (bestIndex === null && state.drag && isMobileLayout() && isTouchLikeInput()) {
      const tolerance = 34;
      targets.forEach((slotButton) => {
        if (!slotButton) return;
        const style = window.getComputedStyle(slotButton);
        if (style.display === 'none' || style.visibility === 'hidden' || style.pointerEvents === 'none') return;
        const index = Number(slotButton.dataset.psbSlot);
        if (!Number.isFinite(index)) return;
        const rect = slotButton.getBoundingClientRect();
        const insideSoft = clientX >= rect.left - tolerance && clientX <= rect.right + tolerance && clientY >= rect.top - tolerance && clientY <= rect.bottom + tolerance;
        if (!insideSoft) return;
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const distance = Math.hypot(clientX - centerX, clientY - centerY);
        const score = 1 / Math.max(distance, 1);
        if (bestIndex === null || score > bestScore) {
          bestIndex = index;
          bestScore = score;
        }
      });
    }

    return bestIndex;
  }

  function vibrateOnDragStart() {
    if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return;
    try { navigator.vibrate(16); } catch (error) {}
  }

  function freezeDragScrollerPosition() {
    if (!els.thumbs || state.dragLockedThumbScrollTop === null || state.dragLockedThumbScrollTop === undefined) return;
    if (els.thumbs.scrollTop !== state.dragLockedThumbScrollTop) {
      els.thumbs.scrollTop = state.dragLockedThumbScrollTop;
    }
  }

  function handleDragScrollGuard(event) {
    if (!state.dragScrollLocked) return;
    if (!isTouchLikeInput()) return;
    event.preventDefault();
    freezeDragScrollerPosition();
  }

  function lockScrollForDrag() {
    if (state.dragScrollLocked) return;
    state.dragScrollLocked = true;
    state.dragLockedThumbScrollTop = els.thumbs ? els.thumbs.scrollTop : null;
    root.classList.add('psb-drag-scroll-locked');
    document.addEventListener('touchmove', handleDragScrollGuard, { passive: false, capture: true });
  }

  function unlockScrollForDrag() {
    if (!state.dragScrollLocked) return;
    state.dragScrollLocked = false;
    state.dragLockedThumbScrollTop = null;
    root.classList.remove('psb-drag-scroll-locked');
    document.removeEventListener('touchmove', handleDragScrollGuard, true);
  }

  function clearDragVisuals() {
    root.querySelectorAll('.is-drag-source').forEach(element => element.classList.remove('is-drag-source'));
    root.querySelectorAll('.is-drop-target').forEach(element => element.classList.remove('is-drop-target'));
    root.classList.remove('psb-slot-drag-focus-active');
    document.body.classList.remove('psb-is-dragging');
    if (!root.classList.contains('psb-drag-hint-active')) {
      document.body.classList.remove('psb-drag-focus-active');
    }
    unlockScrollForDrag();
  }

  function setDropTarget(slotIndex) {
    if (!state.drag) return;
    if (state.drag.currentTargetSlot === slotIndex) return;

    root.querySelectorAll('.is-drop-target').forEach(element => element.classList.remove('is-drop-target'));
    state.drag.currentTargetSlot = slotIndex;

    if (slotIndex !== null && slotIndex !== undefined) {
      root.querySelectorAll(`[data-psb-slot="${slotIndex}"]`).forEach(target => target.classList.add('is-drop-target'));
    }
  }

  function prepareDrag(event, options) {
    if (!event || !options || !options.print || !options.print.src) return;
    if (event.button !== undefined && event.button !== 0) return;
    if (state.drag && state.drag.isActive) return;

    const deferPreventDefault = Boolean(options.deferPreventDefault);
    if (!deferPreventDefault) {
      event.preventDefault();
      event.stopPropagation();
    }

    state.drag = {
      isActive: true,
      isDragging: false,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      sourceType: options.sourceType,
      sourceSlot: options.sourceSlot,
      sourceElement: options.sourceElement,
      print: options.print,
      ghost: null,
      currentTargetSlot: null,
      movedEnough: false,
      deferPreventDefault
    };

    if (options.sourceElement && options.sourceElement.setPointerCapture && event.pointerId !== undefined) {
      try { options.sourceElement.setPointerCapture(event.pointerId); } catch (error) {}
    }

    window.addEventListener('pointermove', handleDragMove, { passive: false });
    window.addEventListener('pointerup', handleDragEnd, { passive: false });
    window.addEventListener('pointercancel', cancelDrag, { passive: false });
  }

  function prepareDragFromSlot(event, slotIndex) {
    const print = state.selectedPrints[slotIndex];
    if (!print) return;
    const slotButton = root.querySelector(`[data-psb-slot="${slotIndex}"]`);
    prepareDrag(event, {
      sourceType: 'slot',
      sourceSlot: slotIndex,
      sourceElement: slotButton,
      print
    });
  }

  function isTouchLikeInput(event = null) {
    if (event && event.pointerType) return event.pointerType === 'touch';
    return isTouchInteractionDevice();
  }

  function cancelPendingTouchDrag() {
    const pending = state.pendingTouchDrag;
    if (!pending) return;
    window.clearTimeout(pending.timer);
    window.removeEventListener('pointermove', pending.handleMove);
    window.removeEventListener('pointerup', pending.handleEnd);
    window.removeEventListener('pointercancel', pending.handleEnd);
    state.pendingTouchDrag = null;
  }

  function prepareLongPressDragFromThumb(event, thumbButton, print) {
    if (!event || !thumbButton || !print) return;
    cancelPendingTouchDrag();

    const pointerId = event.pointerId;
    const startX = event.clientX;
    const startY = event.clientY;
    let lastX = startX;
    let lastY = startY;

    const handleMove = moveEvent => {
      if (pointerId !== undefined && moveEvent.pointerId !== pointerId) return;
      lastX = moveEvent.clientX;
      lastY = moveEvent.clientY;
      if (state.drag && state.drag.isDragging) return;
      const distance = Math.hypot(lastX - startX, lastY - startY);
      if (distance > 18) cancelPendingTouchDrag();
    };

    const handleEnd = endEvent => {
      if (pointerId !== undefined && endEvent.pointerId !== pointerId) return;
      cancelPendingTouchDrag();
    };

    const timer = window.setTimeout(() => {
      if (thumbButton.setPointerCapture && pointerId !== undefined) {
        try { thumbButton.setPointerCapture(pointerId); } catch (error) {}
      }
      const syntheticEvent = {
        button: 0,
        pointerId,
        clientX: lastX,
        clientY: lastY,
        preventDefault() {},
        stopPropagation() {}
      };
      cancelPendingTouchDrag();
      prepareDrag(syntheticEvent, {
        sourceType: 'thumb',
        sourceSlot: null,
        sourceElement: thumbButton,
        print,
        deferPreventDefault: false
      });
      activateDrag(syntheticEvent);
    }, 220);

    state.pendingTouchDrag = { timer, handleMove, handleEnd };
    window.addEventListener('pointermove', handleMove, { passive: true });
    window.addEventListener('pointerup', handleEnd, { passive: true });
    window.addEventListener('pointercancel', handleEnd, { passive: true });
  }

  function prepareDragFromThumb(event, thumbButton, print) {
    if (isTouchLikeInput(event)) {
      prepareLongPressDragFromThumb(event, thumbButton, print);
      return;
    }

    prepareDrag(event, {
      sourceType: 'thumb',
      sourceSlot: null,
      sourceElement: thumbButton,
      print,
      deferPreventDefault: false
    });
  }

  function activateDrag(event) {
    if (!state.drag || state.drag.isDragging) return;
    state.drag.isDragging = true;
    state.drag.ghost = createDragGhost(state.drag.print, state.drag.sourceElement, state.drag.sourceSlot);
    if (state.drag.sourceElement) state.drag.sourceElement.classList.add('is-drag-source');
    document.body.classList.add('psb-is-dragging');
    if (state.drag.sourceType === 'slot') {
      root.classList.add('psb-slot-drag-focus-active');
      document.body.classList.add('psb-drag-focus-active');
    }
    lockScrollForDrag();
    if (dragOverlay) {
      if (state.drag.sourceType === 'slot') dragOverlay.startTrash();
      else dragOverlay.hideHint(false);
    }
    vibrateOnDragStart();
    positionDragGhost(event);
  }

  function handleDragMove(event) {
    if (!state.drag || !state.drag.isActive) return;
    if (state.drag.pointerId !== undefined && event.pointerId !== state.drag.pointerId) return;

    const dx = event.clientX - state.drag.startX;
    const dy = event.clientY - state.drag.startY;
    const distance = Math.hypot(dx, dy);

    if (!state.drag.isDragging) {
      if (distance < 7) return;

      activateDrag(event);
    }

    event.preventDefault();
    event.stopPropagation();
    freezeDragScrollerPosition();
    state.drag.movedEnough = true;
    positionDragGhost(event);
    state.drag.overTrash = Boolean(state.drag.sourceType === 'slot' && dragOverlay && dragOverlay.updateTrash(event.clientX, event.clientY));
    setDropTarget(state.drag.overTrash ? null : getSlotIndexAtPoint(event.clientX, event.clientY));
  }

  function applyDragDrop() {
    if (!state.drag || !state.drag.isDragging) return false;

    if (state.drag.sourceType === 'slot' && state.drag.overTrash) {
      removePrintFromSlot(state.drag.sourceSlot);
      if (dragOverlay) dragOverlay.completeTrashDrop();
      return true;
    }

    const targetSlot = state.drag.currentTargetSlot;
    if (targetSlot === null || targetSlot === undefined) return false;

    if (state.drag.sourceType === 'slot') {
      const sourceSlot = state.drag.sourceSlot;
      if (sourceSlot === targetSlot) return false;

      pushHistory();
      const draggedPrint = state.selectedPrints[sourceSlot];
      const targetPrint = state.selectedPrints[targetSlot];
      state.selectedPrints[targetSlot] = draggedPrint;
      state.selectedPrints[sourceSlot] = targetPrint || null;
      state.activeSlot = targetSlot;
    } else if (state.drag.sourceType === 'thumb') {
      const currentPrint = state.selectedPrints[targetSlot];
      if (currentPrint && currentPrint.id === state.drag.print.id) return false;

      pushHistory();
      state.selectedPrints[targetSlot] = state.drag.print;
      state.activeSlot = targetSlot;
    }

    const dropCompletedSet = isSetComplete();
    if (dropCompletedSet) {
      state.hasCompletedFirstSet = true;
      state.activeSlot = targetSlot;
      hideToast();
    } else {
      state.hasCompletedFirstSet = false;
      const nextEmpty = getNextEmptySlotAfter(targetSlot);
      state.activeSlot = nextEmpty >= 0 ? nextEmpty : getFirstEmptySlotIndex();
    }
    renderSlots();
    updateRenderedThumbSelectionState();
    retryVisibleThumbImages(false);
    updateHiddenProperties();
    setStatus('');
    return true;
  }

  function finishDrag(event) {
    cancelPendingTouchDrag();
    if (!state.drag) return;
    if (event && state.drag.pointerId !== undefined && event.pointerId !== state.drag.pointerId) return;

    const wasDragging = Boolean(state.drag.isDragging);
    const droppedOnTrash = Boolean(wasDragging && state.drag.sourceType === 'slot' && state.drag.overTrash);
    if (wasDragging) {
      if (event) event.preventDefault();
      applyDragDrop();
      state.suppressNextClick = true;
      window.setTimeout(() => { state.suppressNextClick = false; }, 0);
    }

    if (state.drag.ghost) state.drag.ghost.remove();
    if (dragOverlay && !droppedOnTrash) dragOverlay.endTrash();
    clearDragVisuals();
    state.drag = null;
    window.removeEventListener('pointermove', handleDragMove);
    window.removeEventListener('pointerup', handleDragEnd);
    window.removeEventListener('pointercancel', cancelDrag);
  }

  function handleDragEnd(event) {
    finishDrag(event);
  }

  function cancelDrag(event) {
    cancelPendingTouchDrag();
    if (state.drag && state.drag.ghost) state.drag.ghost.remove();
    if (dragOverlay) dragOverlay.endTrash();
    clearDragVisuals();
    state.drag = null;
    window.removeEventListener('pointermove', handleDragMove);
    window.removeEventListener('pointerup', handleDragEnd);
    window.removeEventListener('pointercancel', cancelDrag);
  }

  

  function neutralizeStickyHeaderForBuilder() {
    document.documentElement.classList.add('psb-builder-page');
    if (document.body) document.body.classList.add('psb-builder-page');

  }

  let viewportChangeTimer = null;
  let stableLayoutRefreshTimer = null;

  function scheduleStableLayoutRefresh(delay = 0) {
    window.clearTimeout(stableLayoutRefreshTimer);
    stableLayoutRefreshTimer = window.setTimeout(() => {
      stableLayoutRefreshTimer = null;
      refreshStageMetricsAfterLayoutChange();
      requestDesktopLayoutUpdate(true);
      updateMobilePopupMetrics();
    }, delay);
  }

  function handleViewportChange() {
    neutralizeStickyHeaderForBuilder();
    syncInteractionModeClass();
    applyStageAspectForSetSize();
    refreshStageMetricsAfterLayoutChange();
    updateSheetHeaderButtons();
    syncMobilePickerMode();
    updateMobilePopupMetrics();
    updateTouchSearchPopupMetrics();
    window.clearTimeout(viewportChangeTimer);
    viewportChangeTimer = window.setTimeout(() => {
      if (state.sheetMode === 'expanded') {
        state.sheetLayoutLocked = false;
        state.sheetLayoutMetrics = null;
        updateMobilePopupMetrics();
        window.setTimeout(updateMobilePopupMetrics, 220);
      }
      requestDesktopLayoutUpdate(true);
    }, 80);
  }

  function stabilizeMobileSearchViewport(options = {}) {
    const shouldBlur = Boolean(options.blur);

    // Blur is intentionally viewport-agnostic. On iPad/Android landscape the
    // layout may be wider than 750px, but Search/Enter must still dismiss the
    // on-screen keyboard reliably.
    if (shouldBlur && document.activeElement === els.search) {
      els.search.blur();
      window.requestAnimationFrame(() => {
        if (document.activeElement === els.search) els.search.blur();
      });
    }

    if (state.sheetMode !== 'expanded' || !usesTouchSearchMechanics()) {
      setMobileSearchMode(false);
      return;
    }

    const restoreY = state.mobileViewportLocked
      ? (state.mobileScrollLockY || 0)
      : (window.pageYOffset || document.documentElement.scrollTop || 0);

    setMobileSearchMode(!shouldBlur && document.activeElement === els.search);

    const stabilize = () => {
      updateMobilePopupMetrics();
      updateTouchSearchPopupMetrics();
      if (isMobileLayout() && state.mobileViewportLocked) window.scrollTo(0, restoreY);
    };

    stabilize();
    window.requestAnimationFrame(stabilize);
    window.setTimeout(stabilize, 100);
    window.setTimeout(stabilize, 280);
  }

  function bindEvents() {
    syncSearchClearButton();
    updateStageZoomAvailability();
    ensureGuidanceTopLayer();
    ensureToastTopLayer();
    document.addEventListener('wheel', suppressBackgroundWheel, { passive: false, capture: true });
    document.addEventListener('touchstart', trackOverlayTouch, { passive: true, capture: true });
    document.addEventListener('touchmove', suppressBackgroundTouchMove, { passive: false, capture: true });
    document.addEventListener('touchend', trackOverlayTouch, { passive: true, capture: true });
    document.addEventListener('touchcancel', trackOverlayTouch, { passive: true, capture: true });
    document.addEventListener('keydown', suppressBackgroundScrollKey, true);
    root.addEventListener('dragstart', event => {
      event.preventDefault();
    });

    const blockProtectedImageMenu = event => {
      const protectedTarget = event.target.closest('.psb-thumb, .psb-slot, .psb-spec-slide img, .psb-zoom-slot__image, .psb-bg');
      if (!protectedTarget) return;
      event.preventDefault();
    };

    root.addEventListener('contextmenu', blockProtectedImageMenu);
    if (els.specsLightbox) els.specsLightbox.addEventListener('contextmenu', blockProtectedImageMenu);
    if (els.specsLightbox) els.specsLightbox.addEventListener('dragstart', event => event.preventDefault());
    if (els.zoomLightbox) els.zoomLightbox.addEventListener('contextmenu', blockProtectedImageMenu);
    if (els.zoomLightbox) els.zoomLightbox.addEventListener('dragstart', event => event.preventDefault());

    root.querySelectorAll('img').forEach(img => {
      img.draggable = false;
      img.setAttribute('draggable', 'false');
    });

    els.guidanceCloseButtons.forEach(button => {
      button.addEventListener('click', event => {
        event.preventDefault();
        hideGuidance();
      });
    });

    if (els.guidanceCollection) {
      els.guidanceCollection.addEventListener('change', event => {
        if (!els.collectionSelect) return;
        const nextHandle = event.currentTarget.value || ALL_HANDLE;
        els.collectionSelect.value = nextHandle;
        hideGuidance();
        els.collectionSelect.dispatchEvent(new Event('change', { bubbles: true }));
        expandPrintSheet(false);
      });
    }

    document.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      if (isLayoutPickerOpen()) {
        event.preventDefault();
        closeLayoutPicker({ restoreFocus: true });
        return;
      }
      if (!els.guidance || els.guidance.hidden) return;
      event.preventDefault();
      hideGuidance();
    });

    const stage = root.querySelector('.psb-stage');
    if (stage) {
      stage.addEventListener('click', event => {
        if (!isMobileLayout()) {
          scrollBuilderForPrintChoice({ behavior: state.sheetMode === 'expanded' ? 'smooth' : 'auto' });
          if (state.sheetMode === 'expanded') requestDesktopLayoutUpdate(true);
        }

        if (!event.target.closest('[data-psb-slot], .psb-preview-controls, .psb-stage-zoom-button, .psb-stage-footer-actions, .psb-controls, .psb-spec-lightbox, .psb-zoom-lightbox')) {
          state.activeSlot = null;
          renderSlots();
          updateRenderedThumbSelectionState();
        }
      });
    }

    els.slotButtons.forEach(button => {
      button.addEventListener('pointerdown', event => {
        const slotIndex = Number(button.dataset.psbSlot);
        if (Number.isFinite(slotIndex) && state.selectedPrints[slotIndex]) {
          prepareDragFromSlot(event, slotIndex);
        }
      });

      button.addEventListener('click', event => {
        event.stopPropagation();
        if (state.suppressNextClick) {
          event.preventDefault();
          state.suppressNextClick = false;
          return;
        }
        dismissInitialPrompt();
        markCatalogEngagementPending();
        setActiveSlot(Number(button.dataset.psbSlot));
      });
    });

    els.slotTabs.forEach(button => button.addEventListener('click', event => {
      event.stopPropagation();
      dismissInitialPrompt();
      markCatalogEngagementPending();
      setActiveSlot(Number(button.dataset.psbSlotTab));
    }));

    if (els.setSizeOptions) {
      const trigger = getLayoutPickerTrigger();
      const menu = getLayoutPickerMenu();

      if (trigger) {
        trigger.addEventListener('click', event => {
          event.preventDefault();
          event.stopPropagation();
          toggleLayoutPicker();
        });
      }

      if (menu) {
        menu.addEventListener('click', event => {
          const closeButton = event.target.closest('[data-psb-layout-close]');
          if (closeButton) {
            event.preventDefault();
            event.stopPropagation();
            closeLayoutPicker({ restoreFocus: true });
            return;
          }

          const button = event.target.closest('[data-psb-set-size]');
          if (!button || button.disabled) return;
          event.preventDefault();
          event.stopPropagation();
          closeLayoutPicker();
          dismissInitialPrompt();
          applySetSize(Number(button.dataset.psbSetSize));
        });
      }

      document.addEventListener('click', event => {
        if (!isLayoutPickerOpen()) return;
        const activeMenu = getLayoutPickerMenu();
        const activeTrigger = getLayoutPickerTrigger();
        if ((activeMenu && activeMenu.contains(event.target)) || (activeTrigger && activeTrigger.contains(event.target))) return;
        closeLayoutPicker();
      });

    }

    if (els.productLayoutSelect) {
      els.productLayoutSelect.addEventListener('change', event => {
        const nextCount = Number(event.target.value);
        if (!Number.isFinite(nextCount) || nextCount === getSetSize()) return;
        dismissInitialPrompt();
        const navigated = applySetSize(nextCount);
        if (!navigated) updateProductLayoutSelector();
      });
    }

    if (els.stageZoom) {
      els.stageZoom.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        openZoomLightbox();
      });
    }

    if (els.zoomClose) {
      els.zoomClose.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        closeZoomLightbox();
      });
    }

    if (els.zoomViewport) {
      els.zoomViewport.addEventListener('pointerdown', handleZoomPointerDown, { passive: false });
      els.zoomViewport.addEventListener('pointermove', handleZoomPointerMove, { passive: false });
      els.zoomViewport.addEventListener('pointerup', handleZoomPointerEnd, { passive: false });
      els.zoomViewport.addEventListener('pointercancel', handleZoomPointerEnd, { passive: false });
      els.zoomViewport.addEventListener('wheel', handleZoomWheel, { passive: false });
      els.zoomViewport.addEventListener('dblclick', handleZoomDoubleClick, { passive: false });
    }

    window.addEventListener('resize', refreshOpenZoomOnResize, { passive: true });
    if (window.visualViewport) window.visualViewport.addEventListener('resize', refreshOpenZoomOnResize, { passive: true });

    document.addEventListener('keydown', event => {
      if (!els.zoomLightbox || els.zoomLightbox.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeZoomLightbox();
      }
    });

    if (els.catalogOpen) {
      els.catalogOpen.addEventListener('click', event => {
        event.stopPropagation();
        dismissInitialPrompt();
        markCatalogEngagementPending();
        openCatalogFromFooter();
      });
    }

    if (els.specsOpen) {
      els.specsOpen.addEventListener('click', event => {
        event.stopPropagation();
        openSpecLightbox(0);
      });
    }

    if (els.share) {
      els.share.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        shareBuilderLink();
      });
    }

    els.specsCloseButtons.forEach(button => button.addEventListener('click', event => {
      event.preventDefault();
      closeSpecLightbox();
    }));

    if (els.specsPrev) {
      els.specsPrev.addEventListener('click', event => {
        event.preventDefault();
        showSpecSlide(-1);
      });
    }

    if (els.specsNext) {
      els.specsNext.addEventListener('click', event => {
        event.preventDefault();
        showSpecSlide(1);
      });
    }

    if (els.specsTrack) {
      let specTouchStartX = 0;
      let specTouchStartY = 0;
      let specTouchStarted = false;

      els.specsTrack.addEventListener('touchstart', event => {
        if (!event.touches || !event.touches.length) return;
        specTouchStarted = true;
        specTouchStartX = event.touches[0].clientX;
        specTouchStartY = event.touches[0].clientY;
      }, { passive: true });

      els.specsTrack.addEventListener('touchend', event => {
        if (!specTouchStarted || !event.changedTouches || !event.changedTouches.length) return;
        specTouchStarted = false;
        const dx = event.changedTouches[0].clientX - specTouchStartX;
        const dy = event.changedTouches[0].clientY - specTouchStartY;
        if (Math.abs(dx) < 46 || Math.abs(dx) < Math.abs(dy) * 1.25) return;
        showSpecSlide(dx < 0 ? 1 : -1);
      }, { passive: true });
    }

    if (els.specsLightbox) {
      els.specsLightbox.addEventListener('touchmove', event => {
        if (!els.specsLightbox || els.specsLightbox.hidden) return;
        event.preventDefault();
      }, { passive: false });
    }

    document.addEventListener('keydown', event => {
      if (!els.specsLightbox || els.specsLightbox.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeSpecLightbox();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showSpecSlide(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        showSpecSlide(1);
      }
    });

    if (els.shuffle) els.shuffle.addEventListener('click', event => {
      event.stopPropagation();
      dismissInitialPrompt();
      smartSurpriseMe();
    });

    if (els.clear) els.clear.addEventListener('click', event => {
      event.stopPropagation();
      clearSelection();
    });

    if (els.sheetAddCart) els.sheetAddCart.addEventListener('click', event => {
      submitBuilderSelectionToCart(event);
    });

    if (els.footerAddCart) els.footerAddCart.addEventListener('click', event => {
      submitBuilderSelectionToCart(event);
    });

    if (els.sheetCollapse) els.sheetCollapse.addEventListener('click', event => {
      event.stopPropagation();
      collapsePrintSheet();
    });

    if (els.mobileVariantBar) {
      els.mobileVariantBar.addEventListener('change', handleMobileVariantSelectorChange);
      els.mobileVariantBar.addEventListener('click', event => event.stopPropagation());
    }

    window.addEventListener('resize', handleViewportChange, { passive: true });
    window.addEventListener('orientationchange', handleViewportChange, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange, { passive: true });
      window.visualViewport.addEventListener('scroll', updateTouchSearchPopupMetrics, { passive: true });
    }

    if (els.undo) els.undo.addEventListener('click', event => {
      event.stopPropagation();
      undoHistory();
    });

    if (els.redo) els.redo.addEventListener('click', event => {
      event.stopPropagation();
      redoHistory();
    });

    els.collectionSelect.addEventListener('change', event => {
      pushHistory();
      state.selectedCollectionHandle = event.currentTarget.value;
      state.searchQuery = els.search ? String(els.search.value || '') : String(state.searchQuery || '');
      state.emptySearchResetQuery = '';
      syncSearchClearButton();
      resetResultsAndRender();
      updateHiddenProperties();
    });

    let searchTimer = null;
    const commitSearchInput = () => {
      window.clearTimeout(searchTimer);
      const nextQuery = els.search ? (els.search.value || '') : '';
      syncSearchClearButton();
      if (state.searchQuery === nextQuery) return;
      state.searchQuery = nextQuery;
      state.emptySearchResetQuery = '';
      resetResultsAndRender();
    };

    if (els.searchClear) {
      els.searchClear.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        window.clearTimeout(searchTimer);

        // Clearing is not an invitation to keep typing. Never refocus the field here:
        // on touch devices that would reopen the keyboard and could leave the mobile
        // picker in its keyboard-safe geometry even though the user just closed search.
        if (document.activeElement === els.search && typeof els.search.blur === 'function') {
          els.search.blur();
        }
        clearSearchQuery({ render: true, focus: false });
        setMobileSearchMode(false);

        const settleAfterClear = () => {
          if (document.activeElement === els.search && typeof els.search.blur === 'function') els.search.blur();
          setMobileSearchMode(false);
          updateMobilePopupMetrics();
          updateTouchSearchPopupMetrics();
        };
        window.requestAnimationFrame(settleAfterClear);
        window.setTimeout(settleAfterClear, 120);
        window.setTimeout(settleAfterClear, 300);
      });
    }

    els.search.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'touch' || isMobileLayout() || !isTouchInteractionDevice()) return;
      state.touchSearchScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      state.touchSearchScrollCaptured = true;
    }, { passive: true });

    els.search.addEventListener('input', () => {
      syncSearchClearButton();
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(() => {
        commitSearchInput();
      }, 180);
    });

    els.search.addEventListener('search', () => {
      commitSearchInput();
      stabilizeMobileSearchViewport({ blur: true });
    });

    els.search.addEventListener('keydown', event => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      commitSearchInput();
      stabilizeMobileSearchViewport({ blur: true });
    });

    els.search.addEventListener('focus', () => {
      setMobileSearchMode(true);
      stabilizeMobileSearchViewport();
      window.setTimeout(() => stabilizeMobileSearchViewport(), 140);
    });

    els.search.addEventListener('blur', () => {
      window.clearTimeout(state.mobileSearchRestoreTimer);
      state.mobileSearchRestoreTimer = window.setTimeout(() => {
        if (document.activeElement === els.search) return;
        setMobileSearchMode(false);
        updateMobilePopupMetrics();
        updateTouchSearchPopupMetrics();
        window.setTimeout(() => {
          updateMobilePopupMetrics();
          updateTouchSearchPopupMetrics();
        }, 180);
        window.setTimeout(() => {
          updateMobilePopupMetrics();
          updateTouchSearchPopupMetrics();
        }, 360);
      }, 120);
    });

    document.addEventListener('change', handleProductVariantChange);

    document.addEventListener('submit', event => {
      validateBeforeSubmit(event);
    }, true);

    ['pointerdown', 'mousedown', 'touchstart', 'click'].forEach(eventName => {
      document.addEventListener(eventName, event => {
        guardPurchaseEvent(event);
      }, eventName === 'touchstart' ? { capture: true, passive: false } : true);
    });

    document.addEventListener('keydown', event => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      guardPurchaseEvent(event);
    }, true);

    bindVariantIdObserver();
  }

  async function initCatalog() {
    try {
      state.catalogProvider = createCatalogProvider();
      const bootstrap = await state.catalogProvider.bootstrap();
      state.catalog = bootstrap.catalog || { collections: {}, sources: {}, prints: {} };
      state.collectionEntries = Array.isArray(bootstrap.collections)
        ? bootstrap.collections
        : getCollectionEntriesFromCatalog(state.catalog);
      buildIndexes();
      renderCollectionSelect();
      const restoredTransferredWindow = restoreTransferredApiWindow();
      if (restoredTransferredWindow) {
        renderResults({ reset: true, force: true });
      } else if (isApiCatalogMode() && bootstrap.initial && !state.hasExplicitCatalogState) {
        applyApiPage(bootstrap.initial, { append: false });
        renderResults({ reset: true });
      } else {
        // A transferred collection/search must run a fresh Worker query. Reusing
        // bootstrap.initial would leave the selector updated while the grid still
        // showed the default collection.
        await resetResultsAndRender({ preserveScroll: state.shouldRestoreGridScroll });
      }
      await applyDeeplinkSelection();
      if (state.shouldRestoreGridScroll) restoreGridPosition();
      state.urlSyncReady = true;
      renderSlots();
      updateRenderedThumbSelectionState();
      updateHiddenProperties();
      updateHistoryButtons();
      syncBuilderUrl();
      if (state.restoreSheetExpanded) {
        window.setTimeout(() => {
          openPickerAtCurrentPosition(isDesktopLayout());
          restoreGridPosition();
          scheduleStableLayoutRefresh(80);
        }, 0);
      }
      } catch (error) {
      const hint = getCatalogMode() === 'api'
        ? ' Check the Worker catalog API endpoint.'
        : ' Upload builder-prints.json to theme assets.';
      setStatus(`${error.message}${hint}`, true);
    }
  }

  function init() {
    neutralizeStickyHeaderForBuilder();
    syncInteractionModeClass();
    renderSetSizeSelector();
    renderProductLayoutSelector();
    syncMobileVariantPlacement();
    restoreInterfaceStateFromLocation();
    applySetSize(getInitialSetSizeFromLocation(), { recordHistory: false, syncVariant: false, render: false, allowProductNavigation: false });
    rememberCurrentUrlPrintValue(getSetSize());
    applyVariantOptionsFromLocation();
    renderSlots();
    bindEvents();
    syncShufflePlacement();
    updateSheetHeaderButtons();
    applyStageAspectForSetSize();
    observeStageMetrics();
    refreshStageMetricsAfterLayoutChange();
    requestDesktopLayoutUpdate();
    scheduleStableLayoutRefresh(80);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => scheduleStableLayoutRefresh(0)).catch(() => {});
    }
    window.addEventListener('load', () => scheduleStableLayoutRefresh(0), { once: true });
    updatePreviewFromVariant();
    updateHiddenProperties();
    updateHistoryButtons();
    initCatalog();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
    })();
  };

  if (window.PSBLayoutModule) startBuilder();
  else window.addEventListener('psb:layout-presets-ready', startBuilder, { once: true });
})();
